"use client";

/**
 * MuxVideoPlayerWithCTA - Estilo TikTok Shop (production-ready)
 *
 * Single, scalable component for multiple videos + CTA. The CTA (redirect URL,
 * label, icon) is per-page: each static page has its own setup via
 * staticPageVideoConfig + env keys (e.g. VITE_MEJORES_AMIGOS_*, VITE_TELEGRAM_GROUPS_*).
 * Funnel “one asset first”: `VITE_MUX_CTA_UNIVERSAL_PLAYBACK_ID` (Mux ID or stream.mux.com URL)
 * feeds Telegram/channel_invite when per-locale IDs are unset; optional `VITE_MUX_CTA_FREE_JOIN_COPY`
 * switches CTA copy to “join free” (see videoCTAConfig). Cartel sutil encima del CTA:
 * `VITE_MUX_CTA_FRIENDLY_BANNER=true` o free-join; apagar: `VITE_MUX_CTA_FRIENDLY_BANNER=false` (y sin free-join).
 *
 * Add new pages by extending STATIC_PAGE_VIDEO_REGISTRY; no component changes.
 *
 * Usage:
 * - Static pages: `page` or pathname → config per page (video + CTA redirect).
 * - BioLinkTemplate modal replacement: use `variant="modal"` + config (playbackId, ctaUrl,
 *   ctaLabel, onClose, onCTAClick). One modal block, config-driven; any button can open it
 *   with its own config. You define which modal to replace when wiring the flow.
 * - Overlays / other: manual playbackId/ctaUrl or `service`.
 * - DualCTAVideoLanding / VideoVipTemplate: `hideNativeCta` + `skipWorkerTemplateCta` — solo
 *   video Mux; los CTAs los renderiza el padre encima del player.
 *
 * Config priority: manual > page > service. Con Cloudflare, `resolveWorkerDrivenCta()` puede
 * sustituir URL/label del CTA salvo `skipWorkerTemplateCta`.
 *
 * País para pricing / URLs por país: prop `countryCode` si viene del padre (Worker); si no,
 * `detectCountryCode()` (prioriza `getServerTemplateConfig()` → __TEMPLATE_CONFIG__ o cookies).
 */

import {
  getMuxCtaUniversalPlaybackId,
} from "@/config/muxCtaUniversalPlayback";
import {
  type StaticPageVideoPage,
  getStaticPageFromPathname,
  getStaticPageVideoConfig,
} from "@/config/staticPageVideoConfig";
import {
  CREATE_PAYMENT_INTENT_ENDPOINT,
  STRIPE_APPEARANCE,
  STRIPE_APPEARANCE_EXCLUSIVE,
  STRIPE_PUBLISHABLE_KEY,
  getPackPricing as getPackPricingStatic,
} from "@/config/stripe";
import {
  TELEGRAM_GROUPS_EN_PLAYBACK_ID,
  getTelegramCTAUrlByCountry,
  getTelegramPlaybackIdByCountry,
} from "@/config/telegramGroupsVideo";
import {
  type MuxFriendlyFunnelOpts,
  type SupportedLanguage,
  type VideoCTAService,
  getLocalizedLabel,
  getLocalizedTitle,
  getMuxCtaFriendlyBannerMessage,
  getVideoCTAConfigWithPayment,
} from "@/config/videoCTAConfig";
import { trackButtonClickGA } from "@/lib/analytics";
import {
  BRAND_MUX_DEFAULT_PLAYBACK_ID,
  BRAND_PERSON_NAME,
  BRAND_SOURCE_KEY,
} from "@/lib/brandConfig";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import { getPostPaymentSuccessPath } from "@/lib/muchasGraciasSuccessUrl";
// MercadoPago deshabilitado para checkouts: todos los países usan Stripe one-time
// (`/api/create-payment-intent`) — ver `createPaymentIntent` más abajo.
import type { PackConfig } from "@/lib/api";
import { detectCountryCode } from "@/lib/countryDetection";
import {
  applyInstagramBrowserCSS,
  isInstagramBrowser,
} from "@/lib/instagramBrowserDetection";
import {
  detectPlaybackBrowserEnv,
  isSoftPlaybackResumeReason,
} from "@/lib/muxPlayerCrossBrowser";

/*
 * NO agregar `envKey` acá.
 *
 * Mux Data ya está activo en este player. En @mux/playback-core el monitoreo
 * arranca con:
 *
 *     if (!disableTracking && (envKey || isMuxVideo)) { muxDataSDK.monitor(...) }
 *
 * `isMuxVideo` es true con sólo tener `playbackId`, así que para video alojado
 * en Mux el reporte ocurre igual y el environment se infiere del playback ID.
 * `env_key` únicamente se agrega al payload si se lo pasa explícito.
 *
 * Pasarlo, entonces, no habilita nada — pero si la env apuntara a un
 * environment distinto del dueño del playback ID, partiría las métricas en dos.
 * Riesgo sin contrapartida. ReelsFeed sí lo pasa porque reproduce assets de
 * shares que pueden vivir en otro environment.
 */
import {
  getUserLanguage,
  usePackConfigForVideo,
} from "@/lib/packConfigHelpers";
import { fetchPremiumInvitePublicConfig } from "@/lib/premiumInvitePublicConfig";
import { resolveFreeJoinTelegramUrlByCountry } from "@/lib/resolveFreeJoinTelegramUrl";
import { resolveMuxFriendlyFunnelOpts } from "@/lib/premiumInviteMuxFunnel";
import type { PremiumInvitePublicConfig } from "@/lib/premiumInvitePublicConfig";
import { trackButtonClickPostHog, trackStripePayFailurePostHog } from "@/lib/posthogAnalytics";
import {
  computeStripePackCheckoutAmounts,
  getOrCreateSessionWatcherLuckPercent,
  getWatcherLuckDiscountMaxPercentClient,
  STRIPE_EXCLUSIVE_PACK_DISCOUNT_PERCENT,
} from "@/lib/watcherLuckDiscount";
import { usePackConfig } from "@/lib/usePackConfig";
import {
  getWorkerCtaDisplayLabel,
  resolveWorkerDrivenCta,
} from "@/lib/workerDrivenCta";
import MuxPlayer from "@mux/mux-player-react";
import type { HlsConfig } from "hls.js";
import { Elements } from "@stripe/react-stripe-js";
import { type Stripe, loadStripe } from "@stripe/stripe-js";
import FreeJoinEntrySheet from "@/components/free-join/FreeJoinEntrySheet";
import { PoweredByStripe } from "@/components/checkout/PoweredByStripe";
import {
  ChevronRight,
  ExternalLink,
  Instagram,
  Loader2,
  Play,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useLocation } from "react-router-dom";
import { StripeCheckoutForm } from "./StripeCheckoutForm";
import { getDatafastAttributionPayload } from "@/lib/datafast";

const SAFE_PLAYBACK_ID_FALLBACK = "mux-cta-default";
/**
 * Guaranteed-valid Mux playback ID when env-based fallbacks are missing.
 * Sourced from brandConfig so each brand/fork serves ITS own video instead of a
 * shared hardcoded ID (matches telegramGroupsVideo's HARDCODED_FALLBACK export).
 */
const HARDCODED_FALLBACK_PLAYBACK_ID = BRAND_MUX_DEFAULT_PLAYBACK_ID;

/** “Suerte” toast: no mostrar al iniciar play; solo tras contexto de vista real o fallback de tiempo. */
const WATCHER_LUCK_MIN_PLAYHEAD_SEC = 8.25;
const WATCHER_LUCK_FALLBACK_WALL_MS = 17_000;
const WATCHER_LUCK_TOAST_MS = 5_400;

/**
 * Immersive funnel chrome kill-switch (Mux media-chrome + iOS native).
 * Applied as CSS vars on `<mux-player>` AND mirrored in stylesheet rules.
 * `--media-control-display` is the internal var (`base.mjs`); `--controls` feeds it.
 */
const IMMERSIVE_MUX_CHROME_STYLE = {
  "--controls": "none",
  "--controls-backdrop-color": "transparent",
  "--media-control-display": "none",
  "--center-controls": "none",
  "--bottom-controls": "none",
  "--top-controls": "none",
  "--center-play-button": "none",
  "--bottom-play-button": "none",
  "--pre-play-button": "none",
  "--time-display": "none",
  "--duration-display": "none",
  "--seek-backward-button": "none",
  "--seek-forward-button": "none",
  "--mute-button": "none",
  "--captions-button": "none",
  "--airplay-button": "none",
  "--pip-button": "none",
  "--fullscreen-button": "none",
  "--cast-button": "none",
  "--playback-rate-button": "none",
  "--volume-range": "none",
  "--time-range": "none",
  "--rendition-menu-button": "none",
  "--audio-track-menu-button": "none",
  "--loading-indicator": "none",
  "--dialog": "none",
  pointerEvents: "none",
} as const satisfies Record<string, string>;

/** Once per page load — assert immersive chrome for PostHog validation (not user UI). */
let dualCtaChromeAsserted = false;

function assertImmersiveChromeOnce(opts: {
  playbackId?: string | null;
  country?: string | null;
}): void {
  if (dualCtaChromeAsserted || typeof window === "undefined") return;
  dualCtaChromeAsserted = true;
  try {
    mirrorFunnelEvent({
      event_name: "dual_cta_player_chrome_assert",
      template: "MuxVideoPlayerWithCTA",
      country: opts.country || undefined,
      props: {
        controls_css: "none",
        media_control_display: "none",
        center_controls: "none",
        nohotkeys: true,
        pointer_events: "none",
        note: "media-controller exists in Mux shadow DOM but CSS forces display none; not user-visible chrome",
        playback_id: opts.playbackId
          ? String(opts.playbackId).slice(0, 40)
          : undefined,
        source: BRAND_SOURCE_KEY,
      },
    });
  } catch {
    /* no-fail */
  }
}

/**
 * Strip native / WebKit chrome that bypasses Mux CSS vars (IG iOS, Safari).
 * Safe to call repeatedly — idempotent.
 */
function hardenNativeVideoElement(video: HTMLVideoElement | null | undefined): void {
  if (!video) return;
  try {
    // Only touch attributes that are actually wrong — avoids MutationObserver thrash
    // (setAttribute with same value is usually fine, but controls/removeAttribute
    // races with mux-player re-applying chrome caused main-thread freezes).
    if (video.controls) {
      video.controls = false;
    }
    if (video.hasAttribute("controls")) {
      video.removeAttribute("controls");
    }
    if (video.getAttribute("playsinline") !== "") {
      video.setAttribute("playsinline", "");
    }
    if (video.getAttribute("webkit-playsinline") !== "") {
      video.setAttribute("webkit-playsinline", "");
    }
    if (video.getAttribute("x5-playsinline") !== "true") {
      video.setAttribute("x5-playsinline", "true");
    }
    const list = "nodownload nofullscreen noremoteplayback noplaybackrate";
    if (video.getAttribute("controlslist") !== list) {
      video.setAttribute("controlslist", list);
    }
    if (!video.hasAttribute("disablepictureinpicture")) {
      video.setAttribute("disablepictureinpicture", "");
    }
    // Property form (iOS Safari)
    try {
      (video as HTMLVideoElement & { playsInline?: boolean }).playsInline = true;
    } catch {
      /* ignore */
    }
    try {
      video.disablePictureInPicture = true;
    } catch {
      /* ignore */
    }
    const remote = video as HTMLVideoElement & {
      disableRemotePlayback?: boolean;
    };
    if ("disableRemotePlayback" in remote) {
      try {
        remote.disableRemotePlayback = true;
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* ignore — never block playback on hardening */
  }
}

/** High-frequency force_resume_* was tripling PostHog captures and freezing mobile tabs. */
const HIGH_FREQ_MUX_EVENTS = new Set([
  "force_resume_attempt",
  "force_resume_success",
  "video_buffering",
  "video_stalled",
]);
let lastForceResumePostHogAt = 0;
const FORCE_RESUME_POSTHOG_MIN_MS = 8_000;

/**
 * Log para producción: trackea todos los eventos de video y errores de red
 * Envía logs al servidor (Axiom) y PostHog para análisis.
 *
 * PostHog events (filterable):
 * - `mux_video_event` — envelope with nested `event` field (legacy dashboards)
 * - `mux_video_health` — same props + `stage` (= event) for Insights / funnels
 * - `mux_video_<event>` — per-stage top-level names for simple event filters
 */
function logVideoEvent(payload: {
  event:
    | "video_error"
    | "video_load_start"
    | "video_load_success"
    | "video_load_failed"
    | "video_play_attempt"
    | "video_play_success"
    | "video_play_failed"
    | "video_stalled"
    | "video_buffering"
    | "network_error"
    | "playback_id_invalid"
    | "fallback_attempted"
    | "fallback_failed"
    | "video_shown_thumbnail_only"
    | "autoplay_startup_path"
    | "force_resume_attempt"
    | "force_resume_success"
    | "force_resume_failed"
    | "mark_playback_started"
    | "manual_play_required"
    | "canplay_received";
  country?: string;
  service?: string;
  playbackId?: string;
  errorType?: string;
  errorMessage?: string;
  errorCode?: string | number;
  networkState?: number;
  readyState?: number;
  isNetworkError?: boolean;
  isTokenError?: boolean;
  fallbackAttempted?: boolean;
  userAgent?: string;
  connectionType?: string;
  duration?: number;
  currentTime?: number;
  /** Extra dimensions for PostHog breakdowns */
  reason?: string;
  muted?: boolean;
  isInstagram?: boolean;
  isMobile?: boolean;
  hasPlaybackStarted?: boolean;
}) {
  const data = {
    source: "MuxVideoPlayerWithCTA",
    site: BRAND_SOURCE_KEY,
    brand_source: BRAND_SOURCE_KEY,
    stage: payload.event,
    outcome:
      payload.event.includes("fail") ||
      payload.event.includes("error") ||
      payload.event === "manual_play_required" ||
      payload.event === "video_shown_thumbnail_only"
        ? "failure_or_degraded"
        : payload.event.includes("success") ||
            payload.event === "mark_playback_started" ||
            payload.event === "canplay_received"
          ? "success"
          : "info",
    ...payload,
    ts: new Date().toISOString(),
    url: window.location.href,
    pathname:
      typeof window !== "undefined" ? window.location.pathname : undefined,
    userAgent: navigator.userAgent,
    // Network info si está disponible
    connectionType:
      (navigator as { connection?: { effectiveType?: string } }).connection
        ?.effectiveType || "unknown",
    connectionDownlink:
      (navigator as { connection?: { downlink?: number } }).connection
        ?.downlink || null,
  };

  // Console: failures only; never spam force_resume (prod freeze vector).
  if (payload.event.includes("error") || payload.event.includes("failed")) {
    if (payload.event.startsWith("force_resume")) {
      // Throttle failed resume logs — can fire dozens/sec on IG iOS.
      const now = Date.now();
      if (now - lastForceResumePostHogAt < FORCE_RESUME_POSTHOG_MIN_MS) {
        /* skip console too */
      } else {
        lastForceResumePostHogAt = now;
        console.error("[MuxVideoPlayerWithCTA]", payload.event, payload.reason);
      }
    } else {
      console.error("[MuxVideoPlayerWithCTA]", JSON.stringify(data));
    }
  } else if (import.meta.env.DEV && !HIGH_FREQ_MUX_EVENTS.has(payload.event)) {
    console.log("[MuxVideoPlayerWithCTA]", JSON.stringify(data));
  }

  // PostHog: one event (delfiocampo pattern). Never flood on force_resume/buffer.
  if (
    typeof window !== "undefined" &&
    (window as { posthog?: { capture: (e: string, p?: object) => void } })
      .posthog?.capture
  ) {
    if (HIGH_FREQ_MUX_EVENTS.has(payload.event)) {
      // drop — keep main thread free
    } else {
      const ph = (
        window as { posthog: { capture: (e: string, p?: object) => void } }
      ).posthog;
      try {
        ph.capture("mux_video_event", data);
      } catch {
        /* never break playback for analytics */
      }
    }
  }

  // Axiom: solo mismo origen o proxy. URLs `*.axiom.co` sin CORS en navegador → preflight falla.
  if (typeof window !== "undefined") {
    const axiomEndpoint =
      (import.meta.env.VITE_AXIOM_INGEST_ENDPOINT as string) ||
      (import.meta.env.NEXT_PUBLIC_AXIOM_INGEST_ENDPOINT as string);
    const axiomToken =
      (import.meta.env.VITE_AXIOM_TOKEN as string) ||
      (import.meta.env.NEXT_PUBLIC_AXIOM_TOKEN as string);
    const raw = axiomEndpoint?.trim() ?? "";
    if (raw) {
      let allowBrowser = false;
      try {
        if (raw.startsWith("/")) {
          allowBrowser = true;
        } else {
          const host = new URL(raw).hostname.toLowerCase();
          allowBrowser = !host.endsWith("axiom.co");
        }
      } catch {
        allowBrowser = false;
      }

      if (allowBrowser) {
        fetch(raw, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-ndjson",
            ...(axiomToken && axiomToken.trim() !== ""
              ? { Authorization: `Bearer ${axiomToken}` }
              : {}),
          },
          body: JSON.stringify(data) + "\n",
          keepalive: true,
        }).catch(() => {
          /* fire-and-forget */
        });
      }
    }
  }
}

// Mantener compatibilidad con código existente
function logVideoErrorProduction(payload: Parameters<typeof logVideoEvent>[0]) {
  logVideoEvent(payload);
}

// Checkout flow states
type CheckoutState =
  | "VIDEO_PREVIEW"
  | "CHECKOUT"
  | "PROCESSING"
  | "SUCCESS"
  | "ERROR";

// Initialize Stripe
let stripePromise: Promise<Stripe | null> | null = null;
const getStripe = () => {
  if (!stripePromise && STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

/** Sanitiza playbackId para uso en CSS/className (evita XSS). */
function sanitizePlaybackIdForCss(id: string): string {
  const safe = id.replace(/[^a-zA-Z0-9_-]/g, "");
  return safe.length > 0 ? safe : SAFE_PLAYBACK_ID_FALLBACK;
}

/** Valida que la URL sea http/https o relativa (rechaza javascript:, data:, etc.). */
function isAllowedRedirectUrl(url: string): boolean {
  // Allow relative URLs (starting with /)
  if (url.startsWith("/")) {
    return true;
  }

  // For absolute URLs, check protocol
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Sync browser env for first MuxPlayer paint (Safari/Chrome/IG must not wait for useEffect).
 * Resize only toggles the coarse isMobile flag.
 */
const usePlaybackBrowserEnv = () => {
  const [env, setEnv] = useState(() => detectPlaybackBrowserEnv());

  useEffect(() => {
    const sync = () => {
      const next = detectPlaybackBrowserEnv();
      setEnv((prev) => {
        if (
          prev.isMobile === next.isMobile &&
          prev.isInstagram === next.isInstagram &&
          prev.isIOS === next.isIOS &&
          prev.isAndroid === next.isAndroid &&
          prev.needsMutedAutoplay === next.needsMutedAutoplay &&
          prev.needsSoftKeepAlive === next.needsSoftKeepAlive &&
          prev.preferNativePlayback === next.preferNativePlayback
        ) {
          return prev;
        }
        return next;
      });
    };
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  return env;
};

/**
 * Chromium desktop en macOS (Chrome, Brave, Arc, Opera…) y Arc explícito.
 * Arc a veces no incluye "Arc/" en el user agent; el síntoma típico es fallo MSE en el buffer
 * de audio (`SourceBuffer`/`appendBuffer`). Activamos el mismo endurecimiento para todo
 * Chrome en Mac (no Safari, no Edge).
 */
function detectMuxDesktopMacMseHardening(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/\bArc\/\d/i.test(ua)) return true;
  const brands = (
    navigator as Navigator & {
      userAgentData?: { brands?: { brand: string }[] };
    }
  ).userAgentData?.brands;
  if (brands?.some((b) => b.brand === "Arc")) return true;

  const mac = /Mac OS X|Macintosh/.test(ua);
  const chromiumNotEdge = /\bChrome\/\d/.test(ua) && !/\bEdg\/\d/.test(ua);

  return mac && chromiumNotEdge;
}

const useMuxDesktopMacMseHardening = () =>
  useMemo(() => detectMuxDesktopMacMseHardening(), []);

/**
 * Detect if device is in landscape orientation
 */
const useIsLandscape = () => {
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkOrientation = () => {
      // Use screen.orientation if available, fallback to window dimensions
      if (screen.orientation) {
        setIsLandscape(screen.orientation.type.includes("landscape"));
      } else {
        setIsLandscape(window.innerWidth > window.innerHeight);
      }
    };

    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", checkOrientation);

    return () => {
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", checkOrientation);
    };
  }, []);

  return isLandscape;
};

/**
 * Config for using MuxVideoPlayerWithCTA as overlay in BioLinkTemplate (fullscreen reel or modal).
 * Pass variant + these props + onClose. "fullscreen" = reel-style full viewport; "modal" = centered panel.
 */
export type VideoCTAModalConfig = Pick<
  MuxVideoPlayerWithCTAProps,
  | "service"
  | "playbackId"
  | "ctaUrl"
  | "ctaLabel"
  | "title"
  | "openInNewTab"
  | "displayMode"
  | "loop"
  | "playbackIdByCountry"
  | "ctaUrlByCountry"
  | "countryCode"
  | "language"
  | "skipWorkerTemplateCta"
> & {
  onCTAClick?: (url: string) => void;
  /** "fullscreen" = reel full viewport (default); "modal" = centered panel. */
  variant?: "fullscreen" | "modal";
};

/**
 * Telemetría ligera de posición de reproducción (BioTemplatePremium / funnel).
 * `timeupdate` está limitada a ~1 evento / 4s para no saturar PostHog.
 */
export type PlaybackTelemetryPayload = {
  currentTimeSeconds: number;
  durationSeconds: number | null;
  playbackId: string;
  reason: "timeupdate" | "pause" | "ended";
};

export interface MuxVideoPlayerWithCTAProps {
  // Option 1: Use service type for automatic configuration
  service?: VideoCTAService;
  countryCode?: string;
  language?: "en" | "es" | "de" | "fr" | "it" | "pt" | "nl";

  // Option 2: Static page – video + CTA per page. CTA redirect/label/icon come from
  // staticPageVideoConfig (registry + env). Set per page according to needs.
  page?: StaticPageVideoPage;

  // Option 3: Manual configuration (overrides service/page config if provided)
  playbackId?: string;
  /**
   * Si true, el playbackId manual tiene prioridad absoluta y NO se usan fallbacks por país.
   * Útil para templates con videos específicos (e.g. BioTemplatePremium).
   */
  forceManualPlaybackId?: boolean;
  title?: string;
  ctaUrl?: string; // Redirect al hacer click en "Unirte"
  ctaLabel?: string; // default: "Unirte"
  /**
   * Visual + UX mode for the floating CTA.
   * - `vip_membership`: premium gold style; never shows price on the video
   *   (price appears only after press → parent drawer/checkout).
   * - `default`: existing pack/channel CTA behavior.
   */
  ctaVariant?: "default" | "vip_membership";
  /** Texto opcional arriba del botón CTA (ej. "Ingresar al Canal Privado + Hablar conmigo") */
  ctaSubtitle?: string;
  /**
   * CTA secundario debajo del principal (ej. "Acceso gratis" → registro Clerk
   * con aprobación manual). Solo se renderiza si ambos props están presentes;
   * respeta hideNativeCta (comparte contenedor con el CTA principal).
   */
  secondaryCtaLabel?: string;
  onSecondaryCtaClick?: () => void;
  /**
   * Overlay VIP en BioTemplatePremiumSimple: cartel “no es VIP” cuando el CTA abre Telegram gratis
   * (`premiumSimpleMembershipFreeFunnel` + `getMuxCtaFriendlyBannerMessage`).
   */
  premiumSimpleMembershipFreeFunnel?: boolean;
  /** Resolución admin + env para el cartel “no es VIP” (Invitación premium en DB). */
  muxFriendlyFunnel?: MuxFriendlyFunnelOpts | null;
  /**
   * Si true y hay URL de Telegram gratis, el CTA nativo abre ese canal gratis en vez del checkout.
   * Nota: Argentina queda siempre excluida — su CTA va directo a Stripe (ver `isArgentinaCountry`).
   */
  freeJoinCTA?: boolean;
  openInNewTab?: boolean;

  // Option 4: Country-specific video configurations
  // Supports per-country videos for different languages/regions
  playbackIdByCountry?: {
    // North America
    US?: string;

    // Europe
    ES?: string; // Spain
    DE?: string; // Germany
    FR?: string; // France

    // LATAM (BioLinkTemplate countries)
    MX?: string; // Mexico
    CL?: string; // Chile
    CO?: string; // Colombia
    PE?: string; // Peru
    PY?: string; // Paraguay
    AR?: string; // Argentina
    BR?: string; // Brazil

    // Middle East
    AE?: string; // UAE (United Arab Emirates / Emiratos)

    // Fallback for other countries
    default?: string;
  };

  // Option 5: Country-specific CTA URL configurations (Stripe Payment Links)
  // Different pricing per country - managed via Railway env vars
  ctaUrlByCountry?: {
    // North America
    US?: string; // e.g. $14.99 USD

    // Europe
    ES?: string; // e.g. €12.99 EUR (Spain)
    DE?: string; // e.g. €14.99 EUR (Germany)
    FR?: string; // e.g. €13.99 EUR (France)

    // LATAM (BioLinkTemplate countries) - Each country can have different pricing
    MX?: string; // e.g. $199 MXN (Mexico)
    CL?: string; // e.g. CLP pricing (Chile)
    CO?: string; // e.g. COP pricing (Colombia)
    PE?: string; // e.g. PEN pricing (Peru)
    PY?: string; // e.g. PYG pricing (Paraguay)
    AR?: string; // e.g. ARS pricing (Argentina)
    BR?: string; // e.g. R$49 BRL (Brazil)

    // Middle East
    AE?: string; // e.g. AED 49 (UAE / Emiratos)

    // Fallback for other countries (generic LATAM pricing)
    default?: string;
  };

  displayMode?: "reel" | "contain" | "cover" | "auto";
  loop?: boolean;
  
  /** 
   * Control de autoplay. Por defecto "any" (intenta con sonido, fallback muted).
   * - "any": autoplay con sonido, fallback muted (TikTok/Reels style)
   * - "muted": autoplay solo muted
   * - false: no autoplay, requiere click del usuario
   */
  autoPlay?: "any" | "muted" | false;

  /** 'fullscreen' = fill container (default). 'modal' = backdrop + centered rounded panel, Escape/backdrop to close. */
  variant?: "fullscreen" | "modal";

  /** When set, custom checkout uses this pack (e.g. exclusive, sorpresa) for pricing and payment instead of channel_invite. */
  overridePackId?: string;

  /** Admin-generated invite code that gated access to this page. Sent to the
   * checkout API for server-side expiry enforcement. If the code has expired
   * by the time the user taps the CTA, the server rejects the checkout. */
  inviteCode?: string;

  /** Si true, no se muestra el aviso "Toca para escuchar". Solo FullscreenExclusive (/exclusive) debe pasarlo; otras rutas no. */
  hideUnmutePrompt?: boolean;

  /**
   * Si true, no aplica el CTA inyectado por Cloudflare (`window.__TEMPLATE_CONFIG__`).
   * Por defecto false: `hideCta` vacía el CTA; `showCommunity` / `showBirthday` / `showWishlist` sustituyen URL y texto.
   */
  skipWorkerTemplateCta?: boolean;

  /**
   * Si true, no renderiza el botón flotante nativo del player (`.action-button-container`).
   * Usar en DualCTAVideoLanding y plantillas con CTAs custom encima del video.
   */
  hideNativeCta?: boolean;

  /**
   * El padre renderiza `MuxPosterBackdrop` y controla el loading UX.
   * Evita thumbnails, shimmer y loader pesado duplicados dentro del player.
   */
  externalPosterBackdrop?: boolean;

  /**
   * Landing fullscreen (DualCTA): permite desmutear vía state después del autoplay.
   * En mobile/IG el autoplay es SIEMPRE muted-first (replay 019fb326: sound-first →
   * NotAllowedError + mediapaused). Con este flag, `muxPlayerMuted` sigue `isMuted`
   * (no fuerza mute permanente en Instagram); el audio real sigue pidiendo tap del usuario.
   */
  allowSoundAutoplayInWebView?: boolean;

  /**
   * TikTok/Reel conversion landing: single video + CTA, no swipe feed.
   * Fast startup (preload auto, 480p mobile cap), full-bleed cover, touchAction none.
   * Defaults true when `displayMode="reel"` + `externalPosterBackdrop`.
   */
  conversionReel?: boolean;

  onReady?: () => void;
  onPlay?: () => void;
  /** Cuando autoplay falla y el usuario debe tocar play (con sonido). */
  onRequireUserPlay?: () => void;
  /** Estado mute efectivo tras autoplay / toggle. */
  onMutedChange?: (muted: boolean) => void;
  onEnded?: () => void;
  onCTAClick?: (url: string) => void;
  onClose?: () => void;
  /** Fired throttled (~4s) on timeupdate, and always on pause/ended — para métricas de cuánto vio el video. */
  onPlaybackTelemetry?: (payload: PlaybackTelemetryPayload) => void;
  /** Fired when Mux reports a confirmed video error (after retries, fatal to the user). */
  onError?: (message: string) => void;

  className?: string;
  containerClassName?: string;
}

// Translation helper for UI texts
type TranslationKey =
  | "preparingCheckout"
  | "openingCheckoutPage"
  | "paymentError"
  | "paymentErrorMsg"
  | "retry"
  | "back"
  | "enableAudio"
  | "tapForSound"
  | "tapToPlayWithSound"
  | "openInBrowser"
  | "betterExperience"
  | "linkCopied"
  | "pasteInBrowser"
  | "longPressHint"
  | "loading"
  | "loadingVideo"
  | "connectingToPlayer"
  | "inAppBrowserVideoLoadHint"
  | "inAppBrowserMenuHint"
  | "startingPlayback"
  | "slowConnection"
  | "optimizingStream"
  | "decodingPreview"
  | "playbackReadyBrief"
  | "freeJoinSignUpPrompt";

const translations: Record<
  TranslationKey,
  Record<SupportedLanguage, string>
> = {
  preparingCheckout: {
    en: "Preparing checkout...",
    es: "Preparando checkout...",
    de: "Checkout wird vorbereitet...",
    fr: "Préparation du paiement...",
    it: "Preparazione checkout...",
    pt: "Preparando checkout...",
    nl: "Afrekenen voorbereiden...",
  },
  openingCheckoutPage: {
    en: "Opening checkout — please wait…",
    es: "Abriendo el checkout — esperá un momento…",
    de: "Checkout wird geöffnet — bitte warten…",
    fr: "Ouverture du paiement — patientez…",
    it: "Apertura del checkout — attendere…",
    pt: "Abrindo o checkout — aguarde…",
    nl: "Checkout openen — even geduld…",
  },
  paymentError: {
    en: "Payment error",
    es: "Error en el pago",
    de: "Zahlungsfehler",
    fr: "Erreur de paiement",
    it: "Errore di pagamento",
    pt: "Erro no pagamento",
    nl: "Betaalfout",
  },
  paymentErrorMsg: {
    en: "Something went wrong. Please try again.",
    es: "Hubo un problema al procesar tu pago. Por favor, intenta de nuevo.",
    de: "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.",
    fr: "Quelque chose s'est mal passé. Veuillez réessayer.",
    it: "Si è verificato un problema. Per favore riprova.",
    pt: "Algo deu errado. Por favor, tente novamente.",
    nl: "Er is iets misgegaan. Probeer het opnieuw.",
  },
  retry: {
    en: "Try again",
    es: "Reintentar",
    de: "Erneut versuchen",
    fr: "Réessayer",
    it: "Riprova",
    pt: "Tentar novamente",
    nl: "Opnieuw proberen",
  },
  back: {
    en: "Back",
    es: "Volver",
    de: "Zurück",
    fr: "Retour",
    it: "Indietro",
    pt: "Voltar",
    nl: "Terug",
  },
  enableAudio: {
    en: "Enable audio",
    es: "Activar audio",
    de: "Audio aktivieren",
    fr: "Activer l'audio",
    it: "Attiva audio",
    pt: "Ativar áudio",
    nl: "Audio inschakelen",
  },
  /** Short label on the subtle mute chip (keep brief — video stays visible). */
  tapForSound: {
    en: "Sound",
    es: "Sonido",
    de: "Ton",
    fr: "Son",
    it: "Audio",
    pt: "Som",
    nl: "Geluid",
  },
  tapToPlayWithSound: {
    en: "🔊 Tap to play with sound",
    es: "🔊 Toca para reproducir con audio",
    de: "🔊 Tippen um mit Ton abzuspielen",
    fr: "🔊 Toucher pour lire avec le son",
    it: "🔊 Tocca per riprodurre con audio",
    pt: "🔊 Toque para reproduzir com som",
    nl: "🔊 Tik om af te spelen met geluid",
  },
  openInBrowser: {
    en: "Open in Browser",
    es: "Abrir en Navegador",
    de: "Im Browser öffnen",
    fr: "Ouvrir dans le navigateur",
    it: "Apri nel browser",
    pt: "Abrir no navegador",
    nl: "Openen in browser",
  },
  betterExperience: {
    en: "For the best experience, open this page in your browser",
    es: "Para una mejor experiencia, abre esta página en tu navegador",
    de: "Für die beste Erfahrung, öffnen Sie diese Seite in Ihrem Browser",
    fr: "Pour une meilleure expérience, ouvrez cette page dans votre navigateur",
    it: "Per un'esperienza migliore, apri questa pagina nel tuo browser",
    pt: "Para uma melhor experiência, abra esta página no seu navegador",
    nl: "Voor de beste ervaring, open deze pagina in uw browser",
  },
  linkCopied: {
    en: "Link copied!",
    es: "¡Enlace copiado!",
    de: "Link kopiert!",
    fr: "Lien copié !",
    it: "Link copiato!",
    pt: "Link copiado!",
    nl: "Link gekopieerd!",
  },
  pasteInBrowser: {
    en: "Paste in Safari or Chrome to open",
    es: "Pega en Safari o Chrome para abrir",
    de: "In Safari oder Chrome einfügen zum Öffnen",
    fr: "Collez dans Safari ou Chrome pour ouvrir",
    it: "Incolla in Safari o Chrome per aprire",
    pt: "Cole no Safari ou Chrome para abrir",
    nl: "Plak in Safari of Chrome om te openen",
  },
  longPressHint: {
    en: "Long-press here to open in Safari",
    es: "Mantén pulsado aquí para abrir en Safari",
    de: "Hier lange drücken um in Safari zu öffnen",
    fr: "Maintenez ici pour ouvrir dans Safari",
    it: "Tieni premuto qui per aprire in Safari",
    pt: "Mantenha pressionado aqui para abrir no Safari",
    nl: "Houd hier ingedrukt om in Safari te openen",
  },
  loading: {
    en: "Loading...",
    es: "Cargando...",
    de: "Laden...",
    fr: "Chargement...",
    it: "Caricamento...",
    pt: "Carregando...",
    nl: "Laden...",
  },
  loadingVideo: {
    en: "Loading video...",
    es: "Cargando video...",
    de: "Video wird geladen...",
    fr: "Chargement de la vidéo...",
    it: "Caricamento video...",
    pt: "Carregando vídeo...",
    nl: "Video laden...",
  },
  connectingToPlayer: {
    en: "Connecting to the player…",
    es: "Conectando con el reproductor…",
    de: "Verbindung zum Player…",
    fr: "Connexion au lecteur…",
    it: "Connessione al player…",
    pt: "Conectando ao player…",
    nl: "Verbinden met speler…",
  },
  inAppBrowserVideoLoadHint: {
    en: "Instagram / Facebook browser can be a bit slower — thanks for your patience.",
    es: "En el navegador de Instagram o Facebook la carga puede tardar un poco más.",
    de: "Im Instagram-/Facebook-Browser kann es etwas länger dauern.",
    fr: "Dans le navigateur Instagram/Facebook, le chargement peut être un peu plus lent.",
    it: "Nel browser di Instagram/Facebook il caricamento può richiedere più tempo.",
    pt: "No navegador do Instagram/Facebook o carregamento pode demorar um pouco mais.",
    nl: "In de Instagram-/Facebook-browser kan het iets langer duren.",
  },
  inAppBrowserMenuHint: {
    en: "Tip: tap ⋮ or “…” (top) → Open in browser for smoother playback.",
    es: "Tip: tocá ⋮ o “…” (arriba) → Abrir en el navegador para mejor reproducción.",
    de: "Tipp: ⋮ oben → Im Browser öffnen für flüssigere Wiedergabe.",
    fr: "Astuce : ⋮ en haut → Ouvrir dans le navigateur pour une lecture plus fluide.",
    it: "Suggerimento: ⋮ in alto → Apri nel browser per una riproduzione più fluida.",
    pt: "Dica: ⋮ no topo → Abrir no navegador para reprodução mais suave.",
    nl: "Tip: ⋮ bovenaan → Openen in browser voor vloeiender afspelen.",
  },
  startingPlayback: {
    en: "Starting playback…",
    es: "Iniciando reproducción…",
    de: "Wiedergabe wird gestartet…",
    fr: "Démarrage de la lecture…",
    it: "Avvio riproduzione…",
    pt: "Iniciando reprodução…",
    nl: "Afspelen starten…",
  },
  slowConnection: {
    en: "Slow connection detected. Still loading your video...",
    es: "Detectamos red lenta. Seguimos cargando el video...",
    de: "Langsame Verbindung erkannt. Video wird noch geladen...",
    fr: "Connexion lente détectée. Chargement en cours...",
    it: "Connessione lenta rilevata. Caricamento in corso...",
    pt: "Conexão lenta detectada. Ainda carregando o vídeo...",
    nl: "Trage verbinding gedetecteerd. Video wordt nog geladen...",
  },
  optimizingStream: {
    en: "Optimizing stream quality...",
    es: "Optimizando calidad para tu conexión...",
    de: "Stream-Qualität wird optimiert...",
    fr: "Optimisation de la qualité du flux...",
    it: "Ottimizzazione della qualità dello streaming...",
    pt: "Otimizando qualidade do stream...",
    nl: "Streamkwaliteit optimaliseren...",
  },
  decodingPreview: {
    en: "Caching the opening frames…",
    es: "Preparando los primeros fotogramas…",
    de: "Erste Bilder werden gepuffert…",
    fr: "Mise en cache des premières images…",
    it: "Preparazione dei primi fotogrammi…",
    pt: "Preparando os primeiros quadros…",
    nl: "Eerste beelden bufferen…",
  },
  playbackReadyBrief: {
    en: "Video ready — starting playback.",
    es: "Video listo — iniciando reproducción.",
    de: "Video bereit — Wiedergabe startet.",
    fr: "Vidéo prête — lecture imminente.",
    it: "Video pronto — avvio della riproduzione.",
    pt: "Vídeo pronto — iniciando reprodução.",
    nl: "Video klaar — afspelen start.",
  },
  freeJoinSignUpPrompt: {
    en: "Create your free account to join the channel — one tap and you're in.",
    es: "Creá tu cuenta gratis para unirte al canal — un toque y listo.",
    de: "Erstelle dein kostenloses Konto, um dem Kanal beizutreten — ein Klick genügt.",
    fr: "Crée ton compte gratuit pour rejoindre le canal — un tap et c'est parti.",
    it: "Crea il tuo account gratuito per unirti al canale — un tocco e sei dentro.",
    pt: "Crie sua conta grátis para entrar no canal — um toque e pronto.",
    nl: "Maak je gratis account aan om bij het kanaal te komen — één tik en je bent binnen.",
  },
};

function t(key: TranslationKey, lang: SupportedLanguage): string {
  return translations[key][lang] || translations[key].en;
}

/** Single wrapper for `window.posthogSafeCapture` — replaces the repeated 10-line type-cast pattern. */
function posthogSafeCapture(
  eventName: string,
  properties?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  const fn = (window as Record<string, unknown>).posthogSafeCapture;
  if (typeof fn === "function") {
    (fn as (e: string, p?: Record<string, unknown>) => void)(eventName, properties);
  }
}

const MuxVideoPlayerWithCTA: React.FC<MuxVideoPlayerWithCTAProps> = ({
  // Service-based configuration
  service,
  countryCode, // When passed (e.g. BioLinkTemplate overlay), used for pricing; else fallback to detected
  language, // Will be auto-detected if not provided

  // Static page – different video + CTA per route
  page: pageProp,

  // Manual configuration (overrides service/page if provided)
  playbackId: manualPlaybackId,
  forceManualPlaybackId = false,
  title: manualTitle,
  ctaUrl: manualCtaUrl,
  ctaLabel: manualCtaLabel,
  ctaVariant = "default",
  ctaSubtitle,
  secondaryCtaLabel,
  onSecondaryCtaClick,
  premiumSimpleMembershipFreeFunnel = false,
  muxFriendlyFunnel = null,
  /** Si true y hay URL de Telegram gratis, el CTA nativo abre ese canal gratis en vez del checkout. */
  freeJoinCTA = false,
  openInNewTab = false,
  hideUnmutePrompt = false,
  skipWorkerTemplateCta = false,
  hideNativeCta = false,
  externalPosterBackdrop = false,
  allowSoundAutoplayInWebView = false,
  conversionReel,

  // Country-specific video configurations
  playbackIdByCountry,
  ctaUrlByCountry,

  displayMode = "auto",
  loop = true,
  autoPlay = "any",
  variant = "fullscreen",
  overridePackId,
  inviteCode,
  onReady,
  onPlay,
  onRequireUserPlay,
  onMutedChange,
  onEnded,
  onCTAClick,
  onClose,
  onPlaybackTelemetry,
  onError: onErrorProp,
  className = "",
  containerClassName = "",
}) => {
  const effectivePackId = overridePackId ?? "channel_invite";
  /**
   * Exclusive Membership UX: gold CTA on the player + gold Stripe checkout drawer.
   * FullscreenExclusive passes `overridePackId="exclusive"`; drives styles + Stripe appearance variant.
   */
  const isExclusiveMembership = effectivePackId === "exclusive";
  /** VIP membership CTA: exclusive pack OR explicit vip_membership variant (Invitacion). */
  const isVipMembershipCta =
    ctaVariant === "vip_membership" || isExclusiveMembership;
  /** Never surface price on the video for VIP — drawer/checkout owns pricing after press. */
  const floatingCtaSubtitle = isVipMembershipCta ? undefined : ctaSubtitle;
  const { pathname } = useLocation();
  const { getPackPricing, packs } = usePackConfig();

  // Detect user's country on mount - initialize immediately for correct pricing/video
  // This runs synchronously on first render to avoid flash of wrong content
  const [detectedCountry, setDetectedCountry] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      const country = detectCountryCode();
      if (country && import.meta.env.DEV) {
        console.log(
          "[MuxVideoPlayerWithCTA] Initial country detection:",
          country,
        );
      }
      return country;
    }
    return null;
  });

  // Re-leer país tras el primer paint: mismo pipeline estándar (Cloudflare + cookies normalizadas).
  useEffect(() => {
    const sync = () => {
      const next = detectCountryCode();
      if (next) {
        setDetectedCountry((prev) => (prev === next ? prev : next));
      }
    };
    sync();
    const raf = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Network fallback: when playback fails, try English playback ID (para que siempre haya video)
  const [networkErrorFallbackPlaybackId, setNetworkErrorFallbackPlaybackId] =
    useState<string | null>(null);
  const hasTriedFallbackRef = useRef(false);

  // Stable ref for onError prop — avoids adding it as dep to handleError useCallback
  const onErrorRef = useRef(onErrorProp);
  useEffect(() => { onErrorRef.current = onErrorProp; }, [onErrorProp]);
  const onErrorThrottleRef = useRef<{
    lastKey: string;
    lastAt: number;
  }>({ lastKey: "", lastAt: 0 });

  // Auto-detect language from browser if not provided
  const detectedLanguage: "en" | "es" | "de" | "fr" | "it" | "pt" | "nl" =
    language ||
    (() => {
      const browserLang = navigator.language.toLowerCase();

      // Detect language from browser
      if (browserLang.startsWith("es") || browserLang.includes("es-"))
        return "es";
      if (browserLang.startsWith("de") || browserLang.includes("de-"))
        return "de";
      if (browserLang.startsWith("fr") || browserLang.includes("fr-"))
        return "fr";
      if (browserLang.startsWith("it") || browserLang.includes("it-"))
        return "it";
      if (browserLang.startsWith("pt") || browserLang.includes("pt-"))
        return "pt";
      if (browserLang.startsWith("nl") || browserLang.includes("nl-"))
        return "nl";

      // Default to English
      return "en";
    })();

  // Static-page config (sync): when `page` prop or pathname matches a known static page
  const resolvedPage: StaticPageVideoPage | null =
    pageProp ??
    (!manualPlaybackId && !manualCtaUrl && !service
      ? getStaticPageFromPathname(pathname)
      : null);
  const pageConfig = resolvedPage
    ? getStaticPageVideoConfig(resolvedPage)
    : null;

  // Load configuration from service if provided (and not using page config)
  const [serviceConfig, setServiceConfig] = useState<{
    playbackId: string;
    ctaUrl: string;
    ctaLabel: string;
    title?: string;
    playbackIdByCountry?: {
      // North America
      US?: string;
      // Europe
      ES?: string;
      DE?: string;
      FR?: string;
      // LATAM (BioLinkTemplate countries)
      MX?: string;
      CL?: string;
      CO?: string;
      PE?: string;
      PY?: string;
      AR?: string;
      BR?: string;
      // Middle East
      AE?: string;
      default?: string;
    };
    ctaUrlByCountry?: {
      // North America
      US?: string;
      // Europe
      ES?: string;
      DE?: string;
      FR?: string;
      // LATAM (BioLinkTemplate countries)
      MX?: string;
      CL?: string;
      CO?: string;
      PE?: string;
      PY?: string;
      AR?: string;
      BR?: string;
      // Middle East
      AE?: string;
      default?: string;
    };
  } | null>(null);

  // Initialize isLoadingConfig based on whether we need to load service config
  // This prevents race condition where playbackId is "" during initial render
  const needsServiceConfig = Boolean(
    service && !manualPlaybackId && !manualCtaUrl && !resolvedPage,
  );
  const [isLoadingConfig, setIsLoadingConfig] = useState(needsServiceConfig);
  const [freeJoinConfig, setFreeJoinConfig] = useState<{
    publicConfig: PremiumInvitePublicConfig | null;
    resolvedUrl: string | null;
    enabled: boolean | null;
  }>({ publicConfig: null, resolvedUrl: null, enabled: null });

  useEffect(() => {
    if (!freeJoinCTA) return;
    let cancelled = false;
    fetchPremiumInvitePublicConfig().then((cfg) => {
      if (cancelled) return;
      const resolvedCountry = (countryCode ?? detectCountryCode() ?? "US").toUpperCase();
      const resolvedUrl = resolveFreeJoinTelegramUrlByCountry(cfg, resolvedCountry);
      const funnel = resolveMuxFriendlyFunnelOpts(cfg);
      setFreeJoinConfig({ publicConfig: cfg, resolvedUrl: resolvedUrl ?? null, enabled: funnel.freeJoinCopy });
    });
    return () => { cancelled = true; };
  }, [freeJoinCTA, countryCode]);

  useEffect(() => {
    if (needsServiceConfig) {
      setIsLoadingConfig(true);
      // Argentina: sin free-join. No sustituir el CTA por Telegram gratis ni usar copy "gratis"; AR va a Stripe.
      const arCountry =
        (countryCode ?? detectCountryCode() ?? "US").trim().toUpperCase().slice(0, 2) === "AR";
      const effectiveFreeJoinUrl =
        freeJoinCTA && freeJoinConfig.enabled && !arCountry ? freeJoinConfig.resolvedUrl : null;
      const funnel = arCountry
        ? { friendlyBanner: "off" as const, freeJoinCopy: false }
        : freeJoinConfig.publicConfig
          ? resolveMuxFriendlyFunnelOpts(freeJoinConfig.publicConfig)
          : null;
      getVideoCTAConfigWithPayment(service!, countryCode, detectedLanguage, effectiveFreeJoinUrl, funnel)
        .then((config) => {
          if (config.hasVideo) {
            setServiceConfig({
              playbackId: config.playbackId,
              ctaUrl: config.ctaUrl,
              ctaLabel: getLocalizedLabel(config, language),
              title: getLocalizedTitle(config, language),
              playbackIdByCountry: config.playbackIdByCountry,
              ctaUrlByCountry: config.ctaUrlByCountry,
            });
          } else {
            setServiceConfig(null);
          }
          setIsLoadingConfig(false);
        })
        .catch((error) => {
          console.error(
            "[MuxVideoPlayerWithCTA] Error loading service config:",
            error,
          );
          setIsLoadingConfig(false);
        });
    } else {
      setServiceConfig(null);
      setIsLoadingConfig(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    needsServiceConfig,
    service,
    countryCode,
    language, // Use original prop, not derived detectedLanguage
    freeJoinCTA,
    freeJoinConfig.resolvedUrl,
    freeJoinConfig.enabled,
  ]);

  // Memoized country-specific resolution — runs only when country or configs change, not every render.
  const { countrySpecificPlaybackId, countrySpecificCtaUrl } = useMemo(() => {
    const resolvedCountry = (countryCode ?? detectedCountry ?? "US").toUpperCase();

    // Playback ID
    const pbConfig = playbackIdByCountry || serviceConfig?.playbackIdByCountry;
    let countrySpecificPlaybackId = "";
    if (pbConfig) {
      type PBKey = keyof typeof pbConfig;
      countrySpecificPlaybackId =
        (resolvedCountry in pbConfig && pbConfig[resolvedCountry as PBKey]
          ? (pbConfig[resolvedCountry as PBKey] as string)
          : null) ||
        pbConfig.default ||
        "";
    }

    // CTA URL
    const ctaConfig = ctaUrlByCountry || serviceConfig?.ctaUrlByCountry;
    let countrySpecificCtaUrl = "";
    if (ctaConfig) {
      type CTAKey = keyof typeof ctaConfig;
      const match =
        resolvedCountry in ctaConfig ? ctaConfig[resolvedCountry as CTAKey] : null;
      countrySpecificCtaUrl =
        (match && String(match).length > 0 ? (match as string) : null) ||
        ctaConfig.default ||
        "";
    }

    return { countrySpecificPlaybackId, countrySpecificCtaUrl };
  }, [
    countryCode,
    detectedCountry,
    playbackIdByCountry,
    ctaUrlByCountry,
    serviceConfig,
  ]);

  // =============================================================================
  // PACK CONFIG API INTEGRATION (Feature Flag)
  // =============================================================================

  // Feature flag: enable/disable API-driven configuration
  const USE_API_CONFIG = import.meta.env.VITE_USE_PACK_CONFIG_API === "true";

  // Single source of truth for country: prefer explicit prop (e.g. from BioLinkTemplate/Worker) so Chile gets CL and correct $19.99
  const effectiveCountryForApi = countryCode ?? detectedCountry ?? "US";

  // Argentina nunca usa el free-join de Telegram: su CTA va SIEMPRE directo a Stripe
  // (mismo criterio que InvitacionExclusivaArgentina). El toggle de free-join es global,
  // así que sin este gate un visitante AR en /canal o /channel recibiría el Telegram gratis.
  const isArgentinaCountry =
    (effectiveCountryForApi ?? "").trim().toUpperCase().slice(0, 2) === "AR";

  // Get pack from API if feature flag is enabled and service is provided
  // IMPORTANT: Always call hooks (React rules), use result conditionally
  const pack: PackConfig | null =
    USE_API_CONFIG && service ? (packs[service] as PackConfig) || null : null;

  // Resolve configuration from API with fallbacks
  // This always runs (React rules), but uses pack=null when flag is off → uses only fallbacks
  const apiVideoConfig = usePackConfigForVideo(
    pack,
    effectiveCountryForApi,
    getUserLanguage(),
    {
      // Fallback 1: Static configuration (telegramGroupsVideo)
      playbackIdFallback: (cc) => getTelegramPlaybackIdByCountry(cc),
      playbackIdHardcoded:
        getMuxCtaUniversalPlaybackId() ||
        (import.meta.env.VITE_TELEGRAM_GROUPS_FALLBACK_PLAYBACK_ID as string),
      ctaUrlFallback: (cc) => getTelegramCTAUrlByCountry(cc),
      ctaLabelFallback: (lang) => (lang === "es" ? "Unirte" : "Join"),
    },
  );

  // =============================================================================
  // RESOLUTION PRIORITY (with API integration)
  // =============================================================================

  // Priority: manual props > API config (always for services) > country-specific > static-page config > service config > empty
  // When a non-empty playbackId is passed (e.g. /sorpresa, /exclusive), it is always used and nothing can override it.
  // If forceManualPlaybackId=true, ONLY use manual playbackId (no fallbacks from API/country/service)
  const playbackId =
    manualPlaybackId && manualPlaybackId.trim()
      ? manualPlaybackId.trim()
      : forceManualPlaybackId
        ? ""
        : (service ? apiVideoConfig.playbackId : null) ||
          countrySpecificPlaybackId ||
          pageConfig?.playbackId ||
          serviceConfig?.playbackId ||
          // Última red de seguridad: nunca renderizar un player Mux vacío en las
          // superficies config-driven (service / país / página). Pasa cuando el
          // Worker manda geo UNKNOWN, el admin no cargó override por país, o el env
          // VIP no está definido. Antes esto caía a "" → estado "config error" sin
          // video; el HARDCODED_FALLBACK (BRAND_MUX_DEFAULT_PLAYBACK_ID) sólo se
          // alcanzaba por el path de error de reproducción, no en la resolución
          // inicial. No aplica a páginas standalone (manual) ni a
          // forceManualPlaybackId, que quieren su propio video o ninguno.
          HARDCODED_FALLBACK_PLAYBACK_ID;
  let ctaUrl =
    manualCtaUrl ||
    (service ? apiVideoConfig.ctaUrl : null) ||
    countrySpecificCtaUrl ||
    pageConfig?.ctaUrl ||
    serviceConfig?.ctaUrl ||
    "";
  let ctaLabel =
    manualCtaLabel ||
    (service ? apiVideoConfig.ctaLabel : null) ||
    pageConfig?.ctaLabel ||
    serviceConfig?.ctaLabel ||
    "Unirte";

  if (!skipWorkerTemplateCta) {
    const workerCta = resolveWorkerDrivenCta();
    if (workerCta.mode === "hidden") {
      ctaUrl = "";
    } else if (workerCta.mode === "worker") {
      ctaUrl = workerCta.url;
      ctaLabel = getWorkerCtaDisplayLabel(detectedLanguage, workerCta.variant);
    }
  }
  const title = manualTitle || pageConfig?.title || serviceConfig?.title;

  const muxFriendlyBanner = useMemo(
    () =>
      getMuxCtaFriendlyBannerMessage(
        detectedLanguage,
        {
          service,
          ctaSubtitle,
          staticPage: resolvedPage,
          premiumSimpleMembershipFreeFunnel,
          // Argentina: sin copy "canal gratis / no es VIP"; su CTA va directo a Stripe.
          freeJoinCTA: freeJoinCTA && !isArgentinaCountry,
        },
        isArgentinaCountry
          ? { friendlyBanner: "off", freeJoinCopy: false }
          : muxFriendlyFunnel,
      ),
    [
      detectedLanguage,
      service,
      ctaSubtitle,
      resolvedPage,
      premiumSimpleMembershipFreeFunnel,
      muxFriendlyFunnel,
      freeJoinCTA,
      isArgentinaCountry,
      freeJoinConfig.enabled,
      freeJoinConfig.resolvedUrl,
      freeJoinConfig.publicConfig,
    ],
  );

  // Log API config usage (development only)
  useEffect(() => {
    if (import.meta.env.DEV && USE_API_CONFIG && service) {
      console.log("[MuxVideoPlayerWithCTA] API Config:", {
        enabled: USE_API_CONFIG,
        service,
        pack: pack?.id || "null",
        country: effectiveCountryForApi,
        isActive: apiVideoConfig.isActive,
        playbackId: apiVideoConfig.playbackId?.substring(0, 20) + "...",
        ctaUrl: apiVideoConfig.ctaUrl,
        ctaLabel: apiVideoConfig.ctaLabel,
        pricing: `${apiVideoConfig.priceFormatted} (${apiVideoConfig.currency})`,
      });
    }
  }, [USE_API_CONFIG, service, pack, effectiveCountryForApi, apiVideoConfig]);

  // Single source of truth for country in custom checkout (pricing + API body fallback + tracking)
  // Note: effectiveCountryForApi is defined above for API resolution
  const effectiveCountry = effectiveCountryForApi;

  const stripeWatcherLuckEligible = useMemo(() => {
    if (getWatcherLuckDiscountMaxPercentClient() <= 0) return false;
    if (!STRIPE_PUBLISHABLE_KEY) return false;
    if (!(service === "channel_invite" || Boolean(overridePackId)))
      return false;
    // AR ahora también es elegible: el checkout pasa por Stripe (USD) igual que el resto.
    return true;
  }, [service, overridePackId]);

  const stripeWatcherLuckPercent = useMemo(() => {
    if (!stripeWatcherLuckEligible) return 0;
    return getOrCreateSessionWatcherLuckPercent();
  }, [stripeWatcherLuckEligible]);

  // Check if content is active: only false when API explicitly returns isActive: false for this country.
  // When API has no entry for the country or fails, fallback config is used and content stays active.
  const isContentActive =
    USE_API_CONFIG && service ? apiVideoConfig.isActive : true;

  // Use fallback English playback ID when connection/CDN error occurred (PE, ES, etc.)
  const effectivePlaybackId = networkErrorFallbackPlaybackId || playbackId;

  // Validate playbackId - must be non-empty and valid format
  const isValidPlaybackId =
    effectivePlaybackId &&
    effectivePlaybackId.trim().length > 0 &&
    effectivePlaybackId !== SAFE_PLAYBACK_ID_FALLBACK &&
    effectivePlaybackId.length >= 10; // Mux playback IDs are typically 20+ chars

  // Log playbackId validation on mount (only errors in production)
  useEffect(() => {
    // Only log in development or if there's an error
    if (import.meta.env.DEV) {
      console.log("[MuxVideoPlayerWithCTA] Component initialized:", {
        country: effectiveCountry,
        service,
        playbackId: effectivePlaybackId?.substring(0, 20) + "...",
        isValid: isValidPlaybackId,
        source: manualPlaybackId
          ? "manual"
          : serviceConfig
            ? "service"
            : pageConfig
              ? "page"
              : "none",
      });
    }

    if (!isValidPlaybackId) {
      const errorMsg = !effectivePlaybackId
        ? "PlaybackId is empty - check env vars in Railway"
        : `Invalid playbackId: "${effectivePlaybackId}" (length: ${effectivePlaybackId.length})`;

      console.error(`[MuxVideoPlayerWithCTA] ❌ ${errorMsg}`);

      logVideoEvent({
        event: "playback_id_invalid",
        country: effectiveCountry,
        service,
        playbackId: effectivePlaybackId || "EMPTY",
        errorMessage: errorMsg,
      });
    }
  }, [
    isValidPlaybackId,
    effectivePlaybackId,
    effectiveCountry,
    service,
    manualPlaybackId,
    serviceConfig,
    pageConfig,
  ]);
  const getFallbackPlaybackIdForErrors = useCallback((): string | null => {
    // Páginas standalone (/sorpresa, /exclusive): solo usan su playbackId; nunca sustituir por otro video
    if (manualPlaybackId && manualPlaybackId.trim() && !service) {
      return null;
    }
    const countryConfig =
      playbackIdByCountry || serviceConfig?.playbackIdByCountry;

    // Priority: default country video -> US video -> base service video -> EN fallback -> hardcoded fallback
    // This keeps video available when a country-specific playback ID is invalid in Mux.
    // HARDCODED_FALLBACK_PLAYBACK_ID (local) is guaranteed valid (not from env vars).
    const candidates = [
      countryConfig?.default,
      countryConfig?.US,
      serviceConfig?.playbackId,
      getMuxCtaUniversalPlaybackId(),
      TELEGRAM_GROUPS_EN_PLAYBACK_ID,
      HARDCODED_FALLBACK_PLAYBACK_ID, // Last resort - guaranteed valid
    ];

    for (const candidate of candidates) {
      if (!candidate || candidate.trim() === "") continue;
      if (candidate === effectivePlaybackId) continue;
      return candidate;
    }

    return null;
  }, [
    manualPlaybackId,
    service,
    playbackIdByCountry,
    serviceConfig?.playbackIdByCountry,
    serviceConfig?.playbackId,
    effectivePlaybackId,
  ]);

  // Reset fallback only when country explicitly changes (e.g. test_country) - not on initial detection
  const prevCountryRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevCountryRef.current;
    prevCountryRef.current = effectiveCountry;
    if (prev !== undefined && prev !== effectiveCountry) {
      setNetworkErrorFallbackPlaybackId(null);
      hasTriedFallbackRef.current = false;
    }
  }, [effectiveCountry]);

  // Early validation: if no playbackId (and no Spain fallback), show error
  if (!effectivePlaybackId || effectivePlaybackId.trim() === "") {
    logVideoErrorProduction({
      event: "video_error",
      country: effectiveCountry,
      service: service ?? undefined,
      errorType: "config_missing",
      errorMessage: "No playbackId provided",
    });
    console.error("[MuxVideoPlayerWithCTA] ❌ No playbackId provided", {
      manualPlaybackId,
      service,
      page: pageProp,
      pathname: location.pathname,
      countryCode,
    });
  }

  const showInstagramIcon = pageConfig?.showInstagramIcon ?? false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const playerRef = useRef<any>(null);
  const [isPlaying, setIsPlaying] = useState(false); // Start as false, will be set to true when playing
  // Chrome mobile / Safari / IG: sound-first is blocked — start muted so first paint isn't NotAllowedError.
  const [isMuted, setIsMuted] = useState(() => {
    if (typeof window === "undefined") return true;
    return detectPlaybackBrowserEnv().needsMutedAutoplay;
  });
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true); // Loading state for video
  const [showPlayButton, setShowPlayButton] = useState(false); // Show play button if autoplay fails
  const [showMuteIndicator, setShowMuteIndicator] = useState(false); // Show mute indicator on mobile
  /** After a few seconds, collapse the sound chip to icon-only so the video stays clear. */
  const [muteChipCompact, setMuteChipCompact] = useState(false);
  const [retryCount, setRetryCount] = useState(0); // Track retry attempts
  const MAX_RETRIES = 3; // Maximum number of retry attempts
  const [thumbnailError, setThumbnailError] = useState(false); // Track if thumbnail failed to load
  const [hasPlaybackStarted, setHasPlaybackStarted] = useState(false); // Hide visual loading only after real playback starts
  const hasPlaybackStartedRef = useRef(false);
  const [hasReceivedMuxMetadata, setHasReceivedMuxMetadata] = useState(false);
  /** First decoded frame (`loadeddata`) — better perceived progress than manifest alone (Mux/HTML5). */
  const [muxHasLoadedData, setMuxHasLoadedData] = useState(false);
  /** Enough buffer to start playback (`canplay` / preload segments). */
  const [muxCanPlayBuffered, setMuxCanPlayBuffered] = useState(false);
  const [loadingSeconds, setLoadingSeconds] = useState(0); // Real elapsed loading time
  const [networkTypeHint, setNetworkTypeHint] = useState("unknown");
  const [saveDataEnabled, setSaveDataEnabled] = useState(false);

  // Checkout state management
  const [checkoutState, setCheckoutState] =
    useState<CheckoutState>("VIDEO_PREVIEW");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isCreatingPaymentIntent, setIsCreatingPaymentIntent] = useState(false);
  /** Full-screen “please wait” before hard navigations (router / checkout page / external URL). */
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);

  /** Ingreso gratis custom (sheet multi-paso) en vez de modal Clerk genérico. */
  const [freeJoinSheetOpen, setFreeJoinSheetOpen] = useState(false);

  const checkoutStateRef = useRef(checkoutState);
  checkoutStateRef.current = checkoutState;
  const muxStripeJsUnavailableTrackedRef = useRef(false);

  // Device detection — sync on first paint (Safari/Chrome/IG need correct props before remount)
  const playbackEnv = usePlaybackBrowserEnv();
  const isMobile = playbackEnv.isMobile;
  const isIOS = playbackEnv.isIOS;
  const isAndroid = playbackEnv.isAndroid;
  const needsMutedAutoplay = playbackEnv.needsMutedAutoplay;
  const needsSoftKeepAlive = playbackEnv.needsSoftKeepAlive;
  const preferNativePlayback = playbackEnv.preferNativePlayback;
  const muxDesktopMacMseHardening = useMuxDesktopMacMseHardening();
  const isLandscape = useIsLandscape();
  const isInstagram = playbackEnv.isInstagram || isInstagramBrowser();
  /** MSE endurecido: Chromium en macOS (incl. Arc sin token en UA) + Arc explícito; no Instagram/iOS. */
  const muxArcPlaybackTune =
    muxDesktopMacMseHardening && !isInstagram && !preferNativePlayback;
  /** Menos presión sobre SourceBuffer de audio/vídeo (fallos appendBuffer en Chromium macOS). */
  const fragileMseHlsConfig: Partial<HlsConfig> | undefined = useMemo(
    () =>
      muxArcPlaybackTune
        ? {
            lowLatencyMode: false,
            maxBufferLength: 25,
            maxMaxBufferLength: 50,
            backBufferLength: 60,
            maxBufferHole: 0.5,
            nudgeOffset: 0.05,
            nudgeMaxRetry: 5,
            maxFragLookUpTolerance: 0.25,
            fragLoadingTimeOut: 20000,
          }
        : undefined,
    [muxArcPlaybackTune],
  );
  /**
   * Motor de reproducción.
   *
   * NO cambiar a MSE en Instagram/Android para "arrancar más rápido": se probó
   * la hipótesis contra el replay 019f8489 (SM-A125M, Tucumán, 4G) y quedó
   * refutada. La escalera real del asset teaser es:
   *
   *     1072×1920  3,97 Mbps
   *      714×1280  2,03 Mbps
   *      476×854   1,00 Mbps   ← el player estaba acá
   *      268×480   0,53 Mbps   ← y cayó acá por falta de banda
   *
   * El teléfono es 720×1600 físicos, así que el rung apropiado era 714×1280:
   * el player ya venía reproduciendo POR DEBAJO de la resolución nativa y la
   * red lo hundió más. No arrancó demasiado alto. Forzar `startLevel: 0` (que
   * es lo único que MSE habilitaría acá) clavaría el primer frame en 268×480,
   * visiblemente borroso.
   *
   * El buffering de esa sesión es un problema de ancho de banda del enlace, no
   * de motor: se ataca con una escalera más liviana o un teaser más corto, no
   * desde el cliente. `native` en IG está documentado como comprobado.
   */
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  /** Guards autoplay so each playback cycle gets one controlled startup attempt. */
  const autoplayAttemptKeyRef = useRef<string | null>(null);
  const autoplayRunIdRef = useRef(0);
  /**
   * Immersive conversion reel keep-alive. Default OFF (delfiocampo-stable).
   * Only enabled when `conversionReel` — the always-on path + PostHog triple-capture
   * froze /invitacion-exclusiva and mobile tabs (pause→play thrash).
   */
  const forceKeepPlayingRef = useRef(false);
  /** Only set true for intentional pauses (unmount / playbackId swap). */
  const intentionalPauseRef = useRef(false);
  /**
   * DualCTA uses loop=false — once the teaser ends we must NOT soft-resume
   * (would restart under the end-card and look like a broken player).
   */
  const videoEndedRef = useRef(false);
  /** Generation token — supersedes in-flight resume so we never "win" a stale play(). */
  const resumeGenRef = useRef(0);
  /** Min gap between force-resume attempts (main-thread safety). */
  const lastForceResumeAtRef = useRef(0);
  const hasErrorRef = useRef(false);
  const isMutedRef = useRef(false);
  const parentCtaLockRef = useRef(false);
  const parentCtaUnlockTimerRef = useRef<number | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Instagram browser banner state (dismissible)
  const [showInstagramBanner, setShowInstagramBanner] = useState(false);
  const [instagramBannerDismissed, setInstagramBannerDismissed] =
    useState(false);
  const [linkCopiedForBrowser, setLinkCopiedForBrowser] = useState(false);

  /** Stripe-only fleeting toast: “lucky” bonus discount rolled for this tab session */
  const [showWatcherLuckBanner, setShowWatcherLuckBanner] = useState(false);
  const watcherLuckToastShownRef = useRef(false);
  const watcherLuckToastTimerRef = useRef<number | null>(null);
  const watcherLuckWallTimerRef = useRef<number | null>(null);
  const watcherLuckTUThrottleRef = useRef(0);

  // Show Instagram banner only if video hasn't started after 8s (avoid interrupting successful playback)
  useEffect(() => {
    if (isInstagram && !instagramBannerDismissed) {
      const timer = setTimeout(() => {
        if (!hasPlaybackStartedRef.current) {
          setShowInstagramBanner(true);
        }
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [isInstagram, instagramBannerDismissed]);

  // Apply Instagram browser CSS optimizations
  useEffect(() => {
    if (isInstagram) {
      applyInstagramBrowserCSS();
    }
  }, [isInstagram]);
  useEffect(() => {
    // Re-arm after Strict Mode remount (cleanup sets intentional pause).
    intentionalPauseRef.current = false;
    return () => {
      // Block force-resume against a tearing-down player instance.
      intentionalPauseRef.current = true;
      if (parentCtaUnlockTimerRef.current !== null) {
        clearTimeout(parentCtaUnlockTimerRef.current);
      }
      if (watcherLuckToastTimerRef.current !== null) {
        clearTimeout(watcherLuckToastTimerRef.current);
        watcherLuckToastTimerRef.current = null;
      }
      if (watcherLuckWallTimerRef.current !== null) {
        clearTimeout(watcherLuckWallTimerRef.current);
        watcherLuckWallTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (checkoutState === "VIDEO_PREVIEW") return;
    if (watcherLuckWallTimerRef.current !== null) {
      clearTimeout(watcherLuckWallTimerRef.current);
      watcherLuckWallTimerRef.current = null;
    }
    setShowWatcherLuckBanner(false);
    if (watcherLuckToastTimerRef.current !== null) {
      clearTimeout(watcherLuckToastTimerRef.current);
      watcherLuckToastTimerRef.current = null;
    }
  }, [checkoutState]);
  const safeCssId = sanitizePlaybackIdForCss(effectivePlaybackId ?? "");
  const ctaUrlValid = Boolean(ctaUrl && isAllowedRedirectUrl(ctaUrl));
  // Mostrar botón si hay URL válida O custom checkout (overridePackId + Stripe) para /sorpresa, /exclusive
  const showCTAButton =
    !hideNativeCta &&
    (ctaUrlValid ||
      (Boolean(overridePackId) && Boolean(STRIPE_PUBLISHABLE_KEY)));
  // Free-join CTA: si está activo y hay URL, abrir Telegram gratis en lugar del checkout nativo.
  // Argentina queda excluida: su CTA va directo a Stripe (isArgentinaCountry).
  const isFreeJoinActive = freeJoinConfig.enabled === true && !isArgentinaCountry;
  const freeJoinDestination = isFreeJoinActive ? (freeJoinConfig.resolvedUrl ?? null) : null;
  const effectiveCtaUrl = freeJoinDestination || ctaUrl;
  const effectiveCtaUrlValid = Boolean(effectiveCtaUrl && isAllowedRedirectUrl(effectiveCtaUrl));
  const ctaBusy = isCreatingPaymentIntent || isNavigatingAway;

  // Debug: Log CTA configuration (solo en desarrollo)
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[MuxVideoPlayerWithCTA] CTA Debug:", {
      service,
      ctaUrl,
      effectiveCtaUrl,
      freeJoinDestination,
      freeJoinEnabled: freeJoinConfig.enabled,
      ctaUrlValid,
      effectiveCtaUrlValid,
      ctaLabel,
      playbackId: playbackId?.substring(0, 20) + "...",
      serviceConfig: serviceConfig ? "loaded" : "not loaded",
      stripeKeyConfigured: Boolean(STRIPE_PUBLISHABLE_KEY),
    });
  }, [service, ctaUrl, effectiveCtaUrl, freeJoinDestination, freeJoinConfig.enabled, ctaUrlValid, effectiveCtaUrlValid, ctaLabel, playbackId, serviceConfig]);

  const isModal = variant === "modal";
  const effectiveDisplayMode = isModal ? "contain" : displayMode;
  const isLikelySlowNetwork =
    saveDataEnabled ||
    networkTypeHint === "slow-2g" ||
    networkTypeHint === "2g" ||
    networkTypeHint === "3g";
  const shouldShowSlowConnectionHint = loadingSeconds >= 4;

  const awaitingFirstPlayback =
    !hasPlaybackStarted &&
    !showPlayButton &&
    !isPlaying &&
    !hasError;
  /** Full-screen loader until we have stream metadata *and* a playable buffer (`canplay`). */
  const showHeavyVideoLoader =
    awaitingFirstPlayback &&
    isLoading &&
    (!hasReceivedMuxMetadata || !muxCanPlayBuffered);
  /** Minimal chrome once Mux reports `canplay` but autoplay has not emitted `playing` yet (common on mobile/WebView). */
  const showLightVideoLoader =
    awaitingFirstPlayback &&
    isLoading &&
    hasReceivedMuxMetadata &&
    muxCanPlayBuffered;

  const getDisplayMode = (): "reel" | "contain" | "cover" => {
    if (effectiveDisplayMode === "auto") return "reel";
    return effectiveDisplayMode as "reel" | "contain" | "cover";
  };

  const currentDisplayMode = getDisplayMode();
  const isReelMode = currentDisplayMode === "reel";
  /** Single-video TikTok/Reel CTA funnel — fast, no swipe feed, responsive phone frame. */
  const isConversionReel =
    conversionReel === true
      ? true
      : conversionReel === false
        ? false
        : // Default: do NOT auto-enable (was freezing exclusive/DualCTA tabs).
          // Callers that want keep-alive must pass conversionReel={true} explicitly.
          false;

  // Keep-alive only when explicitly conversion reel (never default-on).
  useEffect(() => {
    forceKeepPlayingRef.current = isConversionReel;
  }, [isConversionReel]);

  /**
   * Dimensiones de Mux Data para el embudo principal.
   *
   * Elegidas para que las preguntas del replay 019f8489 se contesten con un
   * filtro en vez de ingeniería inversa: en qué red estaba (`4g` / `save-data`),
   * si era el WebView de Instagram, qué país y qué variante de página. Mux Data
   * ya trae de fábrica startup time, rebuffer count/duration y cambios de
   * rendition — lo que faltaba era poder segmentarlos.
   */
  const muxDataMetadata = useMemo(
    () => ({
      video_id: effectivePlaybackId,
      video_title: title || "",
      video_stream_type: "on-demand",
      // Regla de creador: etiqueta cada view de Mux Data con el brand
      // (env Mux compartido entre marcas). Habilita scoping por
      // `sub_property_id` en heatmaps/hotspots/exports (ver api/lib/muxData.ts).
      sub_property_id: BRAND_SOURCE_KEY,
      player_name: isConversionReel
        ? "mux-cta-conversion-reel"
        : "mux-cta-player",
      page_type: "watchpage",
      // Sin esto no se puede separar "la red no daba" de "el player eligió mal".
      viewer_connection_type: networkTypeHint,
      custom_1: effectiveCountry || "unknown",
      custom_2: isInstagram
        ? "instagram_webview"
        : isIOS
          ? "ios_safari_webkit"
          : isAndroid
            ? "android_chrome"
            : isMobile
              ? "mobile_browser"
              : playbackEnv.isChrome
                ? "desktop_chrome"
                : playbackEnv.isSafari
                  ? "desktop_safari"
                  : "desktop_browser",
      custom_3: saveDataEnabled ? "save_data_on" : "save_data_off",
    }),
    [
      effectivePlaybackId,
      title,
      isConversionReel,
      networkTypeHint,
      effectiveCountry,
      isInstagram,
      isMobile,
      isIOS,
      isAndroid,
      playbackEnv.isChrome,
      playbackEnv.isSafari,
      saveDataEnabled,
    ],
  );

  /**
   * Mux Player 3.10 `maxResolution` only accepts: 720p | 1080p | 1440p | 2160p.
   * Values like "480p"/"360p" are INVALID and silently fail to cap the ladder
   * (see @mux/playback-core MaxResolution). Use 720p for mobile conversion +
   * `maxAutoResolution` for ABR pricing/auto cap.
   */
  const effectiveMaxResolution = useMemo((): "720p" | "1080p" | "1440p" | "2160p" => {
    if (isConversionReel) {
      // Fast first frame on reels / IG — lowest valid server-side max.
      return "720p";
    }
    if (
      saveDataEnabled ||
      networkTypeHint === "2g" ||
      networkTypeHint === "slow-2g" ||
      isMobile ||
      isInstagram ||
      muxArcPlaybackTune
    ) {
      return "720p";
    }
    return "1080p";
  }, [
    isConversionReel,
    saveDataEnabled,
    networkTypeHint,
    isMobile,
    isInstagram,
    muxArcPlaybackTune,
  ]);

  /** ABR auto-cap (does not strip playlist rungs). Valid: 720p | 1080p | 1440p | 2160p. */
  const effectiveMaxAutoResolution = useMemo((): "720p" | "1080p" | undefined => {
    if (isConversionReel || isMobile || isInstagram || saveDataEnabled) {
      return "720p";
    }
    return undefined;
  }, [isConversionReel, isMobile, isInstagram, saveDataEnabled]);

  const effectivePreload = useMemo((): "auto" | "metadata" | "none" => {
    if (isConversionReel) return "auto";
    if (isMobile || isInstagram || muxArcPlaybackTune) return "metadata";
    return "auto";
  }, [isConversionReel, isMobile, isInstagram, muxArcPlaybackTune]);

  // Conversion reels often run on IG WebView / 3G AR: with only 1 preload
  // segment (and ~5s HLS chunks) almost every session fires `video_stalled`
  // mid-play. Prefetch 2 segments so startup stays fast but the mid-video
  // underrun rate drops for short teasers.
  const effectiveMinPreloadSegments =
    isConversionReel ? 2 : isMobile || isInstagram || muxArcPlaybackTune ? 2 : 1;

  const effectiveCapRendition =
    isConversionReel ||
    Boolean(isMobile || isInstagram || muxArcPlaybackTune);

  const reelObjectFit = isConversionReel || (isReelMode && isMobile) ? "cover" : "contain";

  const [modalMounted, setModalMounted] = useState(!isModal);

  useEffect(() => {
    if (!isModal) return;
    const id = requestAnimationFrame(() => setModalMounted(true));
    return () => cancelAnimationFrame(id);
  }, [isModal]);

  useEffect(() => {
    if (!onClose) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  useEffect(() => {
    if (!onClose) return;
    const id = requestAnimationFrame(() => {
      closeButtonRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [onClose]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      onClose?.();
    },
    [onClose],
  );

  useEffect(() => {
    hasPlaybackStartedRef.current = hasPlaybackStarted;
  }, [hasPlaybackStarted]);

  useEffect(() => {
    hasErrorRef.current = hasError;
  }, [hasError]);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    onMutedChange?.(isMuted);
  }, [isMuted, onMutedChange]);

  useEffect(() => {
    if (showPlayButton) onRequireUserPlay?.();
  }, [showPlayButton, onRequireUserPlay]);

  const muxPlayerMuted = allowSoundAutoplayInWebView
    ? isMuted
    : isInstagram || isMuted;

  useEffect(() => {
    if (effectivePlaybackId) {
      // Only intentional-pause when swapping an *existing* player instance.
      // On first mount `playerRef` is still null — pausing is a no-op — but we must
      // NOT leave intentionalPauseRef=true for 400ms: that blocks keep-alive and can
      // race the first muted autoplay (parent DualCTA stuck on "Cargando").
      const prev = playerRef.current;
      if (prev && typeof prev.pause === "function") {
        intentionalPauseRef.current = true;
        try {
          prev.pause();
        } catch {
          /* ignore */
        }
        window.setTimeout(() => {
          intentionalPauseRef.current = false;
        }, 400);
      } else {
        intentionalPauseRef.current = false;
      }

      setHasError(false);
      hasErrorRef.current = false;
      setErrorMessage("");
      setIsLoading(true); // Reset loading when playback ID changes (including Spain fallback)
      setHasReceivedMuxMetadata(false);
      setMuxHasLoadedData(false);
      setMuxCanPlayBuffered(false);
      setHasPlaybackStarted(false);
      hasPlaybackStartedRef.current = false;
      videoEndedRef.current = false;
      autoplayAttemptKeyRef.current = null;
      autoplayRunIdRef.current += 1;
      resumeGenRef.current += 1;
      setShowPlayButton(false);
      setLoadingSeconds(0);
      setRetryCount(0);
      setThumbnailError(false);

      // NOTE: We intentionally do NOT preload the .m3u8 manifest here.
      // A <link rel="preload" as="fetch" crossOrigin="anonymous"> for the HLS manifest
      // is NEVER reused by Mux's hls.js player (different XHR CORS mode), so it:
      //  1. Triggers Chrome's "preloaded but not used" warning every time
      //  2. Can cause a CORS cache-poisoning that makes the player emit a mediaError
      // The Mux player handles its own manifest fetch efficiently; no preload needed.

      // Keep loading state long enough to avoid exposing a static/non-playing frame too early.
      // It will be hidden earlier by real playback events (canplay/play).
      const maxLoadingTime = isConversionReel
        ? isMobile || isInstagram
          ? 4000
          : 5000
        : isMobile || isInstagram
          ? 6000
          : 8000;
      const maxLoadingTimeout = setTimeout(() => {
        if (!hasPlaybackStartedRef.current) {
          setIsLoading(false);
          setShowPlayButton(true);
          logVideoEvent({
            event: "video_shown_thumbnail_only",
            country: effectiveCountry,
            service,
            playbackId: effectivePlaybackId,
          });
        }
      }, maxLoadingTime);

      return () => {
        clearTimeout(maxLoadingTimeout);
      };
    }
  }, [effectivePlaybackId, isMobile, isInstagram, isConversionReel, effectiveCountry, service]);

  // Network quality hint for user-facing loading messages (best effort)
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const nav = navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    };
    const connection = nav.connection;
    if (!connection) return;
    if (connection.effectiveType) setNetworkTypeHint(connection.effectiveType);
    setSaveDataEnabled(Boolean(connection.saveData));
  }, []);

  // Track real loading duration so user sees progress, not a static wait (desde entrada hasta listo / play)
  useEffect(() => {
    const inInitialMuxLoad = awaitingFirstPlayback && isLoading;
    if (!inInitialMuxLoad || hasPlaybackStarted) {
      setLoadingSeconds(0);
      return;
    }
    const startedAt = Date.now();
    setLoadingSeconds(0);
    const timer = window.setInterval(() => {
      setLoadingSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [
    awaitingFirstPlayback,
    isLoading,
    hasPlaybackStarted,
    effectivePlaybackId,
  ]);

  const handleError = useCallback(
    (
      error?:
        | {
            message?: string;
            type?: string;
            details?: string;
            url?: string;
            fatal?: boolean;
          }
        | Event,
    ) => {
      try {
        let msg = "";
        let type = "";
        let details = "";
        let url: string | undefined;
        let fatal = false;
        if (error && typeof error === "object" && "message" in error) {
          msg = String((error as { message?: string }).message ?? "");
          type = String((error as { type?: string }).type ?? "");
          details = String((error as { details?: string }).details ?? "");
          url = (error as { url?: string }).url;
          fatal = Boolean((error as { fatal?: boolean }).fatal);
        }

        const lowerMsg = msg.toLowerCase();
        const lowerDetails = details.toLowerCase();
        const isBufferAppendMux =
          lowerMsg.includes("bufferappend") ||
          lowerDetails.includes("bufferappend") ||
          lowerMsg.includes("sourcebuffer") ||
          lowerMsg.includes("appendbuffer") ||
          lowerMsg.includes("removed from the parent media source") ||
          lowerMsg.includes("invalidstateerror");

        // MEDIA_ERR_SRC_NOT_SUPPORTED (Code 4) / "no supported sources" — load() no ayuda.
        const isNoSourceMux =
          lowerDetails.includes("code 4") ||
          lowerMsg.includes("no supported sources");

        // Ignore errors that are not related to Mux video
        // Check if error is from a blocked resource (like ad blockers blocking trackers)
        if (typeof url === "string") {
          // Ignore errors from non-Mux domains (like metricool, analytics, etc.)
          if (!url.includes("mux.com") && !url.includes("stream.mux.com")) {
            console.debug(
              "[MuxVideoPlayerWithCTA] Ignoring non-Mux error:",
              url,
            );
            return;
          }
        }

        // Only handle errors that are clearly video-related
        const isVideoError =
          msg.includes("does not exist") ||
          msg.includes("404") ||
          msg.toLowerCase().includes("video") ||
          msg.toLowerCase().includes("playback") ||
          msg.toLowerCase().includes("stream") ||
          msg.toLowerCase().includes("mux") ||
          msg.toLowerCase().includes("manifest") ||
          msg.toLowerCase().includes("network") ||
          msg.toLowerCase().includes("tunnel") ||
          msg.toLowerCase().includes("token") ||
          type === "mediaError" ||
          type === "networkError" ||
          details === "manifestLoadError" ||
          (typeof url === "string" &&
            (url.includes("stream.mux.com") || url.includes("mux.com")));

        // channel_invite = telegram groups, principal. NUNCA bloquear CTA.
        const isJoinFlowService = [
          "telegram",
          "telegram_groups",
          "channel_invite",
        ].includes(service ?? "");

        // If it's not a video error, ignore it
        if (!isVideoError) {
          console.debug("[MuxVideoPlayerWithCTA] Ignoring non-video error:", {
            msg,
            type,
            details,
            url,
          });
          return;
        }

        // Check if it's a network error that might be retryable
        const isNetworkError =
          type === "networkError" ||
          details === "manifestLoadError" ||
          msg.toLowerCase().includes("network") ||
          msg.toLowerCase().includes("tunnel") ||
          msg.toLowerCase().includes("connection");

        // MEDIA_ERR_SRC_NOT_SUPPORTED (code 4): no valid source — permanent, never retry.
        // "The element has no supported sources" = wrong/missing playback ID.
        const isNoSourceError =
          details.toLowerCase().startsWith("code 4") ||
          msg.toLowerCase().includes("no supported sources") ||
          msg.toLowerCase().includes("src_not_supported");

        // Decode/parse errors (e.g. demuxer_error_could_not_parse, MEDIA_ERR_DECODE)
        // Often transient MSE/CDN issues; retrying player.load() commonly recovers.
        const isDecodeError =
          !isNoSourceError && (
            msg.toLowerCase().includes("demuxer") ||
            msg.toLowerCase().includes("could_not_parse") ||
            msg.toLowerCase().includes("decode") ||
            msg.toLowerCase().includes("not supported") ||
            details.toLowerCase().includes("demuxer") ||
            details.toLowerCase().includes("decode") ||
            details.toLowerCase().includes("parse")
          );

        const isInvalid =
          msg.includes("does not exist") ||
          msg.includes("404") ||
          (typeof url === "string" &&
            url.includes("stream.mux.com") &&
            url.includes("404")) ||
          isNoSourceMux;

        // Join flow: priorizar fallback a inglés antes que retries (siempre debe haber video)
        // Si no es join flow y es network/decode error, retry primero
        if (
          !isJoinFlowService &&
          (isNetworkError || isDecodeError) &&
          !isNoSourceError &&
          !isInvalid &&
          retryCount < MAX_RETRIES &&
          !fatal
        ) {
          console.log(
            `[MuxVideoPlayerWithCTA] ${isDecodeError ? "Decode" : "Network"} error detected, retrying (${
              retryCount + 1
            }/${MAX_RETRIES})...`,
          );
          setRetryCount((prev) => prev + 1);
          setIsLoading(true);
          setHasReceivedMuxMetadata(false);
          setMuxHasLoadedData(false);
          setMuxCanPlayBuffered(false);
          setHasError(false);

          // Retry after a delay
          setTimeout(
            () => {
              const player = playerRef.current;
              if (player && typeof player.load === "function") {
                player.load();
              }
            },
            1000 * (retryCount + 1),
          ); // Exponential backoff
          return;
        }

        // Recoverable MSE/HLS errors (p. ej. bufferAppendError): un load() suele bastar.
        // hls.js marca algunos como fatal; igual permitimos reintentos acotados.
        if (
          !isJoinFlowService &&
          type.toLowerCase() === "mediaerror" &&
          !isInvalid &&
          retryCount < MAX_RETRIES &&
          (!fatal || isBufferAppendMux)
        ) {
          console.warn(
            `[MuxVideoPlayerWithCTA] Recoverable mediaError, silent reload (${retryCount + 1}/${MAX_RETRIES}):`,
            msg.slice(0, 120),
          );
          setRetryCount((prev) => prev + 1);
          setIsLoading(true);
          setHasReceivedMuxMetadata(false);
          setMuxHasLoadedData(false);
          setMuxCanPlayBuffered(false);
          setHasError(false);
          setTimeout(() => {
            const player = playerRef.current;
            if (player && typeof player.load === "function") {
              player.load();
            }
          }, 500);
          return;
        }

        // Log producción: error de video detectado
        const normalizedMsg = msg.toLowerCase();
        const normalizedDetails = details.toLowerCase();
        const isTokenError =
          normalizedMsg.includes("token") ||
          normalizedMsg.includes("unauthorized") ||
          normalizedMsg.includes("forbidden") ||
          normalizedMsg.includes("jwt") ||
          normalizedMsg.includes("signature") ||
          normalizedMsg.includes("access denied") ||
          normalizedMsg.includes("401") ||
          normalizedMsg.includes("403") ||
          normalizedDetails.includes("token") ||
          normalizedDetails.includes("unauthorized") ||
          normalizedDetails.includes("forbidden");
        logVideoErrorProduction({
          event: "video_error",
          country: effectiveCountry,
          service: service ?? undefined,
          errorType: type || (isNetworkError ? "network" : "other"),
          errorMessage: msg.slice(0, 200),
          playbackId: effectivePlaybackId,
          isNetworkError,
          isTokenError,
        });

        // Notify parent for Slack/analytics — best-effort, never throw
        try {
          const normalizedMsg = msg.slice(0, 200) || type || "video_error";
          const now = Date.now();
          const key = `${type || "unknown"}|${normalizedMsg}`;
          const previous = onErrorThrottleRef.current;
          const isDuplicateBurst =
            previous.lastKey === key && now - previous.lastAt < 3000;
          if (!isDuplicateBurst) {
            onErrorThrottleRef.current = { lastKey: key, lastAt: now };
            onErrorRef.current?.(normalizedMsg);
          }
        } catch { /* */ }

        // channel_invite (telegram groups): NUNCA bloquear - siempre permitir CTA
        if (isJoinFlowService) {
          if (!hasTriedFallbackRef.current) {
            const englishPlaybackId = getFallbackPlaybackIdForErrors();
            if (englishPlaybackId && englishPlaybackId.trim() !== "") {
              logVideoErrorProduction({
                event: "fallback_attempted",
                country: effectiveCountry,
                service: service ?? undefined,
                errorType: type || (isNetworkError ? "network" : "other"),
                playbackId: effectivePlaybackId,
                fallbackAttempted: true,
              });
              hasTriedFallbackRef.current = true;
              setNetworkErrorFallbackPlaybackId(englishPlaybackId);
            } else {
              logVideoErrorProduction({
                event: "fallback_failed",
                country: effectiveCountry,
                service: service ?? undefined,
                errorMessage: "no fallback playback ID available",
                fallbackAttempted: false,
              });
            }
          } else {
            logVideoErrorProduction({
              event: "fallback_failed",
              country: effectiveCountry,
              service: service ?? undefined,
              errorMessage: "fallback video also failed",
              playbackId: effectivePlaybackId,
              fallbackAttempted: true,
            });
          }
          setIsLoading(false);
          setErrorMessage("");
          setHasError(false);
          return;
        }

        // Para network/token/decode errors (no join flow): intentar fallback si aplica
        if ((isNetworkError || isTokenError || isDecodeError) && !isInvalid) {
          const fallbackPlaybackId = getFallbackPlaybackIdForErrors();
          if (
            fallbackPlaybackId &&
            fallbackPlaybackId.trim() !== "" &&
            !hasTriedFallbackRef.current
          ) {
            logVideoErrorProduction({
              event: "fallback_attempted",
              country: effectiveCountry,
              service: service ?? undefined,
              errorType: isTokenError ? "token" : isDecodeError ? "decode" : "network",
              fallbackAttempted: true,
            });
            hasTriedFallbackRef.current = true;
            setNetworkErrorFallbackPlaybackId(fallbackPlaybackId);
            setIsLoading(false);
            setErrorMessage("");
            return;
          }
          // Sin fallback disponible (caso típico: manualPlaybackId standalone como VIP/sorpresa).
          // No bloquear la UI: el thumbnail y el CTA siguen visibles para no perder conversión.
          logVideoErrorProduction({
            event: "fallback_failed",
            country: effectiveCountry,
            service: service ?? undefined,
            errorType: isTokenError ? "token" : isDecodeError ? "decode" : "network",
            errorMessage: "no fallback playback ID available; keeping CTA visible",
            playbackId: effectivePlaybackId,
            fallbackAttempted: false,
          });
          setIsLoading(false);
          setErrorMessage("");
          setHasError(false);
          return;
        }

        // Solo mostrar error blocking para otros servicios (no join flow)
        setHasError(true);
        setIsLoading(false);
        setErrorMessage(
          isInvalid
            ? "This video is not available. The playback ID may be invalid or the video has been removed."
            : "Video could not be loaded. Please try again later.",
        );
      } catch (err) {
        console.error("[MuxVideoPlayerWithCTA] Error in handleError:", err);
        // Don't set error state for unexpected errors in error handler
        // This prevents cascading errors from breaking the component
      }
    },
    [
      retryCount,
      service,
      effectiveCountry,
      effectivePlaybackId,
      getFallbackPlaybackIdForErrors,
    ],
  );

  /** Mux onError puede ser CustomEvent: `detail` es un objeto con type/details/fatal, no un string. */
  function extractMuxPlayerErrorPayload(
    error: unknown,
  ): {
    message: string;
    type: string;
    details: string;
    url?: string;
    fatal?: boolean;
  } | null {
    if (!error || typeof error !== "object") return null;
    const e = error as Record<string, unknown>;
    if (typeof e.detail === "string" && e.detail.trim() !== "") {
      const s = e.detail.trim();
      return { message: s, type: "mediaError", details: s, fatal: false };
    }
    const raw =
      e.detail && typeof e.detail === "object"
        ? (e.detail as Record<string, unknown>)
        : e;
    const message = String(raw.message ?? "");
    const type = String(raw.type ?? "");
    const details = String(raw.details ?? "");
    const url = typeof raw.url === "string" ? raw.url : undefined;
    const fatal = Boolean(raw.fatal);
    if (!message && !type && !details && !url) return null;
    return { message, type, details, url, fatal };
  }

  // Wrapper for MuxPlayer onError to validate it's a video error
  const handleMuxPlayerError = useCallback(
    (
      error?:
        | {
            message?: string;
            type?: string;
            details?: string;
            url?: string;
            fatal?: boolean;
          }
        | Event,
    ) => {
      if (!error) {
        return;
      }

      const payload = extractMuxPlayerErrorPayload(error);

      let errorStr = "";
      try {
        errorStr = JSON.stringify(error).toLowerCase();
      } catch {
        errorStr = String(error).toLowerCase();
      }

      const combinedStr = payload
        ? `${errorStr} ${payload.message} ${payload.type} ${payload.details} ${payload.url ?? ""}`.toLowerCase()
        : errorStr;

      const hasVideoIndicators =
        combinedStr.includes("video") ||
        combinedStr.includes("playback") ||
        combinedStr.includes("stream") ||
        combinedStr.includes("mux") ||
        combinedStr.includes("manifest") ||
        combinedStr.includes("network") ||
        combinedStr.includes("tunnel") ||
        combinedStr.includes("connection") ||
        combinedStr.includes("mediaerror") ||
        combinedStr.includes("invalid") ||
        combinedStr.includes("not supported") ||
        combinedStr.includes("bufferappend") ||
        (payload?.type === "mediaError" || payload?.type === "networkError");

      if (!hasVideoIndicators) {
        console.debug(
          "[MuxVideoPlayerWithCTA] Ignoring non-video error from MuxPlayer:",
          error,
        );
        return;
      }

      if (payload) {
        handleError(payload);
      } else {
        handleError(error);
      }
    },
    [handleError],
  );

  useEffect(() => {
    if (!effectivePlaybackId) return;

    const onVideoError = (event: Event) => {
      const target = event.target as HTMLVideoElement;
      // Only handle errors from actual video elements
      if (!target || target.tagName !== "VIDEO") {
        return;
      }

      const err = target.error;
      if (err && err.code !== undefined) {
        // Only handle actual video errors (MEDIA_ERR_* codes)
        // MEDIA_ERR_ABORTED = 1, MEDIA_ERR_NETWORK = 2, MEDIA_ERR_DECODE = 3, MEDIA_ERR_SRC_NOT_SUPPORTED = 4
        if (err.code >= 1 && err.code <= 4) {
          const errorDetails = {
            message: err.message || `Video error code ${err.code}`,
            type: "mediaError",
            details: `Code ${err.code}: ${
              err.message || "Unknown video error"
            }`,
            url: target.src || target.currentSrc,
          };

          // Log detailed error info
          logVideoEvent({
            event: err.code === 2 ? "network_error" : "video_load_failed",
            country: effectiveCountry,
            service,
            playbackId: effectivePlaybackId,
            errorType: "mediaError",
            errorMessage: err.message || `Video error code ${err.code}`,
            errorCode: err.code,
            networkState: target.networkState,
            readyState: target.readyState,
            isNetworkError: err.code === 2,
          });

          handleError(errorDetails);
        }
      } else {
        // Only handle network errors if they're from Mux domains
        const src = target.src || target.currentSrc || "";
        if (src.includes("mux.com") || src.includes("stream.mux.com")) {
          // Log network error
          logVideoEvent({
            event: "network_error",
            country: effectiveCountry,
            service,
            playbackId: effectivePlaybackId,
            errorType: "networkError",
            errorMessage: "Video could not be loaded",
            networkState: target.networkState,
            readyState: target.readyState,
            isNetworkError: true,
          });

          handleError({
            type: "networkError",
            message: "Video could not be loaded",
            details: "Network or loading error",
            url: src,
          });
        }
      }
    };

    const setup = (): (() => void) | undefined => {
      const p = playerRef.current;
      if (!p) return undefined;
      let el: HTMLVideoElement | null = null;
      if (
        typeof (p as { querySelector?: (s: string) => HTMLElement | null })
          .querySelector === "function"
      ) {
        el = (
          p as { querySelector: (s: string) => HTMLVideoElement | null }
        ).querySelector("video");
      }
      if (!el && (p as { shadowRoot?: ShadowRoot }).shadowRoot) {
        el = (p as { shadowRoot: ShadowRoot }).shadowRoot.querySelector(
          "video",
        );
      }
      if (!el && (p as { media?: HTMLVideoElement }).media) {
        el = (p as { media: HTMLVideoElement }).media;
      }
      if (el) {
        el.addEventListener("error", onVideoError);
        return () => el?.removeEventListener("error", onVideoError);
      }
      return undefined;
    };

    let cleanup2: (() => void) | undefined;
    const cleanup1 = setup();
    const t = setTimeout(() => {
      cleanup2 = setup();
    }, 500);
    return () => {
      cleanup1?.();
      cleanup2?.();
      clearTimeout(t);
    };
  }, [effectivePlaybackId, handleError, effectiveCountry, service]);

  // Helper function to get video element from player
  const getVideoElement = useCallback(
    (
      player: {
        querySelector?: (s: string) => HTMLElement | null;
        shadowRoot?: ShadowRoot;
        media?: HTMLVideoElement;
      } | null,
    ): HTMLVideoElement | null => {
      if (!player) return null;

      // Try different methods to get the video element
      if (typeof player.querySelector === "function") {
        const el = player.querySelector("video") as HTMLVideoElement | null;
        if (el) return el;
      }

      if (player.shadowRoot) {
        const el = player.shadowRoot.querySelector("video");
        if (el) return el;
      }

      if (player.media) {
        return player.media;
      }

      return null;
    },
    [],
  );

  /**
   * Force the video back to playing. Used after pause/stall/visibility loss.
   * Only runs once playback has already started (first start is managed autoplay).
   *
   * Robust path (replay 019fb3e4 IG iOS):
   * 1. Harden native video (strip controls)
   * 2. Prefer `HTMLVideoElement.play()` over mux-player.play() (iOS reliability)
   * 3. Generation token so superseding resumes don't get stuck on AbortError
   * 4. Fall back to muted play if the browser blocks sound after unmute
   */
  const resumeForcedPlayback = useCallback(
    async (reason: string) => {
      if (intentionalPauseRef.current) return;
      if (hasErrorRef.current) return;
      // First start is owned by the controlled autoplay path (or manual play button).
      if (!hasPlaybackStartedRef.current) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      // Soft resume (stall/canplay/visibility): always after first frame — DualCTA has
      // conversionReel=false but Chrome/Safari/IG still pause on toolbar / app switch.
      // Aggressive thrash (pause/native_pause/watchdog): conversionReel OR soft keep-alive envs.
      const soft = isSoftPlaybackResumeReason(reason);
      if (!forceKeepPlayingRef.current && !soft && !needsSoftKeepAlive) return;
      // Teaser finished (DualCTA loop=false) — never auto-restart under end-card.
      if (videoEndedRef.current && !loop) return;

      // Hard throttle — pause/watchdog can fire dozens of times/sec and freeze tabs.
      const now = Date.now();
      if (now - lastForceResumeAtRef.current < 450) return;
      lastForceResumeAtRef.current = now;

      const player = playerRef.current as
        | {
            play?: () => Promise<void>;
            querySelector?: (s: string) => HTMLElement | null;
            shadowRoot?: ShadowRoot;
            media?: HTMLVideoElement;
          }
        | null;
      if (!player) return;

      const videoElement = getVideoElement(player);
      hardenNativeVideoElement(videoElement);

      if (videoElement) {
        // Natural end without loop — leave paused (parent shows end CTA card).
        if (videoElement.ended && !loop) {
          videoEndedRef.current = true;
          setIsPlaying(false);
          return;
        }
        // Near-end pause (some WebViews fire pause before ended) — don't restart.
        const dur = Number(videoElement.duration);
        const ct = Number(videoElement.currentTime);
        if (
          !loop &&
          Number.isFinite(dur) &&
          dur > 0 &&
          Number.isFinite(ct) &&
          ct >= dur - 0.25
        ) {
          videoEndedRef.current = true;
          setIsPlaying(false);
          return;
        }
        if (!videoElement.paused && !videoElement.ended) {
          setIsPlaying(true);
          setShowPlayButton(false);
          return;
        }
        if (videoElement.ended && loop) {
          try {
            videoElement.currentTime = 0;
            videoEndedRef.current = false;
          } catch {
            /* ignore seek failures */
          }
        }
      } else if (typeof player.play !== "function") {
        return;
      }

      // Analytics dropped for force_resume_attempt (high-freq); keep console-free.

      const gen = ++resumeGenRef.current;

      const playNativeOrPlayer = async (): Promise<void> => {
        if (videoElement && typeof videoElement.play === "function") {
          await videoElement.play();
          return;
        }
        if (typeof player.play === "function") {
          await player.play();
          return;
        }
        throw new Error("no_play_method");
      };

      const markPlaying = (_resumeReason: string) => {
        if (gen !== resumeGenRef.current) return;
        setShowPlayButton(false);
        setIsPlaying(true);
        setIsLoading(false);
        // No PostHog on success (high-freq); delfi pattern = quiet resume.
      };

      try {
        // Preserve user mute preference when possible.
        if (videoElement) {
          videoElement.muted = isMutedRef.current;
        }
        await playNativeOrPlayer();
        markPlaying(reason);
      } catch (error) {
        if (gen !== resumeGenRef.current) return;
        const errorName =
          typeof error === "object" && error !== null
            ? String((error as { name?: unknown }).name ?? "")
            : "";
        const errorMessage =
          error instanceof Error ? error.message : String(error);

        // Concurrent play() races — a newer resume owns the outcome.
        if (errorName === "AbortError") {
          // If still paused after a beat, newest gen will retry via pause/watchdog.
          return;
        }

        // After unmute, iOS/IG can reject play(); keep immersive by falling back to muted.
        if (
          errorName === "NotAllowedError" ||
          errorName === "NotSupportedError" ||
          errorName === "Error"
        ) {
          try {
            if (videoElement) {
              videoElement.muted = true;
            }
            setIsMuted(true);
            isMutedRef.current = true;
            await playNativeOrPlayer();
            markPlaying(`${reason}_muted_fallback`);
            setShowMuteIndicator(true);
          } catch (retryError) {
            logVideoEvent({
              event: "force_resume_failed",
              country: effectiveCountry,
              service,
              playbackId: effectivePlaybackId,
              reason: `${reason}_muted_fallback`,
              errorType: errorName,
              errorMessage:
                retryError instanceof Error
                  ? retryError.message
                  : String(retryError),
              isInstagram,
              isMobile,
            });
            console.debug(
              `[MuxVideoPlayerWithCTA] force resume muted (${reason}) failed:`,
              retryError,
            );
          }
        } else {
          logVideoEvent({
            event: "force_resume_failed",
            country: effectiveCountry,
            service,
            playbackId: effectivePlaybackId,
            reason,
            errorType: errorName,
            errorMessage,
            isInstagram,
            isMobile,
          });
          console.debug(
            `[MuxVideoPlayerWithCTA] force resume (${reason}) failed:`,
            error,
          );
        }
      }
    },
    [
      getVideoElement,
      loop,
      effectiveCountry,
      service,
      effectivePlaybackId,
      isInstagram,
      isMobile,
      needsSoftKeepAlive,
    ],
  );

  const revealWatcherLuckToast = useCallback(() => {
    if (
      !stripeWatcherLuckEligible ||
      stripeWatcherLuckPercent <= 0 ||
      watcherLuckToastShownRef.current
    ) {
      return;
    }
    if (checkoutStateRef.current !== "VIDEO_PREVIEW") return;

    watcherLuckToastShownRef.current = true;

    if (watcherLuckWallTimerRef.current !== null) {
      window.clearTimeout(watcherLuckWallTimerRef.current);
      watcherLuckWallTimerRef.current = null;
    }

    setShowWatcherLuckBanner(true);
    if (watcherLuckToastTimerRef.current !== null) {
      window.clearTimeout(watcherLuckToastTimerRef.current);
    }
    watcherLuckToastTimerRef.current = window.setTimeout(() => {
      setShowWatcherLuckBanner(false);
      watcherLuckToastTimerRef.current = null;
    }, WATCHER_LUCK_TOAST_MS);
  }, [stripeWatcherLuckEligible, stripeWatcherLuckPercent]);

  const onPlaybackTelemetryRef = useRef(onPlaybackTelemetry);
  useEffect(() => {
    onPlaybackTelemetryRef.current = onPlaybackTelemetry;
  }, [onPlaybackTelemetry]);

  const playbackTelemetryThrottleRef = useRef(0);

  const emitPlaybackTelemetry = useCallback(
    (reason: "timeupdate" | "pause" | "ended") => {
      const cb = onPlaybackTelemetryRef.current;
      if (!cb) return;
      const now = Date.now();
      if (reason === "timeupdate") {
        if (now - playbackTelemetryThrottleRef.current < 4000) return;
        playbackTelemetryThrottleRef.current = now;
      }
      const p = playerRef.current;
      if (!p) return;
      let currentTimeSeconds = 0;
      let durationSeconds: number | null = null;
      const v = getVideoElement(p);
      if (v) {
        const ct = Number(v.currentTime);
        const d = Number(v.duration);
        currentTimeSeconds = Number.isFinite(ct) ? ct : 0;
        durationSeconds = Number.isFinite(d) && d > 0 ? d : null;
      } else {
        const ct = (p as { currentTime?: number }).currentTime;
        const d = (p as { duration?: number }).duration;
        currentTimeSeconds =
          typeof ct === "number" && Number.isFinite(ct) ? ct : 0;
        durationSeconds =
          typeof d === "number" && Number.isFinite(d) && d > 0 ? d : null;
      }
      cb({
        currentTimeSeconds,
        durationSeconds,
        playbackId: effectivePlaybackId,
        reason,
      });
    },
    [getVideoElement, effectivePlaybackId],
  );

  const handleTimeUpdateForTelemetry = useCallback(() => {
    emitPlaybackTelemetry("timeupdate");
    if (
      stripeWatcherLuckEligible &&
      stripeWatcherLuckPercent > 0 &&
      !watcherLuckToastShownRef.current
    ) {
      const now = Date.now();
      if (now - watcherLuckTUThrottleRef.current < 650) return;
      watcherLuckTUThrottleRef.current = now;
      try {
        const p = playerRef.current;
        if (!p) return;
        let ct = 0;
        const v = getVideoElement(p);
        if (v && Number.isFinite(v.currentTime)) {
          ct = v.currentTime;
        } else {
          const raw = (p as { currentTime?: number }).currentTime;
          ct = typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
        }
        if (ct >= WATCHER_LUCK_MIN_PLAYHEAD_SEC) {
          revealWatcherLuckToast();
        }
      } catch {
        /* ignore */
      }
    }
  }, [
    emitPlaybackTelemetry,
    stripeWatcherLuckEligible,
    stripeWatcherLuckPercent,
    getVideoElement,
    revealWatcherLuckToast,
  ]);

  const handleCanPlay = useCallback(() => {
    setMuxCanPlayBuffered(true);
    console.log("[MuxVideoPlayerWithCTA] Video can play - setting volume");

    // Log successful video load
    logVideoEvent({
      event: "video_load_success",
      country: effectiveCountry,
      service,
      playbackId: effectivePlaybackId,
      isInstagram,
      isMobile,
    });
    logVideoEvent({
      event: "canplay_received",
      country: effectiveCountry,
      service,
      playbackId: effectivePlaybackId,
      hasPlaybackStarted: hasPlaybackStartedRef.current,
      isInstagram,
      isMobile,
    });

    // Set volume when video can play (before it starts)
    try {
      const player = playerRef.current;
      if (player) {
        const videoElement = getVideoElement(player);
        hardenNativeVideoElement(videoElement);
        if (videoElement && "volume" in videoElement && videoElement.paused) {
          videoElement.volume = 1.0;
        }
        // Replay 019fb326 (Android IG CO): after stall/buffering, `canplay` fires
        // again but play may have stopped — re-arm keep-playing without chrome.
        if (
          hasPlaybackStartedRef.current &&
          videoElement &&
          videoElement.paused &&
          !videoElement.ended
        ) {
          void resumeForcedPlayback("canplay_after_buffer");
        }
        // No cerramos el loading aquí: el overlay sigue hasta `play` (o botón manual / timeout).
      }
    } catch (error) {
      console.debug("[MuxVideoPlayerWithCTA] Error in canplay:", error);
    }
  }, [
    getVideoElement,
    effectiveCountry,
    service,
    effectivePlaybackId,
    resumeForcedPlayback,
    isInstagram,
    isMobile,
  ]);

  /** Stall / rebuffer recovery — Android IG often stalls without a lasting `pause` event. */
  const handleStalledOrWaiting = useCallback(
    (kind: "stalled" | "buffering") => {
      logVideoEvent({
        event: kind === "stalled" ? "video_stalled" : "video_buffering",
        country: effectiveCountry,
        service,
        playbackId: effectivePlaybackId,
        hasPlaybackStarted: hasPlaybackStartedRef.current,
        isInstagram,
        isMobile,
      });
      if (!hasPlaybackStartedRef.current) return;
      window.setTimeout(() => {
        void resumeForcedPlayback(`${kind}_recover`);
      }, 200);
      window.setTimeout(() => {
        void resumeForcedPlayback(`${kind}_recover_retry`);
      }, 900);
    },
    [
      effectiveCountry,
      service,
      effectivePlaybackId,
      resumeForcedPlayback,
      isInstagram,
      isMobile,
    ],
  );

  /**
   * Mark first real playback for BOTH internal state and parent (DualCTA).
   * Managed autoplay calls `video.play()` directly; if we only waited for the
   * Mux `onPlay` event, DualCTA's overlay stayed on "Cargando" forever when the
   * event was delayed/lost after a successful play() promise.
   */
  const markPlaybackStarted = useCallback(
    (reason = "unknown") => {
      const isFirst = !hasPlaybackStartedRef.current;
      hasPlaybackStartedRef.current = true;
      videoEndedRef.current = false;
      setHasPlaybackStarted(true);
      setIsPlaying(true);
      setIsLoading(false);
      setShowPlayButton(false);

      if (!isFirst) return;

      logVideoEvent({
        event: "video_play_success",
        country: effectiveCountry,
        service,
        playbackId: effectivePlaybackId,
        reason,
        isInstagram,
        isMobile,
      });
      logVideoEvent({
        event: "mark_playback_started",
        country: effectiveCountry,
        service,
        playbackId: effectivePlaybackId,
        reason,
        isInstagram,
        isMobile,
      });
      console.log("[MuxVideoPlayerWithCTA] Video is playing");
      onPlay?.();
    },
    [
      effectiveCountry,
      service,
      effectivePlaybackId,
      onPlay,
      isInstagram,
      isMobile,
    ],
  );

  const handlePlay = useCallback(() => {
    markPlaybackStarted("mux_onplay");

    // Set volume when video starts playing (safe - video is already playing)
    try {
      const p = playerRef.current;
      if (p) {
        const videoElement = getVideoElement(p);
        hardenNativeVideoElement(videoElement);
        if (videoElement && "volume" in videoElement) {
          videoElement.volume = 1.0;
        }
      }
    } catch (error) {
      // Non-critical error, just log it
      console.debug(
        "[MuxVideoPlayerWithCTA] Error setting volume on play:",
        error,
      );
    }

    if (
      stripeWatcherLuckEligible &&
      stripeWatcherLuckPercent > 0 &&
      !watcherLuckToastShownRef.current &&
      watcherLuckWallTimerRef.current === null
    ) {
      watcherLuckWallTimerRef.current = window.setTimeout(() => {
        watcherLuckWallTimerRef.current = null;
        revealWatcherLuckToast();
      }, WATCHER_LUCK_FALLBACK_WALL_MS);
    }
  }, [
    markPlaybackStarted,
    getVideoElement,
    stripeWatcherLuckEligible,
    stripeWatcherLuckPercent,
    revealWatcherLuckToast,
  ]);

  const dismissWatcherLuckBanner = useCallback(() => {
    setShowWatcherLuckBanner(false);
    if (watcherLuckToastTimerRef.current !== null) {
      clearTimeout(watcherLuckToastTimerRef.current);
      watcherLuckToastTimerRef.current = null;
    }
    if (watcherLuckWallTimerRef.current !== null) {
      clearTimeout(watcherLuckWallTimerRef.current);
      watcherLuckWallTimerRef.current = null;
    }
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    if (watcherLuckWallTimerRef.current !== null) {
      window.clearTimeout(watcherLuckWallTimerRef.current);
      watcherLuckWallTimerRef.current = null;
    }
    emitPlaybackTelemetry("pause");
    // conversionReel: never stay paused. Soft keep-alive (Chrome/Safari/IG): recover
    // accidental WebView pauses without DualCTA freeze thrash.
    if (intentionalPauseRef.current) return;
    if (videoEndedRef.current && !loop) return;
    // Don't restart a finished teaser (pause often fires right before/after ended).
    try {
      const v = getVideoElement(playerRef.current);
      if (v?.ended && !loop) {
        videoEndedRef.current = true;
        return;
      }
      const dur = Number(v?.duration);
      const ct = Number(v?.currentTime);
      if (
        !loop &&
        v &&
        Number.isFinite(dur) &&
        dur > 0 &&
        Number.isFinite(ct) &&
        ct >= dur - 0.25
      ) {
        videoEndedRef.current = true;
        return;
      }
    } catch {
      /* ignore */
    }
    if (forceKeepPlayingRef.current || needsSoftKeepAlive) {
      requestAnimationFrame(() => {
        void resumeForcedPlayback("pause_event");
      });
      window.setTimeout(() => {
        void resumeForcedPlayback("pause_event_retry");
      }, 280);
    }
  }, [
    emitPlaybackTelemetry,
    resumeForcedPlayback,
    needsSoftKeepAlive,
    loop,
    getVideoElement,
  ]);

  // Manual play under user gesture — native <video>.play() first (iOS Safari reliability).
  const handleManualPlay = useCallback(() => {
    try {
      const player = playerRef.current;
      if (!player) return;

      const videoElement = getVideoElement(player);
      hardenNativeVideoElement(videoElement);
      if (videoElement) {
        try {
          videoElement.playsInline = true;
          videoElement.setAttribute("playsinline", "");
          videoElement.setAttribute("webkit-playsinline", "");
        } catch {
          /* ignore */
        }
        videoElement.muted = false;
        if ("volume" in videoElement) {
          videoElement.volume = 1.0;
        }
        setIsMuted(false);
        isMutedRef.current = false;
      }

      const playNativeOrPlayer = async () => {
        if (videoElement && typeof videoElement.play === "function") {
          await videoElement.play();
          return;
        }
        if (typeof player.play === "function") {
          await player.play();
          return;
        }
        throw new Error("no_play_method");
      };

      void playNativeOrPlayer()
        .then(() => {
          setIsPlaying(true);
          setShowPlayButton(false);
          setShowMuteIndicator(false);
          setIsLoading(false);
          hasPlaybackStartedRef.current = true;
          markPlaybackStarted("manual_play_sound");
        })
        .catch((error) => {
          console.warn("[MuxVideoPlayerWithCTA] Manual play failed:", error);
          if (!videoElement) return;
          videoElement.muted = true;
          setIsMuted(true);
          isMutedRef.current = true;
          void playNativeOrPlayer()
            .then(() => {
              setIsPlaying(true);
              setShowPlayButton(false);
              setShowMuteIndicator(true);
              setIsLoading(false);
              hasPlaybackStartedRef.current = true;
              markPlaybackStarted("manual_play_muted");
            })
            .catch(() => {
              console.error(
                "[MuxVideoPlayerWithCTA] Muted manual play also failed",
              );
            });
        });
    } catch (error) {
      console.error(
        "[MuxVideoPlayerWithCTA] Error in handleManualPlay:",
        error,
      );
    }
  }, [getVideoElement, markPlaybackStarted]);

  const handleEnded = useCallback(() => {
    videoEndedRef.current = true;
    setIsPlaying(false);
    emitPlaybackTelemetry("ended");
    onEnded?.();
    // With loop=true Mux should restart; if it hiccups, force keep-playing.
    // DualCTA (loop=false): stop soft-resume — parent end-card owns the UX.
    if (forceKeepPlayingRef.current && loop) {
      videoEndedRef.current = false;
      window.setTimeout(() => {
        void resumeForcedPlayback("ended_loop");
      }, 50);
    }
  }, [onEnded, emitPlaybackTelemetry, loop, resumeForcedPlayback]);

  /**
   * Force unmuted + volume 1 under a user gesture (autoplay policy).
   * Does NOT depend on conversionReel keep-alive — exclusive/DualCTA must
   * keep playing with sound after the first tap even when forceKeepPlaying=false.
   */
  const unlockSoundFromUserGesture = useCallback(
    (reason = "user_gesture") => {
      try {
        const player = playerRef.current;
        if (!player) return false;
        const videoElement = getVideoElement(player);
        if (!videoElement) return false;

        hardenNativeVideoElement(videoElement);
        videoElement.muted = false;
        if ("volume" in videoElement) videoElement.volume = 1.0;
        setIsMuted(false);
        isMutedRef.current = false;
        setShowMuteIndicator(false);
        setIsPlaying(true);
        setShowPlayButton(false);
        hasPlaybackStartedRef.current = true;

        const playNative = async () => {
          try {
            if (typeof videoElement.play === "function") {
              await videoElement.play();
              return;
            }
          } catch {
            /* fall through */
          }
          try {
            if (typeof player.play === "function") {
              await player.play();
            }
          } catch (err) {
            console.debug(
              `[MuxVideoPlayerWithCTA] unlock play failed (${reason}):`,
              err,
            );
          }
        };
        void playNative();

        console.log(`[MuxVideoPlayerWithCTA] Sound unlocked (${reason})`);
        return true;
      } catch (error) {
        console.error(
          "[MuxVideoPlayerWithCTA] unlockSoundFromUserGesture failed:",
          error,
        );
        return false;
      }
    },
    [getVideoElement],
  );

  // Toggle mute/unmute - Instagram-like tap to unmute on mobile
  const toggleMute = useCallback(() => {
    try {
      const player = playerRef.current;
      if (!player) return;

      const videoElement = getVideoElement(player);
      if (videoElement) {
        // Prefer one-shot unlock path when currently muted (conversion UX).
        if (isMuted || videoElement.muted) {
          unlockSoundFromUserGesture("toggle_mute_chip");
          return;
        }

        hardenNativeVideoElement(videoElement);
        videoElement.muted = true;
        setIsMuted(true);
        isMutedRef.current = true;
        setShowMuteIndicator(true);

        console.log("[MuxVideoPlayerWithCTA] Muted video");
      }
    } catch (error) {
      console.error("[MuxVideoPlayerWithCTA] Error in toggleMute:", error);
    }
  }, [isMuted, getVideoElement, unlockSoundFromUserGesture]);

  const togglePlayPause = useCallback(() => {
    try {
      const player = playerRef.current;
      if (!player) return;
      if (typeof player.play !== "function") return;

      // Immersive mode: taps never pause — only resume (or unmute via toggleMute).
      if (forceKeepPlayingRef.current) {
        void resumeForcedPlayback("toggle_play");
        if (!hasPlaybackStartedRef.current && typeof player.play === "function") {
          player.play().catch((error: unknown) => {
            console.debug(
              "[MuxVideoPlayerWithCTA] Play failed in toggle:",
              error,
            );
          });
        }
        return;
      }

      if (typeof player.pause !== "function") return;
      if (isPlaying) {
        intentionalPauseRef.current = true;
        player.pause();
        setIsPlaying(false);
        intentionalPauseRef.current = false;
      } else {
        player.play().catch((error: unknown) => {
          console.debug(
            "[MuxVideoPlayerWithCTA] Play failed in toggle:",
            error,
          );
          setIsPlaying(false);
        });
        setIsPlaying(true);
      }
    } catch (error) {
      console.error("[MuxVideoPlayerWithCTA] Error in togglePlayPause:", error);
    }
  }, [isPlaying, resumeForcedPlayback]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  }, []);

  /** Desktop / any pointer: first interaction unlocks audio if still muted. */
  const handlePointerDownUnlockSound = useCallback(
    (e: React.PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target?.closest(".action-button-container") ||
        target?.closest(".mux-cta-close-btn")
      ) {
        return;
      }
      if (!hasPlaybackStartedRef.current) return;
      if (!isMutedRef.current) return;
      unlockSoundFromUserGesture("pointerdown");
    },
    [unlockSoundFromUserGesture],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current) return;
      const touches = e.changedTouches;
      if (!touches?.length) {
        touchStartRef.current = null;
        return;
      }
      const t = touches[0];
      const target = document.elementFromPoint(
        t.clientX,
        t.clientY,
      ) as HTMLElement | null;
      if (
        target?.closest(".action-button-container") ||
        target?.closest(".mux-cta-close-btn")
      ) {
        touchStartRef.current = null;
        return;
      }
      const dx = Math.abs(t.clientX - touchStartRef.current.x);
      const dy = Math.abs(t.clientY - touchStartRef.current.y);
      const dt = Date.now() - touchStartRef.current.time;
      touchStartRef.current = null;

      // Only respond to taps (small movement, short duration)
      if (dx < 10 && dy < 10 && dt < 300) {
        // Immersive: never pause on tap. Playing → mute toggle; paused → resume.
        const videoElement = getVideoElement(playerRef.current);
        const currentlyPaused =
          videoElement != null ? videoElement.paused : !isPlaying;
        if (currentlyPaused) {
          if (hasPlaybackStartedRef.current) {
            void resumeForcedPlayback("user_tap");
          } else {
            // First start needs a user gesture path (autoplay may have been blocked).
            togglePlayPause();
          }
        } else {
          toggleMute();
        }
      }
    },
    [
      togglePlayPause,
      toggleMute,
      isPlaying,
      getVideoElement,
      resumeForcedPlayback,
    ],
  );

  const handleLoadedMetadata = useCallback(() => {
    setHasReceivedMuxMetadata(true);
    // Log metadata loaded (video info received)
    logVideoEvent({
      event: "video_load_start",
      country: effectiveCountry,
      service,
      playbackId: effectivePlaybackId,
    });
    onReady?.();
    console.log("[MuxVideoPlayerWithCTA] Metadata loaded - setting volume");

    // Set volume when metadata loads (before playback starts)
    try {
      const player = playerRef.current;
      if (player) {
        const videoElement = getVideoElement(player);
        if (videoElement && "volume" in videoElement && videoElement.paused) {
          videoElement.volume = 1.0;
        }
      }
    } catch (error) {
      console.debug("[MuxVideoPlayerWithCTA] Error in loadedMetadata:", error);
    }
  }, [
    onReady,
    getVideoElement,
    effectiveCountry,
    service,
    effectivePlaybackId,
  ]);

  const handleMuxLoadedData = useCallback(() => {
    setMuxHasLoadedData(true);
  }, []);

  // Show mute indicator when video is playing and muted
  // Keep visible until user unmutes (don't auto-hide). Compact after a short beat.
  useEffect(() => {
    if (isPlaying && isMuted) {
      setShowMuteIndicator(true);
      setMuteChipCompact(false);
      const compactTimer = window.setTimeout(() => {
        setMuteChipCompact(true);
      }, 3200);
      return () => window.clearTimeout(compactTimer);
    }
    setShowMuteIndicator(false);
    setMuteChipCompact(false);
  }, [isPlaying, isMuted]);

  // Managed autoplay: we still call play() ourselves for muted-first control, but we
  // also pass muted autoPlay to mux-player as a backup when native play() events lag
  // (DualCTA "Cargando" trap). Prefer muted always for the native attribute.
  const managedAutoplay = autoPlay !== false;
  const isMobileOrInstagram = isMobile || isInstagram;
  /**
   * Muted-first on Instagram / Meta IAB AND all mobile (iOS Safari/Chrome WebKit).
   * Sound-first on mobile → NotAllowedError + DualCTA "video stuck" Slack spam
   * (session Colón AR / iOS 18.7: video_stuck_not_playing while m3u8 200 OK).
   * Desktop Chrome/Safari: try sound first, fall back to muted, then unlock on gesture.
   * Keep `isMobileOrInstagram` in the expression for validate-dual-cta-mux-integration.
   */
  const preferMutedFirstAutoplay =
    isMobileOrInstagram || needsMutedAutoplay;

  const logAutoplayStartup = useCallback(
    (
      path:
        | "already_playing"
        | "sound"
        | "muted"
        | "manual_required"
        | "aborted_race",
      extra?: { errorType?: string; errorMessage?: string },
    ) => {
      logVideoEvent({
        event: "autoplay_startup_path",
        country: effectiveCountry,
        service,
        playbackId: effectivePlaybackId,
        errorType: extra?.errorType ?? path,
        errorMessage: extra?.errorMessage,
      });
    },
    [effectiveCountry, service, effectivePlaybackId],
  );

  useEffect(() => {
    if (!managedAutoplay) return;
    const player = playerRef.current;
    if (!player) return;
    if (!effectivePlaybackId) return;
    // Wait until Mux signals enough buffer — avoids play() before media is ready.
    if (!muxCanPlayBuffered) return;

    const attemptKey = `${effectivePlaybackId}:${retryCount}`;
    if (autoplayAttemptKeyRef.current === attemptKey) return;
    autoplayAttemptKeyRef.current = attemptKey;
    const runId = ++autoplayRunIdRef.current;
    let cancelled = false;

    const isPlayerAlreadyPlaying = () => {
      const videoElement = getVideoElement(player);
      return Boolean(
        videoElement && !videoElement.paused && !videoElement.ended,
      );
    };

    const revealManualPlay = () => {
      if (cancelled || runId !== autoplayRunIdRef.current) return;
      if (hasPlaybackStartedRef.current || isPlayerAlreadyPlaying()) return;
      logAutoplayStartup("manual_required");
      logVideoEvent({
        event: "manual_play_required",
        country: effectiveCountry,
        service,
        playbackId: effectivePlaybackId,
        reason: "autoplay_failed",
        isInstagram,
        isMobile,
      });
      setIsLoading(false);
      setShowPlayButton(true);
    };

    const attemptPlay = async (muted: boolean): Promise<boolean> => {
      if (cancelled || runId !== autoplayRunIdRef.current) return false;
      if (hasPlaybackStartedRef.current || isPlayerAlreadyPlaying()) {
        // Already playing (or we already marked start) — still notify parent so
        // DualCTA/loading overlays dismiss (onPlay may have been missed).
        markPlaybackStarted("already_playing");
        logAutoplayStartup("already_playing");
        return true;
      }

      const videoElement = getVideoElement(player);
      // Harden *after* play when possible: attribute writes can interrupt startup.
      if (videoElement) {
        videoElement.muted = muted;
        try {
          videoElement.playsInline = true;
          videoElement.setAttribute("playsinline", "");
          videoElement.setAttribute("webkit-playsinline", "");
        } catch {
          /* ignore */
        }
        if (!muted && "volume" in videoElement) videoElement.volume = 1.0;
      }
      setIsMuted(muted);
      isMutedRef.current = muted;

      const playFn = async () => {
        // Safari / iOS / IG: native HTMLVideoElement.play() is more reliable under
        // autoplay policy. Desktop Chrome: mux-player.play() keeps media-chrome in sync.
        if (preferNativePlayback || isInstagram || isIOS) {
          if (videoElement && typeof videoElement.play === "function") {
            await videoElement.play();
            return;
          }
          if (typeof player.play === "function") {
            await player.play();
            return;
          }
        } else {
          if (typeof player.play === "function") {
            await player.play();
            return;
          }
          if (videoElement && typeof videoElement.play === "function") {
            await videoElement.play();
            return;
          }
        }
        throw new Error("no_play_method");
      };

      try {
        await playFn();
        if (cancelled || runId !== autoplayRunIdRef.current) return false;
        // Mark BEFORE harden — parent overlay + keep-alive must arm immediately.
        markPlaybackStarted(muted ? "autoplay_muted" : "autoplay_sound");
        setShowMuteIndicator(muted);
        hardenNativeVideoElement(getVideoElement(player));
        logAutoplayStartup(muted ? "muted" : "sound");
        return true;
      } catch (error) {
        const errorName =
          typeof error === "object" && error !== null
            ? String((error as { name?: unknown }).name ?? "")
            : "";
        const errorMessage =
          error instanceof Error ? error.message : String(error);

        if (
          errorName === "AbortError" &&
          (hasPlaybackStartedRef.current || isPlayerAlreadyPlaying())
        ) {
          markPlaybackStarted("aborted_race");
          logAutoplayStartup("aborted_race", {
            errorType: errorName,
            errorMessage,
          });
          return true;
        }
        logVideoEvent({
          event: "video_play_failed",
          country: effectiveCountry,
          service,
          playbackId: effectivePlaybackId,
          errorType: errorName,
          errorMessage,
          reason: muted ? "autoplay_muted" : "autoplay_sound",
          isInstagram,
          isMobile,
        });
        console.debug(
          `[MuxVideoPlayerWithCTA] Autoplay ${muted ? "muted" : "sound"} failed:`,
          error,
        );
        return false;
      }
    };

    const tryPlay = async () => {
      // Mobile/IG: muted first always (019fb326 sound-first NotAllowedError churn).
      // Desktop: try sound first, then muted.
      if (preferMutedFirstAutoplay) {
        if (await attemptPlay(true)) return;
        // Sound second only if muted somehow failed (rare) — still no gesture.
        if (await attemptPlay(false)) return;
      } else {
        if (await attemptPlay(false)) return;
        if (await attemptPlay(true)) return;
      }
      revealManualPlay();
    };

    void tryPlay();

    const checkDelay = isMobileOrInstagram ? 1500 : 2000;
    const checkPlaybackTimeout = setTimeout(() => {
      if (cancelled || runId !== autoplayRunIdRef.current) return;
      if (hasPlaybackStartedRef.current || isPlayerAlreadyPlaying()) return;
      revealManualPlay();
    }, checkDelay);

    return () => {
      cancelled = true;
      clearTimeout(checkPlaybackTimeout);
    };
  }, [
    managedAutoplay,
    effectivePlaybackId,
    retryCount,
    muxCanPlayBuffered,
    getVideoElement,
    isMobileOrInstagram,
    preferMutedFirstAutoplay,
    logAutoplayStartup,
    markPlaybackStarted,
    effectiveCountry,
    service,
    isInstagram,
    isMobile,
    isIOS,
    preferNativePlayback,
  ]);

  // Soft keep-alive: resume after tab/WebView hide, bfcache restore, or silent pause.
  // conversionReel OR mobile/Safari/IG — DualCTA sets conversionReel=false but still needs this.
  useEffect(() => {
    if (!forceKeepPlayingRef.current && !needsSoftKeepAlive) return;

    const tryResume = (reason: string) => {
      if (document.visibilityState === "hidden") return;
      void resumeForcedPlayback(reason);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") tryResume("visibility");
    };
    const onPageShow = () => tryResume("pageshow");
    const onFocus = () => tryResume("window_focus");

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onFocus);
    };
  }, [resumeForcedPlayback, needsSoftKeepAlive]);

  /**
   * Native video lock (most robust layer for iOS/IG/Chrome mobile):
   * - strip `controls` if the browser/Mux re-adds them
   * - on every native `pause`, force resume (bypass media-chrome)
   * - re-harden on attribute mutations
   */
  useEffect(() => {
    if (!forceKeepPlayingRef.current && !needsSoftKeepAlive) return;
    if (!effectivePlaybackId) return;

    let cancelled = false;
    let attachedVideo: HTMLVideoElement | null = null;
    let mo: MutationObserver | null = null;
    let pollId: number | null = null;

    const onNativePause = () => {
      if (cancelled) return;
      if (intentionalPauseRef.current) return;
      // conversionReel always; soft keep-alive on Chrome/Safari/IG mobile.
      if (!forceKeepPlayingRef.current && !needsSoftKeepAlive) return;
      if (!hasPlaybackStartedRef.current) return;
      if (videoEndedRef.current && !loop) return;
      if (document.visibilityState === "hidden") return;
      // Single delayed resume (was 3× microtask+rAF+timeout → main-thread thrash).
      window.setTimeout(() => {
        void resumeForcedPlayback("native_pause");
      }, 200);
    };

    const attach = (video: HTMLVideoElement) => {
      if (cancelled || attachedVideo === video) {
        hardenNativeVideoElement(video);
        return;
      }
      // Detach previous
      if (attachedVideo) {
        attachedVideo.removeEventListener("pause", onNativePause);
        mo?.disconnect();
        mo = null;
      }
      attachedVideo = video;
      hardenNativeVideoElement(video);
      video.addEventListener("pause", onNativePause);
      mo = new MutationObserver(() => {
        hardenNativeVideoElement(video);
      });
      mo.observe(video, {
        attributes: true,
        attributeFilter: ["controls", "controlslist", "playsinline"],
      });
    };

    const tryAttach = () => {
      if (cancelled) return true;
      const video = getVideoElement(playerRef.current);
      if (video) {
        attach(video);
        return true;
      }
      return false;
    };

    if (!tryAttach()) {
      pollId = window.setInterval(() => {
        if (tryAttach() && pollId != null) {
          window.clearInterval(pollId);
          pollId = null;
        }
      }, 150);
      // Give up polling after player swap window
      window.setTimeout(() => {
        if (pollId != null) {
          window.clearInterval(pollId);
          pollId = null;
        }
      }, 20_000);
    }

    // Re-probe after canplay / mux rebuilds internal media element
    const reattachId = window.setInterval(() => {
      if (cancelled) return;
      const video = getVideoElement(playerRef.current);
      if (video) attach(video);
    }, 2500);

    return () => {
      cancelled = true;
      if (pollId != null) window.clearInterval(pollId);
      window.clearInterval(reattachId);
      if (attachedVideo) {
        attachedVideo.removeEventListener("pause", onNativePause);
      }
      mo?.disconnect();
    };
  }, [
    effectivePlaybackId,
    retryCount,
    getVideoElement,
    resumeForcedPlayback,
    needsSoftKeepAlive,
    loop,
  ]);

  // Watchdog: if media-chrome / stall left us paused, recover without controls UI.
  useEffect(() => {
    if (!forceKeepPlayingRef.current && !needsSoftKeepAlive) return;
    if (!hasPlaybackStarted) return;

    // Slower watchdog (was 700ms + PostHog flood → tab freeze on exclusive/DualCTA).
    // Soft path (mobile/IG/Safari): 3.5s to avoid thrash; conversionReel keeps 2.5s.
    const intervalMs = forceKeepPlayingRef.current ? 2500 : 3500;
    const id = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      if (intentionalPauseRef.current || hasErrorRef.current) return;
      if (videoEndedRef.current && !loop) return;
      const videoElement = getVideoElement(playerRef.current);
      if (!videoElement) return;
      hardenNativeVideoElement(videoElement);
      if (videoElement.ended && !loop) {
        videoEndedRef.current = true;
        return;
      }
      if (videoElement.paused && !videoElement.ended) {
        void resumeForcedPlayback("watchdog");
      }
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [
    hasPlaybackStarted,
    getVideoElement,
    resumeForcedPlayback,
    needsSoftKeepAlive,
    loop,
  ]);

  // Create payment intent for checkout — Stripe (USD) en todos los países.
  // MercadoPago está deshabilitado para checkouts; AR pasa por el mismo flujo Stripe
  // y los precios por país siguen en `getPackPricing` (USD cents).
  //
  // Tipos de pack:
  //   • "exclusive" → SUBSCRIPTION (membresía mensual, hosted Stripe Checkout con redirect).
  //   • Resto (channel_invite, sorpresa, telegram_groups, talk, etc.) → ONE-TIME PaymentIntent
  //     con Elements embebido en este mismo player.
  const createPaymentIntent = useCallback(
    async (packId: string, email?: string) => {
      setCheckoutError(null);

      let stripeFailMeta: { http?: number; code?: string; kind?: string } = {};
      let amountCentsForTrack: number | undefined;

      try {
        // Para overridePackId (sorpresa, exclusive) priorizar static para que siempre exista precio y se genere el payment intent
        const packPricing =
          (overridePackId
            ? getPackPricingStatic(packId, effectiveCountry)
            : null) ??
          getPackPricing(packId, effectiveCountry) ??
          getPackPricingStatic(packId, effectiveCountry);
        if (!packPricing) {
          stripeFailMeta = { kind: "invalid_pack_id" };
          throw new Error("Invalid pack ID");
        }
        amountCentsForTrack = packPricing.amount;

        // ── Pack "exclusive" = membresía Stripe (subscription) ───────────────
        // Redirige al checkout hosted de Stripe. El Price ID se busca en
        // `membership_products` por `plan_tier=vip` (admin lo pega en
        // /admin/suscripciones); si no hay, se crea inline con el monto del
        // catálogo (`getPackPricing("exclusive")`).
        //
        // Descuentos (cupón Stripe PROMO{N}, duration: once = solo el primer mes):
        //   • 50% baseline del pack exclusive (STRIPE_EXCLUSIVE_PACK_DISCOUNT_PERCENT)
        //   • + watcher luck % opcional (clampado por VITE_VIDEO_WATCHER_LUCK_DISCOUNT_MAX_PERCENT)
        if (packId === "exclusive") {
          const origin = typeof window !== "undefined" ? window.location.origin : "";
          // Post-pago: Clerk en `/portal/completar-perfil` (es) o
          // `/portal/complete-profile` (en) → completar perfil → `/pending-approval`.
          // El path se elige por idioma del visitante; Stripe agrega `?session_id={...}` solo.
          // `lang=` viaja para que cualquier hop manual posterior (e.g. /registro) lo respete.
          const postPaymentPath = getPostPaymentSuccessPath(detectedLanguage);
          const join = postPaymentPath.includes("?") ? "&" : "?";
          const langCode = (detectedLanguage || "").trim().toLowerCase().split("-")[0];
          const langSuffix = langCode ? `&lang=${encodeURIComponent(langCode)}` : "";
          const successUrl =
            `${origin}${postPaymentPath}` +
            `${join}plan=exclusiva&source=mux_video_exclusive${langSuffix}`;
          const cancelUrl =
            typeof window !== "undefined" ? window.location.href : `${origin}/exclusive`;

          // Cupón combinado: 50% baseline + watcher luck (si está activo).
          // En modo subscription el cupón aplica solo al primer mes (`duration: "once"`)
          // — ver api/create-subscription-checkout (crea PROMO50 si no existe).
          //
          // Contrato del endpoint hasDiscount:
          //   amount          = precio FINAL con descuento (cents)
          //   originalAmount  = precio de lista sin descuento (cents)
          //   discountPercentage = % del cupón PROMO{N}
          // hasDiscount requiere originalAmount > amount.
          const watcherLuck = stripeWatcherLuckEligible ? stripeWatcherLuckPercent : 0;
          const combinedDiscountPct = Math.min(
            75,
            Math.round(STRIPE_EXCLUSIVE_PACK_DISCOUNT_PERCENT + watcherLuck),
          );
          const listAmountCents = packPricing.amount;
          const discountedAmountCents =
            combinedDiscountPct > 0
              ? Math.max(
                  50, // mínimo Stripe USD
                  Math.round(listAmountCents * (1 - combinedDiscountPct / 100)),
                )
              : listAmountCents;
          const productName = packPricing.nameEs || packPricing.name || "Exclusive Membership";

          const subscriptionRes = await fetch(`${origin}/api/create-subscription-checkout`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
            ...getDatafastAttributionPayload(),
              amount: discountedAmountCents,
              currency: "usd",
              productName,
              buyerEmail: email || undefined,
              country: effectiveCountry || undefined,
              successUrl,
              cancelUrl,
              intervalCount: 1,
              ...(combinedDiscountPct > 0 && discountedAmountCents < listAmountCents
                ? {
                    originalAmount: listAmountCents,
                    discountPercentage: combinedDiscountPct,
                  }
                : {}),
              ...(inviteCode ? { invite_code: inviteCode } : {}),
              metadata: {
                // Source explícito para attribution end-to-end (Stripe checkout +
                // subscription + webhook). El endpoint también lo setea por defecto,
                // pero lo pasamos explícito para que aparezca en logs del cliente.
                source: BRAND_SOURCE_KEY,
                flow: "mux_video_exclusive",
                plan_tier: "exclusive",
                pack_id: "exclusive",
                product_name: productName,
                product_type: "exclusive",
                template: "MuxVideoPlayerWithCTA",
                template_variant: "exclusive_membership",
                surface: typeof window !== "undefined" ? window.location.pathname : "",
                country: effectiveCountry || "",
                ...(combinedDiscountPct > 0
                  ? { discount_percent_first_month: String(combinedDiscountPct) }
                  : {}),
                ...(watcherLuck > 0 ? { watcher_luck_percent: String(watcherLuck) } : {}),
              },
            }),
          });
          const subData = (await subscriptionRes.json()) as {
            url?: string;
            error?: string;
            code?: string;
          };
          if (!subscriptionRes.ok || !subData.url) {
            stripeFailMeta = {
              http: subscriptionRes.status,
              code: subData.code,
              kind: "subscription_checkout_failed",
            };
            throw new Error(
              subData.error || `Subscription checkout failed (${subscriptionRes.status})`,
            );
          }
          flushSync(() => setIsNavigatingAway(true));
          window.location.assign(subData.url);
          return;
        }
        const apiUrl =
          typeof window !== "undefined"
            ? `${window.location.origin}${CREATE_PAYMENT_INTENT_ENDPOINT}`
            : CREATE_PAYMENT_INTENT_ENDPOINT;
        // Custom Stripe: always USD regardless of country; amount is in USD cents
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount: packPricing.amount,
            currency: "usd",
            packId: packId,
            buyerEmail: email || undefined, // Optional - Stripe will collect it in billing details
            source: window.location.pathname,
            country: effectiveCountry || undefined, // Fallback when server headers (IP country) are missing
            watcherLuckDiscountPercent: stripeWatcherLuckEligible
              ? stripeWatcherLuckPercent
              : 0,
            ...(inviteCode ? { invite_code: inviteCode } : {}),
          }),
        });

        if (!response.ok) {
          const text = await response.text();
          let errMsg = "Failed to create payment intent";
          let errCode: string | undefined;
          try {
            const errorData = JSON.parse(text) as {
              error?: string;
              message?: string;
              code?: string;
              redirect_url?: string;
            };

            errMsg = errorData.error || errorData.message || errMsg;
            errCode = errorData.code;
          } catch {
            // API returned non-JSON (e.g. 404 HTML)
            if (response.status === 404) {
              errMsg =
                "Payment API not found (404). Check deployment and /api/create-payment-intent.";
            } else if (response.status >= 500) {
              errMsg = `Server error (${response.status}). Check Railway logs for STRIPE_SECRET_KEY and API errors.`;
            } else {
              errMsg = `API error (${response.status})`;
            }
          }
          stripeFailMeta = {
            http: response.status,
            code: errCode,
            kind: "http_error",
          };
          console.error(
            "[MuxVideoPlayerWithCTA] create-payment-intent failed:",
            response.status,
            text.slice(0, 200),
          );
          throw new Error(errMsg);
        }

        const data = await response.json();
        if (!data?.clientSecret) {
          stripeFailMeta = { kind: "missing_client_secret" };
          throw new Error("Invalid API response: missing clientSecret");
        }
        setClientSecret(data.clientSecret);
        setCheckoutState("CHECKOUT");
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to initialize checkout";

        // Track payment intent creation error (GA + PostHog)
        trackButtonClickGA(
          "Payment intent creation failed",
          "mux_payment_intent_error",
          "error",
          service,
          effectiveCountry,
          "MuxVideoPlayerWithCTA",
        );
        trackButtonClickPostHog(
          "Payment intent creation failed",
          "mux_payment_intent_error",
          "error",
          service,
          effectiveCountry,
          "MuxVideoPlayerWithCTA",
        );
        posthogSafeCapture("payment_intent_error", {
          service,
          country: effectiveCountry,
          pathname: window.location.pathname,
          error_message: message.slice(0, 200),
          pack_id: packId,
        });

        trackStripePayFailurePostHog({
          stage: "payment_intent_create",
          flow_surface: "mux_video_cta",
          http_status: stripeFailMeta.http,
          stripe_error_code: stripeFailMeta.code,
          failure_kind: stripeFailMeta.kind,
          error_message: message,
          pack_id: packId,
          amount_cents: amountCentsForTrack,
          currency: "usd",
        });

        setCheckoutError(message);
        setCheckoutState("ERROR");
      } finally {
        setIsCreatingPaymentIntent(false);
      }
    },
    [
      effectiveCountry,
      getPackPricing,
      overridePackId,
      inviteCode,
      service,
      stripeWatcherLuckEligible,
      stripeWatcherLuckPercent,
      detectedLanguage,
    ],
  );

  const activateCtaFromUser = useCallback(() => {
    try {
      // Parent-controlled flows (e.g. BioTemplatePremium) must always win.
      if (onCTAClick) {
        if (parentCtaLockRef.current) {
          return;
        }
        parentCtaLockRef.current = true;
        if (parentCtaUnlockTimerRef.current !== null) {
          clearTimeout(parentCtaUnlockTimerRef.current);
        }
        parentCtaUnlockTimerRef.current = window.setTimeout(() => {
          parentCtaLockRef.current = false;
          parentCtaUnlockTimerRef.current = null;
        }, 900);
        Promise.resolve(onCTAClick(ctaUrl || "")).catch((callbackError) => {
          console.error(
            "[MuxVideoPlayerWithCTA] onCTAClick callback failed:",
            callbackError,
          );
        });
        return;
      }
      const isCheckoutEnabled = Boolean(STRIPE_PUBLISHABLE_KEY);
      const isPurchasableService =
        service === "channel_invite" || Boolean(overridePackId);
      if (!effectiveCtaUrlValid && !(isCheckoutEnabled && isPurchasableService)) {
        console.warn(
          "[MuxVideoPlayerWithCTA] CTA URL is not valid and custom checkout not available",
        );
        return;
      }

      // Free-join: sheet de ingreso custom (bienvenida → cuenta → Telegram).
      if (freeJoinDestination && effectiveCtaUrlValid) {
        mirrorFunnelEvent({
          event_name: "mux_video_free_join_click",
          template: "MuxVideoPlayerWithCTA",
          country: effectiveCountry || undefined,
          language: detectedLanguage || undefined,
          landing_path: typeof window !== "undefined" ? window.location.pathname : undefined,
          props: {
            service,
            pack_id: effectivePackId,
            is_mobile: isMobile,
            destination: freeJoinDestination,
            free_telegram_url: freeJoinDestination,
            has_free_telegram: true,
            playback_id: effectivePlaybackId || undefined,
            source: BRAND_SOURCE_KEY,
            gateway: "none",
            plan_tier: "free",
            entry_ux: "free_join_sheet",
          },
        });
        setFreeJoinSheetOpen(true);
        return;
      }

      if (isCheckoutEnabled && isPurchasableService) {
        if (service === "channel_invite" && !overridePackId) {
          const routerUrl = `/checkout-channel-invite-router?pack=${effectivePackId}&country=${effectiveCountry || "US"}&lang=${detectedLanguage}`;
          flushSync(() => setIsNavigatingAway(true));
          window.location.href = routerUrl;
          return;
        }

        const buttonLabel = isMobile
          ? "Custom checkout start (mobile)"
          : "Custom checkout start (desktop)";
        trackButtonClickGA(
          buttonLabel,
          "mux_custom_checkout_start",
          "custom_checkout",
          service,
          effectiveCountry,
          "MuxVideoPlayerWithCTA",
        );
        trackButtonClickPostHog(
          buttonLabel,
          "mux_custom_checkout_start",
          "custom_checkout",
          service,
          effectiveCountry,
          "MuxVideoPlayerWithCTA",
        );
        posthogSafeCapture("custom_checkout_started", {
          service,
          country: effectiveCountry,
          pathname: window.location.pathname,
          is_mobile: isMobile,
        });

        mirrorFunnelEvent({
          event_name: "mux_video_cta_clicked",
          template: "MuxVideoPlayerWithCTA",
          country: effectiveCountry || undefined,
          language: detectedLanguage || undefined,
          landing_path: typeof window !== "undefined" ? window.location.pathname : undefined,
          props: {
            service,
            pack_id: effectivePackId,
            is_mobile: isMobile,
            is_exclusive: isExclusiveMembership,
            playback_id: effectivePlaybackId || undefined,
            source: BRAND_SOURCE_KEY,
            gateway: "stripe",
            plan_tier: isExclusiveMembership ? "vip" : service,
          },
        });

        if (isMobile) {
          // Exclusive Membership en mobile: NO redirigir al one-time embed.
          // Mantenemos el flujo Stripe Subscription hosted:
          // createPaymentIntent → /api/create-subscription-checkout → Stripe hosted
          // → successUrl /portal/completar-perfil → Clerk → /pending-approval.
          if (isExclusiveMembership) {
            flushSync(() => setIsCreatingPaymentIntent(true));
            void createPaymentIntent(effectivePackId);
            return;
          }

          const checkoutUrl = `/checkout-channel-invite?pack=${effectivePackId}&country=${effectiveCountry || "US"}&lang=${detectedLanguage}`;
          flushSync(() => setIsNavigatingAway(true));
          window.location.href = checkoutUrl;
          return;
        }

        flushSync(() => setIsCreatingPaymentIntent(true));
        void createPaymentIntent(effectivePackId);
        return;
      }

      if (isInstagram) {
        flushSync(() => setIsNavigatingAway(true));
        window.location.href = effectiveCtaUrl;
        return;
      }
      if (openInNewTab) {
        const newWindow = window.open(
          effectiveCtaUrl,
          "_blank",
          "noopener,noreferrer",
        );
        if (!newWindow) {
          flushSync(() => setIsNavigatingAway(true));
          window.location.href = effectiveCtaUrl;
        }
      } else {
        flushSync(() => setIsNavigatingAway(true));
        window.location.href = effectiveCtaUrl;
      }
    } catch (error) {
      console.error("[MuxVideoPlayerWithCTA] Error in activateCtaFromUser:", error);
      try {
        flushSync(() => setIsNavigatingAway(true));
        window.location.href = effectiveCtaUrl;
      } catch (fallbackError) {
        console.error(
          "[MuxVideoPlayerWithCTA] Fallback navigation also failed:",
          fallbackError,
        );
        setIsNavigatingAway(false);
      }
    }
  }, [
    effectiveCtaUrl,
    effectiveCtaUrlValid,
    freeJoinDestination,
    openInNewTab,
    onCTAClick,
    isInstagram,
    service,
    createPaymentIntent,
    isMobile,
    effectiveCountry,
    detectedLanguage,
    effectivePackId,
    overridePackId,
    isExclusiveMembership,
    parentCtaLockRef,
    parentCtaUnlockTimerRef,
  ]);

  const handleCTAClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      activateCtaFromUser();
    },
    [activateCtaFromUser],
  );

  // Checkout handlers - email is now collected by Stripe in billing details

  const handleCheckoutSuccess = useCallback(
    (paymentIntentId?: string, paymentIntentSecret?: string) => {
      // Track custom checkout success before redirect (GA + PostHog)
      trackButtonClickGA(
        "Custom checkout completed",
        "mux_custom_checkout_success",
        "conversion",
        service,
        effectiveCountry,
        "MuxVideoPlayerWithCTA",
      );
      trackButtonClickPostHog(
        "Custom checkout completed",
        "mux_custom_checkout_success",
        "conversion",
        service,
        effectiveCountry,
        "MuxVideoPlayerWithCTA",
      );
      posthogSafeCapture("custom_checkout_success", {
        service,
        country: effectiveCountry,
        pathname: window.location.pathname,
        payment_intent_id: paymentIntentId ?? undefined,
      });

      const postPaymentPath = getPostPaymentSuccessPath(detectedLanguage);
      const params = new URLSearchParams();
      if (paymentIntentId) {
        params.append("payment_intent", paymentIntentId);
      }
      if (paymentIntentSecret) {
        params.append("payment_intent_client_secret", paymentIntentSecret);
      }
      params.append("redirect_status", "succeeded");
      params.append("provider", "stripe");
      const langCode = (detectedLanguage || "").trim().toLowerCase().split("-")[0];
      if (langCode) params.append("lang", langCode);

      const join = postPaymentPath.includes("?") ? "&" : "?";
      const redirectUrl = `${postPaymentPath}${join}${params.toString()}`;

      flushSync(() => setIsNavigatingAway(true));
      window.location.href = redirectUrl;
    },
    [service, effectiveCountry, detectedLanguage],
  );

  const handleCheckoutError = useCallback(
    (error: string) => {
      // Track checkout error (GA + PostHog)
      trackButtonClickGA(
        "Custom checkout error",
        "mux_custom_checkout_error",
        "error",
        service,
        effectiveCountry,
        "MuxVideoPlayerWithCTA",
      );
      trackButtonClickPostHog(
        "Custom checkout error",
        "mux_custom_checkout_error",
        "error",
        service,
        effectiveCountry,
        "MuxVideoPlayerWithCTA",
      );
      posthogSafeCapture("custom_checkout_error", {
        service,
        country: effectiveCountry,
        pathname: window.location.pathname,
        error_message: error.slice(0, 200),
      });

      trackStripePayFailurePostHog({
        stage: "payment_confirm",
        flow_surface: "mux_video_cta",
        failure_kind: "stripe_checkout_form",
        error_message: error,
        pack_id: effectivePackId,
        currency: "usd",
      });

      // Log error to console for debugging
      console.error("[MuxVideoPlayerWithCTA] Checkout error:", error);

      setCheckoutError(error);
      setCheckoutState("ERROR");
      setIsNavigatingAway(false);
    },
    [service, effectiveCountry, effectivePackId],
  );

  const handleBackToVideo = useCallback(() => {
    muxStripeJsUnavailableTrackedRef.current = false;
    setCheckoutState("VIDEO_PREVIEW");
    setClientSecret(null);
    setCheckoutError(null);
    setIsNavigatingAway(false);
  }, []);

  const handleRetryCheckout = useCallback(() => {
    setCheckoutError(null);
    setCheckoutState("VIDEO_PREVIEW");
    setIsNavigatingAway(false);
  }, []);

  // Open URL in Safari from iOS WebView (Instagram/Facebook in-app browser)
  // Uses Safari URL schemes - works to break out of embedded WebView
  const openInSafariFromWebView = useCallback((url: string) => {
    const xSafariScheme = `x-safari-https://${url.replace(/^https?:\/\//, "")}`;
    const legacyScheme = `com-apple-mobilesafari-tab:${url}`;
    setTimeout(() => {
      window.location.href = xSafariScheme;
    }, 50);
    setTimeout(() => {
      window.location.href = legacyScheme;
    }, 400);
    setTimeout(() => {
      window.location.href = url;
    }, 1200);
  }, []);

  // Handle Instagram banner open in browser action
  const handleOpenInBrowser = useCallback(() => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (!url) return;

    // 1. Copy to clipboard (fallback if Safari scheme fails, or for Android)
    const copyToClipboard = () => {
      if (navigator.clipboard?.writeText) {
        return navigator.clipboard.writeText(url);
      }
      return Promise.reject(new Error("Clipboard not available"));
    };
    copyToClipboard()
      .then(() => {
        setLinkCopiedForBrowser(true);
        setTimeout(() => setLinkCopiedForBrowser(false), 4000);
      })
      .catch(() => {
        /* ignore */
      });

    trackButtonClickGA(
      "Instagram banner - Open in browser",
      "instagram_banner_open_browser",
      "instagram_ux",
      service,
      effectiveCountry,
      "MuxVideoPlayerWithCTA",
    );
    trackButtonClickPostHog(
      "Instagram banner - Open in browser",
      "instagram_banner_open_browser",
      "instagram_ux",
      service,
      effectiveCountry,
      "MuxVideoPlayerWithCTA",
    );

    // 2. iOS: Safari URL scheme - breaks out of Instagram WebView
    if (isIOS) {
      openInSafariFromWebView(url);
      return;
    }

    // 3. Android / other: share sheet (may offer "Open in Chrome")
    if (navigator.share) {
      navigator.share({ title: document.title, url }).catch(() => {});
    }
  }, [service, effectiveCountry, isIOS, openInSafariFromWebView]);

  const handleDismissInstagramBanner = useCallback(() => {
    setShowInstagramBanner(false);
    setInstagramBannerDismissed(true);

    // Track banner dismissal
    trackButtonClickGA(
      "Instagram banner - Dismissed",
      "instagram_banner_dismissed",
      "instagram_ux",
      service,
      effectiveCountry,
      "MuxVideoPlayerWithCTA",
    );
    trackButtonClickPostHog(
      "Instagram banner - Dismissed",
      "instagram_banner_dismissed",
      "instagram_ux",
      service,
      effectiveCountry,
      "MuxVideoPlayerWithCTA",
    );
  }, [service, effectiveCountry]);

  // Safari-specific touch handler for button
  const handleCTATouchStart = useCallback(
    (e: React.TouchEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      // Prevent container from handling this touch
      touchStartRef.current = null;
    },
    [],
  );

  const handleCTATouchEnd = useCallback(
    (e: React.TouchEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      activateCtaFromUser();
    },
    [activateCtaFromUser],
  );

  // Container styling — reel mobile/desktop alineado con MuxVideoPlayer
  // When Instagram Explorer: always add instagram-browser-container for viewport/scroll/video fixes
  const getContainerClasses = () => {
    // z-[1] keeps mux surface above external MuxPosterBackdrop sibling (z-auto).
    const baseClasses = "relative z-[1] bg-black group";
    const instagramClasses = isInstagram ? "instagram-browser-container" : "";

    if (isModal) {
      return `${baseClasses} w-full h-full min-h-0 flex items-center justify-center overflow-hidden ${instagramClasses} ${containerClassName}`;
    }
    if (isReelMode) {
      if (isMobile) {
        // Mobile: Fixed height, no scroll, fully contained
        return `${baseClasses} w-full h-dvh max-h-dvh overflow-hidden ${instagramClasses} ${containerClassName}`;
      }
      // Desktop: Use h-full to respect parent container, no min-height to avoid scroll
      return `${baseClasses} w-full h-full flex items-start justify-center overflow-hidden ${instagramClasses} ${containerClassName}`;
    }
    if (currentDisplayMode === "contain") {
      return `${baseClasses} w-full h-full min-h-[400px] max-h-[90vh] flex items-center justify-center ${instagramClasses} ${containerClassName}`;
    }
    if (currentDisplayMode === "cover") {
      return `${baseClasses} w-full h-full min-h-[80vh] max-h-[90vh] ${instagramClasses} ${containerClassName}`;
    }
    return `${baseClasses} w-full h-full min-h-[400px] max-h-[800px] ${instagramClasses} ${containerClassName}`;
  };

  const handleContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isReelMode || isMobile) return;
      const target = e.target as HTMLElement;
      if (
        target.closest(".action-button-container") ||
        target.closest(".mux-cta-close-btn")
      )
        return;
      // Immersive: playing → mute toggle; paused → force resume (never pause).
      const videoElement = getVideoElement(playerRef.current);
      const currentlyPaused =
        videoElement != null ? videoElement.paused : !isPlaying;
      if (currentlyPaused) {
        if (hasPlaybackStartedRef.current) {
          void resumeForcedPlayback("desktop_click");
        } else {
          togglePlayPause();
        }
      } else {
        toggleMute();
      }
    },
    [
      isReelMode,
      isMobile,
      togglePlayPause,
      toggleMute,
      isPlaying,
      getVideoElement,
      resumeForcedPlayback,
    ],
  );

  const getVideoStyles = useCallback(() => {
    const id = safeCssId;
    if (isReelMode) {
      if (isMobile) {
        // Platform-specific height handling
        const heightStyles = isIOS
          ? `
            /* iOS Safari specific - use multiple fallbacks for dynamic toolbar */
            height: 100vh !important;
            height: 100dvh !important;
            height: -webkit-fill-available !important;
            min-height: 100vh !important;
            min-height: 100dvh !important;
            min-height: -webkit-fill-available !important;
          `
          : `
            /* Android Chrome - dvh works well.
               OJO con el ORDEN: la última declaración que el navegador entiende
               es la que gana. Antes esto ponía dvh y DESPUÉS vh, o sea que en
               todo Chrome moderno ganaba 100vh — el viewport LARGO. Con la barra
               del WebView de IG desplegada (viewport 733→423 en el replay
               019f8489) el contenedor quedaba 310px más alto que la pantalla y
               el CTA de abajo salía del área visible.
               Fallback primero, unidad moderna al final. */
            height: 100vh !important;
            height: 100dvh !important;
            min-height: 100vh !important;
            min-height: 100svh !important;
          `;

        // Landscape mode adjustments
        const landscapeStyles = isLandscape
          ? `
          /* Landscape orientation adjustments using Mux CSS variables */
          @media (orientation: landscape) {
            .mux-video-player-cta-${id} {
              /* Fallback primero, unidad moderna al final (ver nota de orden arriba). */
              height: 100vh !important;
              height: 100dvh !important;
            }
            .mux-video-player-cta-${id} mux-player {
              --media-object-fit: contain;
              --media-object-position: center;
            }
          }
        `
          : "";

        return `
          .mux-video-player-cta-${id} {
            width: 100vw !important;
            ${heightStyles}
            /* Fallback primero, unidad moderna al final (ver nota de orden arriba). */
            max-height: 100vh !important;
            max-height: 100dvh !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            overflow: hidden !important;
            -webkit-overflow-scrolling: touch !important;
            /* GPU acceleration for smoother scrolling */
            -webkit-transform: translateZ(0) !important;
            transform: translateZ(0) !important;
            will-change: transform !important;
            /* TikTok style: full immersive screen */
            margin: 0 !important;
            padding: 0 !important;
          }
          .mux-video-player-cta-${id} mux-player {
            width: 100% !important;
            height: 100% !important;
            /* Fallback primero, unidad moderna al final (ver nota de orden arriba). */
            min-height: 100vh !important;
            min-height: 100svh !important;
            max-height: 100vh !important;
            max-height: 100dvh !important;
            /* Use official Mux CSS variables for video sizing - TikTok style cover */
            --media-object-fit: cover;
            --media-object-position: center;
          }
          /* Immersive: never surface native Mux chrome (controls stay hidden) */
          .mux-video-player-cta-${id} mux-player::part(bottom),
          .mux-video-player-cta-${id} mux-player::part(top),
          .mux-video-player-cta-${id} mux-player::part(center),
          .mux-video-player-cta-${id} mux-player::part(gesture-layer) {
            display: none !important;
            pointer-events: none !important;
            opacity: 0 !important;
          }
          /* Video element inherits from --media-object-fit and --media-object-position */
          .mux-video-player-cta-${id} mux-player video {
            width: 100% !important;
            height: 100% !important;
            /* Ensure inline playback on all mobile devices */
            -webkit-playsinline: true !important;
            playsinline: true !important;
            pointer-events: none !important;
          }
          /* iOS Safari specific viewport fixes */
          @supports (-webkit-touch-callout: none) {
            .mux-video-player-cta-${id} {
              height: -webkit-fill-available !important;
              min-height: -webkit-fill-available !important;
            }
          }
          /* Android specific optimizations */
          ${
            isAndroid
              ? `
          .mux-video-player-cta-${id} {
            /* Android Chrome handles dvh well */
            height: 100dvh !important;
            overscroll-behavior: none !important;
          }
          .mux-video-player-cta-${id} mux-player video {
            /* Ensure inline playback on Android */
            playsinline: true !important;
          }
          `
              : ""
          }
          /* iOS specific optimizations */
          ${
            isIOS
              ? `
          .mux-video-player-cta-${id} {
            /* iOS needs -webkit-fill-available for proper height */
            height: -webkit-fill-available !important;
            /* Prevent iOS rubber-banding effect */
            overscroll-behavior: none !important;
          }
          .mux-video-player-cta-${id} mux-player video {
            /* iOS requires webkit prefix */
            -webkit-playsinline: true !important;
            playsinline: true !important;
          }
          `
              : ""
          }
          ${landscapeStyles}
          ${
            isInstagram
              ? `
          .mux-video-player-cta-${id} {
            -webkit-overflow-scrolling: touch !important;
            touch-action: pan-y pinch-zoom !important;
            -webkit-transform: translateZ(0) !important;
            transform: translateZ(0) !important;
            min-height: 100svh !important;
            height: 100svh !important;
            overscroll-behavior: none !important;
          }
          .mux-video-player-cta-${id} mux-player {
            -webkit-playsinline: true !important;
            playsinline: true !important;
            x5-playsinline: true !important;
            x5-video-player-type: h5 !important;
            x5-video-player-fullscreen: false !important;
          }
          .mux-video-player-cta-${id} mux-player video {
            -webkit-playsinline: true !important;
            playsinline: true !important;
            x5-playsinline: true !important;
            x5-video-player-type: h5 !important;
            x5-video-player-fullscreen: false !important;
          }
          .mux-video-player-cta-${id} button,
          .mux-video-player-cta-${id} .action-button-container button {
            -webkit-tap-highlight-color: transparent !important;
            touch-action: manipulation !important;
            min-height: 48px !important;
            min-width: 48px !important;
            -webkit-user-select: none !important;
            user-select: none !important;
          }
          `
              : ""
          }
        `;
      }
      return `
        .mux-video-player-cta-${id} {
          width: 100% !important;
          height: 100% !important;
          min-height: 100vh !important;
          display: flex !important;
          align-items: stretch !important;
          justify-content: center !important;
          background-color: #000 !important;
          position: relative !important;
          overflow: hidden !important;
          box-sizing: border-box !important;
        }
        .mux-video-player-cta-${id}[data-mux-reel-viewport="page"] {
          /* Fallback primero, unidad moderna al final (ver nota de orden arriba). */
          height: 100vh !important;
          height: 100dvh !important;
          min-height: 100vh !important;
          min-height: 100svh !important;
        }
        .mux-video-player-cta-${id} mux-player {
          width: auto !important;
          height: 100% !important;
          max-width: min(56.25vh, 100%) !important;
          /* Fallback primero, unidad moderna al final (ver nota de orden arriba). */
          max-height: 100vh !important;
          max-height: 100dvh !important;
          min-width: 320px !important;
          margin: 0 auto !important;
          display: block !important;
          position: relative !important;
          /* Use official Mux CSS variables for video sizing */
          --media-object-fit: contain;
          --media-object-position: center;
        }
        /* Immersive: never surface native Mux chrome */
        .mux-video-player-cta-${id} mux-player::part(bottom),
        .mux-video-player-cta-${id} mux-player::part(top),
        .mux-video-player-cta-${id} mux-player::part(center),
        .mux-video-player-cta-${id} mux-player::part(gesture-layer) {
          display: none !important;
          pointer-events: none !important;
          opacity: 0 !important;
        }
        /* Video element inherits from --media-object-fit and --media-object-position */
        .mux-video-player-cta-${id} mux-player video {
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
          pointer-events: none !important;
        }
        @media (min-width: 768px) and (max-width: 1024px) {
          .mux-video-player-cta-${id} mux-player {
            max-width: min(56.25vh, 85vw) !important;
          }
        }
        @media (min-width: 1025px) {
          .mux-video-player-cta-${id} mux-player {
            max-width: min(56.25vh, 90vw) !important;
          }
        }
        @media (min-width: 1920px) {
          .mux-video-player-cta-${id} mux-player {
            max-width: min(56.25vh, 1200px) !important;
          }
        }
      `;
    }
    if (currentDisplayMode === "cover") {
      return `
        .mux-video-player-cta-${id} mux-player {
          object-fit: cover !important;
          width: 100% !important;
          height: 100% !important;
        }
      `;
    }
    return `
      .mux-video-player-cta-${id} mux-player {
        object-fit: contain !important;
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
      }
    `;
  }, [
    isReelMode,
    isMobile,
    isInstagram,
    isIOS,
    isAndroid,
    isLandscape,
    currentDisplayMode,
    safeCssId,
  ]);

  // Get CTA button styles to ensure visibility and proper positioning
  const getCTAButtonStyles = useCallback(() => {
    const id = safeCssId;
    const isValid = Boolean(showCTAButton);

    // Detectar si Special Offer está activo para aplicar estilo verde (más conversión)
    const specialOfferEnv = (import.meta.env.VITE_PREMIUM_SPECIAL_OFFER as string)?.toLowerCase().trim();
    const isSpecialOfferActive = specialOfferEnv === "true" || specialOfferEnv === "1" || specialOfferEnv === "active";

    // Precedence: Special Offer (green) > Default (brand gold). Exclusive Membership also gold.
    // Brand gold is the default CTA across all templates — replaces the old purple/pink theme.
    const buttonBg = isSpecialOfferActive
      ? "linear-gradient(to right, #10b981, #059669, #047857) !important"
      : "linear-gradient(135deg, #fde68a 0%, #fbbf24 35%, #f59e0b 65%, #b45309 100%) !important";

    const buttonBgHover = isSpecialOfferActive
      ? "linear-gradient(to right, #059669, #047857, #065f46) !important"
      : "linear-gradient(135deg, #fcd34d 0%, #f59e0b 40%, #d97706 70%, #92400e 100%) !important";

    const buttonShadow = isSpecialOfferActive
      ? "0 4px 14px 0 rgba(16, 185, 129, 0.5), 0 2px 4px 0 rgba(5, 150, 105, 0.4) !important"
      : "0 10px 30px -6px rgba(217, 119, 6, 0.65), 0 4px 12px -2px rgba(180, 83, 9, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.4), inset 0 -1px 0 rgba(120, 53, 15, 0.3) !important";

    const buttonShadowHover = isSpecialOfferActive
      ? "0 6px 20px 0 rgba(16, 185, 129, 0.6), 0 4px 8px 0 rgba(5, 150, 105, 0.5) !important"
      : "0 14px 36px -6px rgba(217, 119, 6, 0.75), 0 6px 16px -2px rgba(180, 83, 9, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(120, 53, 15, 0.35) !important";

    const buttonTextColor = isSpecialOfferActive
      ? "white"
      : "#3f2305";

    const buttonBorder = isSpecialOfferActive
      ? "1px solid rgba(255, 255, 255, 0.1) !important"
      : "1px solid rgba(254, 243, 199, 0.45) !important";

    const buttonBorderHover = isSpecialOfferActive
      ? "rgba(255, 255, 255, 0.2) !important"
      : "rgba(254, 243, 199, 0.6) !important";

    const buttonTextShadow = isSpecialOfferActive
      ? "0 1px 2px rgba(0, 0, 0, 0.1) !important"
      : "0 1px 0 rgba(255, 255, 255, 0.35) !important";

    const buttonIconFilter = isSpecialOfferActive
      ? "drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1)) !important"
      : "drop-shadow(0 1px 0 rgba(120, 53, 15, 0.35)) !important";

    // Base button styles (shared across all modes)
    const baseButtonStyles = `
      /* Instagram browser banner animation */
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .animate-slide-down {
        animation: slideDown 0.3s ease-out;
      }

      /* Exclusive gold shine sweep */
      @keyframes muxCtaGoldShine {
        0% { transform: translateX(-100%); }
        55% { transform: translateX(120%); }
        100% { transform: translateX(120%); }
      }
      /* Exclusive subtle pulse so the gold CTA feels premium, not flat */
      @keyframes muxCtaGoldPulse {
        0%, 100% { box-shadow: 0 10px 30px -6px rgba(217, 119, 6, 0.65), 0 4px 12px -2px rgba(180, 83, 9, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.4), inset 0 -1px 0 rgba(120, 53, 15, 0.3); }
        50% { box-shadow: 0 12px 34px -6px rgba(217, 119, 6, 0.78), 0 6px 14px -2px rgba(180, 83, 9, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(120, 53, 15, 0.35); }
      }

      .mux-video-player-cta-${id} .cta-action-button {
        z-index: 51 !important;
        position: relative !important;
        pointer-events: auto !important;
        visibility: visible !important;
        opacity: ${isValid ? 1 : 0.7} !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
        box-sizing: border-box !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
        border: ${buttonBorder};
        background: ${buttonBg};
        color: ${buttonTextColor} !important;
        font-weight: 700 !important;
        letter-spacing: 0.01em !important;
        border-radius: 9999px !important;
        box-shadow: ${buttonShadow};
        transition: all 0.3s ease-out !important;
        cursor: pointer !important;
        -webkit-tap-highlight-color: transparent !important;
        -webkit-touch-callout: none !important;
        -webkit-user-select: none !important;
        user-select: none !important;
        touch-action: manipulation !important;
        ${isVipMembershipCta ? "animation: muxCtaGoldPulse 3.4s ease-in-out infinite !important;" : ""}
      }
      ${
        isVipMembershipCta
          ? `
      .mux-video-player-cta-${id} .cta-action-button::before {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(110deg, transparent 35%, rgba(255, 255, 255, 0.55) 50%, transparent 65%);
        transform: translateX(-100%);
        animation: muxCtaGoldShine 3.6s ease-in-out infinite;
        pointer-events: none;
        z-index: 0;
      }
      .mux-video-player-cta-${id} .cta-action-button > * {
        position: relative;
        z-index: 1;
      }
      .mux-video-player-cta-${id} .cta-action-button.cta-vip-membership {
        border-radius: 1.15rem !important;
        min-height: 54px !important;
        height: auto !important;
        padding: 0.9rem 1.2rem !important;
        font-size: 0.98rem !important;
        letter-spacing: 0.01em !important;
        gap: 0.5rem !important;
        box-shadow:
          0 12px 36px -8px rgba(251, 191, 36, 0.55),
          0 4px 14px -2px rgba(180, 83, 9, 0.45),
          inset 0 1px 0 rgba(255, 255, 255, 0.5) !important;
      }
      /* Stripe badge OUTSIDE the button — subtle trust cue on dark video */
      .mux-video-player-cta-${id} .cta-stripe-trust {
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        width: 100% !important;
        margin-top: 0.55rem !important;
        pointer-events: none !important;
        opacity: 0.55 !important;
      }
      .mux-video-player-cta-${id} .cta-stripe-trust img {
        height: 14px !important;
        width: auto !important;
        max-width: 110px !important;
      }
      .mux-video-player-cta-${id} .cta-vip-scrim {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: min(42%, 220px);
        pointer-events: none;
        z-index: 40;
        background: linear-gradient(
          to top,
          rgba(0, 0, 0, 0.82) 0%,
          rgba(0, 0, 0, 0.45) 42%,
          transparent 100%
        );
      }
      `
          : ""
      }
      .mux-video-player-cta-${id} .cta-action-button:hover:not(:disabled) {
        background: ${buttonBgHover};
        box-shadow: ${buttonShadowHover};
        border-color: ${buttonBorderHover};
      }
      .mux-video-player-cta-${id} .cta-action-button:active:not(:disabled) {
        transform: scale(0.97) !important;
      }
      .mux-video-player-cta-${id} .cta-action-button:disabled {
        background: linear-gradient(to right, #4b5563, #374151) !important;
        color: rgba(255, 255, 255, 0.85) !important;
        cursor: not-allowed !important;
        opacity: 0.7 !important;
        animation: none !important;
      }
      .mux-video-player-cta-${id} .cta-button-text {
        pointer-events: none !important;
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        flex-shrink: 1 !important;
        text-shadow: ${buttonTextShadow};
        letter-spacing: 0.025em !important;
      }
      .mux-video-player-cta-${id} .cta-button-icon {
        pointer-events: none !important;
        flex-shrink: 0 !important;
        filter: ${buttonIconFilter};
        transition: transform 0.3s ease-out !important;
      }
      .mux-video-player-cta-${id} .cta-action-button svg[class*="lucide-instagram"] {
        pointer-events: none !important;
        flex-shrink: 0 !important;
        filter: ${buttonIconFilter};
      }
      .mux-video-player-cta-${id} .cta-action-button:hover:not(:disabled) .cta-button-icon {
        transform: translate(2px, -2px) !important;
      }
    `;

    // Conversion reels (DualCTA / Invitacion phone-frame): ALWAYS absolute + full width of
    // the video column — never position:fixed and never md:hidden. Desktop QA and mobile
    // must see the same CTA inside md:max-w-[430px].
    if (isConversionReel || (isReelMode && !isMobile)) {
      return (
        baseButtonStyles +
        `
        .mux-video-player-cta-${id} .action-button-container {
          z-index: 150 !important;
          position: absolute !important;
          pointer-events: auto !important;
          visibility: visible !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          align-items: center !important;
          bottom: max(1.25rem, calc(env(safe-area-inset-bottom, 0px) + 1rem)) !important;
          left: 0 !important;
          right: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          transform: none !important;
          padding: 0 1.25rem !important;
          box-sizing: border-box !important;
        }
        .mux-video-player-cta-${id} .cta-action-button {
          min-height: 52px !important;
          height: auto !important;
          min-height: 52px !important;
          padding: 0 1.25rem !important;
          font-size: 0.9375rem !important;
          gap: 0.5rem !important;
          width: 100% !important;
          flex-shrink: 0 !important;
          display: flex !important;
        }
        .mux-video-player-cta-${id} .cta-button-text {
          font-size: 0.9375rem !important;
        }
        .mux-video-player-cta-${id} .cta-button-icon {
          width: 1.125rem !important;
          height: 1.125rem !important;
        }
      `
      );
    }

    if (isMobile) {
      // Mobile non-conversion: centered at bottom with safe-area insets
      // Prefer absolute when inside a contained frame (phone-shell); fixed only as fallback
      // for true fullscreen without a parent max-width shell.
      const platformStyles = isIOS
        ? `
        /* iOS specific - ensure proper touch handling */
        .mux-video-player-cta-${id} .cta-action-button {
          -webkit-appearance: none !important;
          -webkit-tap-highlight-color: transparent !important;
          /* Prevent iOS text selection on long press */
          -webkit-user-select: none !important;
          -webkit-touch-callout: none !important;
        }
      `
        : isAndroid
          ? `
        /* Android specific - better ripple effect handling */
        .mux-video-player-cta-${id} .cta-action-button {
          -webkit-tap-highlight-color: rgba(255, 255, 255, 0.1) !important;
        }
      `
          : "";

      return (
        baseButtonStyles +
        `
        .mux-video-player-cta-${id} .action-button-container {
          z-index: 150 !important;
          position: absolute !important;
          pointer-events: auto !important;
          visibility: visible !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          align-items: center !important;
          /* TikTok Shop style - bottom with safe-area for all devices */
          bottom: max(1.25rem, calc(env(safe-area-inset-bottom, 0px) + 1rem)) !important;
          left: max(1rem, env(safe-area-inset-left, 1rem)) !important;
          right: max(1rem, env(safe-area-inset-right, 1rem)) !important;
          width: auto !important;
          min-width: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
        }
        .mux-video-player-cta-${id} .cta-action-button {
          /* TikTok Shop style - prominent button */
          min-height: 56px !important;
          height: 56px !important;
          flex-shrink: 0 !important;
          padding: 0 1.75rem !important;
          font-size: 1.0625rem !important;
          font-weight: 800 !important;
          gap: 0.75rem !important;
          flex: 1 !important;
          max-width: 100% !important;
          width: 100% !important;
          /* Prevent double-tap zoom on button */
          touch-action: manipulation !important;
          /* Enhanced shadow for TikTok Shop look — brand gold (green only for Special Offer) */
          box-shadow: ${
            isSpecialOfferActive
              ? "0 8px 24px 0 rgba(16, 185, 129, 0.55), 0 4px 12px 0 rgba(5, 150, 105, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.15) !important"
              : "0 12px 32px -4px rgba(217, 119, 6, 0.7), 0 6px 14px -2px rgba(180, 83, 9, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.45), inset 0 -1px 0 rgba(120, 53, 15, 0.35), 0 0 0 1px rgba(254, 243, 199, 0.35) !important"
          };
          /* Backdrop blur for better visibility on any video */
          backdrop-filter: blur(8px) !important;
          -webkit-backdrop-filter: blur(8px) !important;
        }
        .mux-video-player-cta-${id} .cta-button-text {
          font-size: 1rem !important;
          font-weight: 800 !important;
          letter-spacing: 0.02em !important;
        }
        .mux-video-player-cta-${id} .cta-button-icon {
          width: 1.25rem !important;
          height: 1.25rem !important;
        }
        /* Small mobile devices (iPhone SE, small Android) */
        @media (max-width: 375px) {
          .mux-video-player-cta-${id} .action-button-container {
            bottom: max(1rem, calc(env(safe-area-inset-bottom, 0px) + 0.75rem)) !important;
            left: max(0.75rem, env(safe-area-inset-left, 0.75rem)) !important;
            right: max(0.75rem, env(safe-area-inset-right, 0.75rem)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            min-height: 52px !important;
            height: 52px !important;
            padding: 0 1.25rem !important;
            font-size: 0.9375rem !important;
            gap: 0.625rem !important;
          }
          .mux-video-player-cta-${id} .cta-button-text {
            font-size: 0.9375rem !important;
          }
          .mux-video-player-cta-${id} .cta-button-icon {
            width: 1.125rem !important;
            height: 1.125rem !important;
          }
        }
        /* Extra small devices (very small Android phones) */
        @media (max-width: 320px) {
          .mux-video-player-cta-${id} .action-button-container {
            padding: 0 !important;
            bottom: max(0.75rem, env(safe-area-inset-bottom)) !important;
            left: max(0.75rem, env(safe-area-inset-left)) !important;
            right: max(0.75rem, env(safe-area-inset-right)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            min-height: 52px !important;
            height: 52px !important;
            padding: 0 1.25rem !important;
            font-size: 0.9375rem !important;
            font-weight: 800 !important;
          }
          .mux-video-player-cta-${id} .cta-button-text {
            font-size: 0.9375rem !important;
            font-weight: 800 !important;
          }
        }
        /* Medium mobile devices (iPhone 12/13/14) */
        @media (min-width: 376px) and (max-width: 414px) {
          .mux-video-player-cta-${id} .action-button-container {
            left: max(1rem, env(safe-area-inset-left)) !important;
            right: max(1rem, env(safe-area-inset-right)) !important;
            bottom: max(1rem, env(safe-area-inset-bottom)) !important;
          }
        }
        /* Large mobile devices (iPhone Pro Max, large Android) */
        @media (min-width: 415px) and (max-width: 767px) {
          .mux-video-player-cta-${id} .action-button-container {
            left: max(1rem, env(safe-area-inset-left)) !important;
            right: max(1rem, env(safe-area-inset-right)) !important;
            bottom: max(1rem, env(safe-area-inset-bottom)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            min-height: 58px !important;
            height: 58px !important;
            padding: 0 1.75rem !important;
            font-size: 1.0625rem !important;
            font-weight: 800 !important;
          }
          .mux-video-player-cta-${id} .cta-button-text {
            font-size: 1.0625rem !important;
            font-weight: 800 !important;
          }
        }
        /* Landscape orientation - adjust for side notches, keep button visible */
        @media (orientation: landscape) and (max-height: 500px) {
          .mux-video-player-cta-${id} .action-button-container {
            bottom: max(0.75rem, env(safe-area-inset-bottom)) !important;
            left: max(2rem, calc(env(safe-area-inset-left) + 0.5rem)) !important;
            right: max(2rem, calc(env(safe-area-inset-right) + 0.5rem)) !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            min-height: 48px !important;
            height: 48px !important;
            padding: 0 1.25rem !important;
            font-size: 0.875rem !important;
          }
        }
        /* Instagram / Meta in-app browser: más margen inferior (chrome del WebView) */
        ${
          isInstagram
            ? `
          .mux-video-player-cta-${id} .action-button-container {
            bottom: max(2.25rem, calc(env(safe-area-inset-bottom, 0px) + 2rem)) !important;
            left: max(0.85rem, env(safe-area-inset-left, 0.85rem)) !important;
            right: max(0.85rem, env(safe-area-inset-right, 0.85rem)) !important;
            padding-bottom: 0 !important;
          }
          .mux-video-player-cta-${id} .cta-action-button {
            -webkit-transform: translateZ(0) !important;
            transform: translateZ(0) !important;
            min-height: 56px !important;
            /* Avoid 300ms click delay + accidental zoom in IG WebView */
            touch-action: manipulation !important;
          }
          .mux-video-player-cta-${id} .cta-action-button.cta-vip-membership {
            min-height: 58px !important;
            padding: 0.9rem 1rem !important;
          }
        `
            : ""
        }
        ${platformStyles}
      `
      );
    }

    // Desktop non-reel mode: full width with padding, button at bottom
    return (
      baseButtonStyles +
      `
      .mux-video-player-cta-${id} .action-button-container {
        z-index: 100 !important;
        position: absolute !important;
        pointer-events: auto !important;
        visibility: visible !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: center !important;
        align-items: center !important;
        bottom: max(2rem, env(safe-area-inset-bottom)) !important;
        left: 1.5rem !important;
        right: 1.5rem !important;
        width: auto !important;
        max-width: calc(100% - 3rem) !important;
      }
      .mux-video-player-cta-${id} .cta-action-button {
        min-height: 56px !important;
        height: 56px !important;
        padding: 0 1.5rem !important;
        font-size: 1rem !important;
        gap: 0.625rem !important;
        max-width: 32rem !important;
        width: 100% !important;
        flex-shrink: 0 !important;
      }
      .mux-video-player-cta-${id} .cta-button-text {
        font-size: 1rem !important;
      }
      .mux-video-player-cta-${id} .cta-button-icon {
        width: 1.25rem !important;
        height: 1.25rem !important;
      }
    `
    );
  }, [
    safeCssId,
    isMobile,
    isReelMode,
    isConversionReel,
    isInstagram,
    isIOS,
    isAndroid,
    showCTAButton,
    isExclusiveMembership,
    isVipMembershipCta,
  ]);

  // Function to retry loading the video
  const handleRetry = useCallback(() => {
    setHasError(false);
    setErrorMessage("");
    setIsLoading(true);
    setHasReceivedMuxMetadata(false);
    setMuxHasLoadedData(false);
    setMuxCanPlayBuffered(false);
    setHasPlaybackStarted(false);
    setShowPlayButton(false);
    setLoadingSeconds(0);
    setRetryCount(0);

    // Force reload the player
    const player = playerRef.current;
    if (player) {
      if (typeof player.load === "function") {
        player.load();
      } else if (typeof player.play === "function") {
        // Fallback: try to play
        player.play().catch((error: unknown) => {
          console.debug("[MuxVideoPlayerWithCTA] Retry play failed:", error);
        });
      }
    }
  }, []);

  // Get optimized thumbnail URL for fallback display and faster loading
  // Only generate thumbnail URL if playbackId is valid and not empty
  // Use Mux Image API with optimizations for faster load and better UX
  // Mobile-first optimization: Use appropriate sizes for device viewport
  const getThumbnailWidth = () => {
    if (typeof window === "undefined") return "1080";
    // Use actual viewport width for better responsive loading
    const viewportWidth = window.innerWidth;
    const dpr = window.devicePixelRatio || 1;

    // Calculate optimal width based on viewport and pixel density
    // Cap at 1920 for performance (max needed for most screens)
    const optimalWidth = Math.min(viewportWidth * dpr, 1920);

    // Round to nearest standard width for better CDN caching
    if (optimalWidth <= 480) return "480";
    if (optimalWidth <= 720) return "720";
    if (optimalWidth <= 1080) return "1080";
    return "1920";
  };

  const thumbnailFitMode =
    isConversionReel || (isReelMode && isMobile) ? "smartcrop" : "preserve";
  const thumbnailObjectFit = reelObjectFit;

  const thumbnailUrl =
    effectivePlaybackId && effectivePlaybackId.trim() !== ""
      ? `https://image.mux.com/${effectivePlaybackId}/thumbnail.webp?time=0&width=${getThumbnailWidth()}&fit_mode=${thumbnailFitMode}&quality=${
          isMobile ? "75" : "80"
        }`
      : null;

  // Generate low-quality placeholder for instant display (LQIP - Low Quality Image Placeholder)
  // Ultra-fast load for perceived performance, especially on slow mobile connections
  const placeholderUrl =
    effectivePlaybackId && effectivePlaybackId.trim() !== ""
      ? `https://image.mux.com/${effectivePlaybackId}/thumbnail.webp?time=0&width=64&fit_mode=${thumbnailFitMode}&quality=50`
      : null;

  /**
   * Subtle mute chip (Instagram/TikTok style): top-right, glass, no pulse/bounce,
   * no amber pill — so the video stays the focus. After ~3s collapses to icon-only.
   */
  const renderMuteIndicator = () => {
    if (!(showMuteIndicator && isPlaying && !hideUnmutePrompt)) return null;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          toggleMute();
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
          e.preventDefault();
          toggleMute();
        }}
        className="absolute z-30 cursor-pointer touch-manipulation select-none transition-opacity duration-500 hover:opacity-100 active:scale-95"
        style={{
          top: "max(0.65rem, env(safe-area-inset-top, 8px))",
          right: "max(0.65rem, env(safe-area-inset-right, 8px))",
          opacity: muteChipCompact ? 0.72 : 0.9,
          WebkitTapHighlightColor: "transparent",
        }}
        aria-label={t("enableAudio", detectedLanguage)}
        title={t("enableAudio", detectedLanguage)}
      >
        <span
          className={`flex items-center rounded-full border border-white/10 bg-black/28 text-white/90 shadow-sm backdrop-blur-md transition-all duration-500 ease-out ${
            muteChipCompact
              ? "gap-0 px-2 py-2"
              : "gap-1.5 px-2.5 py-1.5"
          }`}
        >
          <svg
            className="h-3.5 w-3.5 shrink-0 text-white/80"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
            />
            <line x1="18" y1="6" x2="6" y2="18" strokeWidth={2} />
          </svg>
          <span
            className={`overflow-hidden whitespace-nowrap text-[11px] font-medium leading-none tracking-wide text-white/75 transition-all duration-500 ease-out ${
              muteChipCompact
                ? "max-w-0 opacity-0"
                : "max-w-[5.5rem] opacity-100"
            }`}
          >
            {t("tapForSound", detectedLanguage)}
          </span>
        </span>
      </button>
    );
  };

  const renderMuxLoadChrome = () => {
    if (externalPosterBackdrop) return null;
    return (
    <>
      {showHeavyVideoLoader && (
        <div
          className={`absolute inset-0 z-40 flex items-center justify-center transition-colors duration-500 ${
            muxHasLoadedData
              ? "bg-black/25 backdrop-blur-[1px]"
              : "bg-black/42 backdrop-blur-[2px]"
          }`}
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label={t("loadingVideo", detectedLanguage)}
          style={{
            paddingTop: "max(1rem, env(safe-area-inset-top, 0px))",
            paddingBottom: "max(1rem, env(safe-area-inset-bottom, 0px))",
            paddingLeft: "max(1rem, env(safe-area-inset-left, 0px))",
            paddingRight: "max(1rem, env(safe-area-inset-right, 0px))",
          }}
        >
          <div className="text-center space-y-4 px-6 py-4 max-w-md mx-auto">
            <div className="flex justify-center gap-2" aria-hidden="true">
              <span
                className={`h-1.5 w-10 rounded-full transition-colors duration-300 ${
                  hasReceivedMuxMetadata
                    ? "bg-amber-500"
                    : "bg-white/20"
                }`}
              />
              <span
                className={`h-1.5 w-10 rounded-full transition-colors duration-300 ${
                  muxHasLoadedData ? "bg-amber-500" : "bg-white/20"
                }`}
              />
              <span
                className={`h-1.5 w-10 rounded-full transition-colors duration-300 ${
                  muxCanPlayBuffered
                    ? "bg-amber-400"
                    : hasReceivedMuxMetadata && muxHasLoadedData
                      ? "bg-white/40 animate-pulse"
                      : "bg-white/15"
                }`}
              />
            </div>
            <div className="relative mx-auto w-16 h-16 md:w-20 md:h-20">
              <div
                className="absolute inset-0 w-full h-full border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin"
                style={{
                  animationDuration: "1s",
                  willChange: "transform",
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <svg
                  className="w-6 h-6 md:w-8 md:h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div
                className="absolute inset-0 w-full h-full bg-amber-500 rounded-full blur-lg opacity-20"
                style={{ willChange: "opacity" }}
              />
            </div>

            <div className="space-y-1">
              <p className="text-white text-sm md:text-base font-semibold tracking-wide">
                {!hasReceivedMuxMetadata
                  ? t("loadingVideo", detectedLanguage)
                  : !muxHasLoadedData
                    ? t("loadingVideo", detectedLanguage)
                    : t("optimizingStream", detectedLanguage)}
              </p>
              <p className="text-white/85 text-xs font-medium">
                {!hasReceivedMuxMetadata
                  ? t("connectingToPlayer", detectedLanguage)
                  : !muxHasLoadedData
                    ? t("decodingPreview", detectedLanguage)
                    : detectedLanguage === "es"
                      ? `Tiempo de carga: ${loadingSeconds}s`
                      : `Loading time: ${loadingSeconds}s`}
              </p>
              {!isMobile && hasReceivedMuxMetadata && muxHasLoadedData && (
                <p className="text-gray-300 text-xs font-medium opacity-85">
                  {t("startingPlayback", detectedLanguage)}
                </p>
              )}
              {shouldShowSlowConnectionHint && (
                <p className="text-amber-200 text-xs font-medium">
                  {isLikelySlowNetwork
                    ? t("slowConnection", detectedLanguage)
                    : t("optimizingStream", detectedLanguage)}
                </p>
              )}
              {isInstagram && (
                <p className="text-white/70 text-xs leading-relaxed pt-1">
                  {t("inAppBrowserVideoLoadHint", detectedLanguage)}
                </p>
              )}
            </div>

            {isMobile && (hasReceivedMuxMetadata || muxHasLoadedData) && (
              <div className="flex items-center justify-center gap-1.5">
                <div
                  className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce"
                  style={{
                    animationDelay: "0ms",
                    animationDuration: "1s",
                  }}
                />
                <div
                  className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce"
                  style={{
                    animationDelay: "150ms",
                    animationDuration: "1s",
                  }}
                />
                <div
                  className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce"
                  style={{
                    animationDelay: "300ms",
                    animationDuration: "1s",
                  }}
                />
              </div>
            )}
            {loadingSeconds >= 10 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRetry();
                }}
                className="mt-3 px-4 py-2 text-xs font-semibold text-white bg-white/15 hover:bg-white/25 rounded-full transition-colors"
              >
                {detectedLanguage === "es"
                  ? "Reintentar carga"
                  : "Retry loading"}
              </button>
            )}
          </div>
        </div>
      )}
      {showLightVideoLoader && (
        <div
          className={`absolute inset-x-0 z-40 flex justify-center px-4 pointer-events-none ${
            isInstagram ? "bottom-20 md:bottom-24" : "bottom-6 md:bottom-8"
          }`}
          style={{
            paddingBottom: isInstagram
              ? "max(2rem, calc(env(safe-area-inset-bottom, 0px) + 3.5rem))"
              : "max(0.5rem, env(safe-area-inset-bottom, 0px))",
          }}
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label={t("playbackReadyBrief", detectedLanguage)}
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/82 px-4 py-2.5 shadow-lg backdrop-blur-md max-w-[min(100%,22rem)]">
            <Loader2
              className="h-4 w-4 shrink-0 animate-spin text-amber-300"
              aria-hidden
            />
            <span className="text-xs font-medium text-white/95 text-center">
              {t("playbackReadyBrief", detectedLanguage)}
            </span>
          </div>
        </div>
      )}
    </>
    );
  };

  const muxPlayerRevealWrapperStyle: React.CSSProperties | undefined =
    externalPosterBackdrop
      ? {
          opacity: hasPlaybackStarted || showPlayButton ? 1 : 0,
          visibility:
            hasPlaybackStarted || showPlayButton ? "visible" : "hidden",
          transition: "opacity 280ms ease-out",
        }
      : undefined;

  // No playbackId error - show configuration error (use effectivePlaybackId so Spain fallback counts)
  if (!effectivePlaybackId || effectivePlaybackId.trim() === "") {
    return (
      <div
        className={`relative w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center ${containerClassName}`}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center text-white p-8 max-w-md z-10">
          <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
            <span className="text-yellow-400 text-2xl">⚙️</span>
          </div>
          <h3 className="text-lg font-semibold mb-2">
            Video Configuration Missing
          </h3>
          <p className="text-sm text-gray-300 mb-4">
            No video playback ID configured for this service.
            {service && ` (Service: ${service})`}
            {countryCode && ` (Country: ${countryCode})`}
          </p>
          <p className="text-xs text-gray-400">
            Please check environment variables or service configuration.
          </p>
        </div>
      </div>
    );
  }

  // Error state - NUNCA bloquear para channel_invite (telegram groups). Siempre mostrar video + CTA.
  const isJoinFlow = ["telegram", "telegram_groups", "channel_invite"].includes(
    service ?? "",
  );
  if (
    !isJoinFlow &&
    hasError &&
    errorMessage &&
    !errorMessage.toLowerCase().includes("network") &&
    !errorMessage.toLowerCase().includes("connection")
  ) {
    // Only block for invalid videos, not network errors
    return (
      <div
        className={`relative w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center ${containerClassName}`}
        style={
          thumbnailUrl && !thumbnailError
            ? {
                backgroundImage: `url(${thumbnailUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }
            : undefined
        }
      >
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative text-center text-white p-8 max-w-md z-10">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
            <span className="text-red-400 text-2xl">⚠️</span>
          </div>
          {title && <h3 className="text-lg font-semibold mb-2">{title}</h3>}
          <p className="text-sm text-gray-300 mb-4">{errorMessage}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                setHasError(false);
                setErrorMessage("");
                onClose?.();
              }}
              className="px-4 py-2 bg-gray-700/50 hover:bg-gray-700/70 text-white rounded-full text-sm font-medium transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>

        {/* Hidden image to preload thumbnail and detect errors */}
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt=""
            className="hidden"
            onError={() => {
              setThumbnailError(true);
            }}
            onLoad={() => {
              setThumbnailError(false);
            }}
            loading="lazy"
          />
        )}
      </div>
    );
  }

  // For network errors, continue rendering - show video player with poster and CTA
  // This ensures BioTemplate always provides a good UX even with VPN/network issues

  // Show loading state while service config is being loaded
  // Note: For services, apiVideoConfig always provides valid fallbacks, so we don't need to wait
  // This only blocks rendering when we truly have no playbackId available
  if (isLoadingConfig && !playbackId) {
    return (
      <div
        className={`relative w-full h-full bg-black flex items-center justify-center ${containerClassName}`}
      >
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 text-white animate-spin mx-auto" />
          <div className="text-white text-sm">
            Loading video configuration...
          </div>
        </div>
      </div>
    );
  }

  if (!effectivePlaybackId) {
    return (
      <div
        className={`relative w-full h-full bg-black flex items-center justify-center ${containerClassName}`}
      >
        <div className="text-white text-sm">
          {service
            ? `No playback ID configured for service: ${service}`
            : "No playback ID provided"}
        </div>
      </div>
    );
  }

  // Get pack info (effectivePackId: channel_invite or overridePackId e.g. exclusive); fallback to static so checkout always renders when we have clientSecret.
  // Para sorpresa/exclusive priorizar siempre static para que el custom element tenga precio y se renderice.
  const packPricing =
    (overridePackId
      ? getPackPricingStatic(effectivePackId, effectiveCountry)
      : null) ??
    getPackPricing(effectivePackId, effectiveCountry) ??
    getPackPricingStatic(effectivePackId, effectiveCountry);

  // Precio mostrado y enviado al PI: pack list + reglas exclusivo + bonus “suerte” (solo Stripe).
  const watcherCheckoutPricing = computeStripePackCheckoutAmounts({
    baseAmountCents: packPricing?.amount ?? 0,
    packId: effectivePackId,
    watcherLuckPercent: stripeWatcherLuckPercent,
  });

  const checkoutDisplayAmount =
    packPricing == null ? 0 : watcherCheckoutPricing.finalAmountCents;
  const checkoutOriginalAmount =
    watcherCheckoutPricing.showDiscountBadge
      ? watcherCheckoutPricing.originalListAmountCents
      : undefined;
  const checkoutDiscountPercent =
    watcherCheckoutPricing.discountPercentDisplay ?? undefined;

  // Format price for display
  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  // Email collection removed - Stripe collects email in billing details

  // Loading state while creating payment intent
  const checkoutContentWrapper = (content: React.ReactNode) => {
    const inner = (
      <div
        className={`${getContainerClasses()} mux-video-player-cta-${safeCssId} ${className}`}
        style={{ minHeight: 0, flex: 1 }}
      >
        {content}
      </div>
    );
    if (isModal) {
      // Mobile: take the full dvh so the form has room and the pay button is always reachable.
      // Desktop: cap to a comfortable max so the modal feels framed against the backdrop.
      return (
        <div
          role="dialog"
          aria-modal="true"
          className={`w-full h-full flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4 ${
            isInstagram ? "instagram-browser-modal" : ""
          }`}
          style={{
            paddingTop: "max(0px, env(safe-area-inset-top, 0px))",
          }}
        >
          <div
            className={`w-full max-w-4xl rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl bg-black flex flex-col ${
              isExclusiveMembership ? "ring-1 ring-amber-500/30" : ""
            }`}
            style={{
              // Mobile: bottom-sheet style up to 95dvh so the pay button is always above the keyboard.
              // Desktop (≥640px) max-height handled by sm:max-h class below via inline style fallback.
              maxHeight: "min(95dvh, 95vh)",
              height: "auto",
            }}
          >
            {inner}
          </div>
        </div>
      );
    }
    return inner;
  };

  if (ctaBusy) {
    const loadingCopy = isNavigatingAway
      ? t("openingCheckoutPage", detectedLanguage)
      : t("preparingCheckout", detectedLanguage);
    const exclusiveOfferCopy =
      isExclusiveMembership && STRIPE_EXCLUSIVE_PACK_DISCOUNT_PERCENT > 0
        ? detectedLanguage === "es"
          ? `Ingresando te llevás ${STRIPE_EXCLUSIVE_PACK_DISCOUNT_PERCENT}% OFF el primer mes`
          : `Join now and get ${STRIPE_EXCLUSIVE_PACK_DISCOUNT_PERCENT}% off your first month`
        : null;
    return checkoutContentWrapper(
      <div
        className="w-full h-full flex flex-col items-center justify-center bg-black gap-4 p-4"
        aria-busy="true"
        aria-live="polite"
        aria-label={loadingCopy}
      >
        <div
          className="w-10 h-10 border-2 border-white/30 border-t-white rounded-full animate-spin"
          aria-hidden
        />
        <p className="text-white/80 text-sm text-center max-w-xs px-2">
          {loadingCopy}
        </p>
        {exclusiveOfferCopy && (
          <p
            className="text-center max-w-xs px-3"
            role="status"
          >
            <span
              className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold text-amber-950"
              style={{
                background:
                  "linear-gradient(135deg, #fde68a 0%, #fbbf24 45%, #f59e0b 100%)",
              }}
            >
              {exclusiveOfferCopy}
            </span>
          </p>
        )}
      </div>,
    );
  }

  // Render checkout state: solo si tenemos clientSecret, packPricing y monto final válido (coincide con PaymentIntent)
  if (checkoutState === "CHECKOUT" && clientSecret && packPricing && checkoutDisplayAmount > 0) {
    const stripePromise = getStripe();
    if (!stripePromise) {
      if (!muxStripeJsUnavailableTrackedRef.current) {
        muxStripeJsUnavailableTrackedRef.current = true;
        trackStripePayFailurePostHog({
          stage: "stripe_js_unavailable",
          flow_surface: "mux_video_cta",
          failure_kind: "stripe_promise_null",
          pack_id: effectivePackId,
        });
      }
      return checkoutContentWrapper(
        <div className="w-full h-full flex items-center justify-center bg-black p-4">
          <div className="text-white text-center">
            <p className="text-red-400 mb-4">Checkout not configured</p>
            <button
              onClick={handleBackToVideo}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
            >
              Volver
            </button>
          </div>
        </div>,
      );
    }

    return checkoutContentWrapper(
      <div className="w-full h-full bg-black flex flex-col min-h-0">
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: isExclusiveMembership
              ? STRIPE_APPEARANCE_EXCLUSIVE
              : STRIPE_APPEARANCE,
            locale: detectedLanguage === "es" ? "es" : "en",
            loader: 'auto',
          }}
        >
          <div className="flex-1 flex flex-col min-h-0">
            <StripeCheckoutForm
              amount={checkoutDisplayAmount}
              currency="usd"
              packName={
                detectedLanguage === "es" ? packPricing.nameEs : packPricing.name
              }
              packThumbnail={packPricing.thumbnail}
              originalAmount={checkoutOriginalAmount}
              discountPercent={checkoutDiscountPercent}
              language={detectedLanguage === "es" ? "es" : "en"}
              variant={isExclusiveMembership ? "exclusive" : "default"}
              onSuccess={handleCheckoutSuccess}
              onError={handleCheckoutError}
              onBack={handleBackToVideo}
            />
          </div>
        </Elements>
      </div>,
    );
  }

  // Note: SUCCESS state redirects to /thank-you or /gracias, no need to render

  // Render invalid playbackId error (before attempting to load video)
  if (!isValidPlaybackId && !isLoadingConfig) {
    const errorTitle =
      detectedLanguage === "es" ? "Video no disponible" : "Video unavailable";
    const errorMsg =
      detectedLanguage === "es"
        ? "El video no está configurado correctamente. Por favor contacta a soporte."
        : "The video is not configured correctly. Please contact support.";
    const backLabel = detectedLanguage === "es" ? "Cerrar" : "Close";

    return checkoutContentWrapper(
      <div
        className="w-full h-full flex items-center justify-center bg-black p-4 overflow-y-auto"
        role="alert"
      >
        <div className="text-center text-white w-full max-w-md mx-auto space-y-6">
          <div
            className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-amber-500/20 rounded-full flex items-center justify-center"
            aria-hidden
          >
            <svg
              className="w-8 h-8 sm:w-10 sm:h-10 text-amber-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl font-bold mb-2">{errorTitle}</h3>
            <p className="text-white/70 text-sm">{errorMsg}</p>
            {import.meta.env.DEV && (
              <p className="text-white/50 text-xs mt-4 font-mono">
                Debug: playbackId="{effectivePlaybackId}" country=
                {effectiveCountry} service={service}
              </p>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="w-full bg-white/10 text-white font-semibold py-3 px-4 sm:py-4 sm:px-6 rounded-xl hover:bg-white/20 active:bg-white/30 transition-colors min-h-[44px] touch-manipulation"
            >
              {backLabel}
            </button>
          )}
        </div>
      </div>,
    );
  }

  // Render error state (same container as video for responsive)
  if (checkoutState === "ERROR") {
    const errorTitle = t("paymentError", detectedLanguage);
    const errorDefaultMsg = t("paymentErrorMsg", detectedLanguage);
    const retryLabel = t("retry", detectedLanguage);
    const backLabel = t("back", detectedLanguage);
    return checkoutContentWrapper(
      <div
        className="w-full h-full flex items-center justify-center bg-black p-4 overflow-y-auto"
        role="alert"
      >
        <div className="text-center text-white w-full max-w-md mx-auto space-y-6">
          <div
            className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center"
            aria-hidden
          >
            <svg
              className="w-8 h-8 sm:w-10 sm:h-10 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl font-bold mb-2">{errorTitle}</h3>
            <p className="text-white/70 text-sm">
              {checkoutError || errorDefaultMsg}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={handleRetryCheckout}
              className="w-full bg-white text-black font-semibold py-3 px-4 sm:py-4 sm:px-6 rounded-xl hover:bg-white/90 active:bg-white/95 transition-colors min-h-[44px] touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {retryLabel}
            </button>
            <button
              type="button"
              onClick={handleBackToVideo}
              className="w-full bg-white/10 text-white font-semibold py-3 px-4 sm:py-4 sm:px-6 rounded-xl hover:bg-white/20 active:bg-white/30 transition-colors min-h-[44px] touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            >
              {backLabel}
            </button>
          </div>
        </div>
      </div>,
    );
  }

  // If content is inactive for this country (API soft delete), show message
  if (!isContentActive) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-8 text-center">
        <div className="max-w-md">
          <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Contenido no disponible
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Este contenido no está disponible en tu región en este momento.
          </p>
        </div>
      </div>
    );
  }

  // Render video preview (default state)
  return (
    <>
      {/* Custom styles for video player */}
      <style>{getVideoStyles()}</style>
      {/* Custom styles for CTA button to ensure visibility and perfect responsiveness */}
      <style>{getCTAButtonStyles()}</style>
      {/* Mux Player custom styling using official CSS variables and parts */}
      <style>{`
        /* Use Mux official CSS variable to control media object fit */
        .mux-video-player-cta-${safeCssId} mux-player {
          --media-object-fit: ${reelObjectFit};
          --media-object-position: center;
        }
        
        /* Instagram Explorer optimizations */
        ${
          isInstagram
            ? `
          .mux-video-player-cta-${safeCssId} mux-player {
            /* Force native playback for better Instagram compatibility */
            -webkit-playsinline: true;
            playsinline: true;
            /* Block media-chrome gesture-receiver from toggling pause on tap.
               Mute / CTA are handled by our overlay container, not mux-player. */
            pointer-events: none !important;
          }
          .mux-video-player-cta-${safeCssId} mux-player video {
            /* Ensure video plays inline in Instagram without fullscreen */
            -webkit-playsinline: true;
            playsinline: true;
            /* Prevent Instagram scroll interference */
            touch-action: none;
            pointer-events: none !important;
          }
        `
            : ""
        }
        
        /* TikTok Shop style: Hide ALL controls for immersive experience in all countries.
           CSS vars alone still leave media-control-bar / media-play-button in the
           shadow tree (replay 019fb3e4). Kill chrome via vars + ::part + pointer-events. */
        .mux-video-player-cta-${safeCssId} mux-player {
          /* Hide center play button for seamless autoplay experience */
          --center-play-button: none !important;
          --pre-play-button: none !important;
          /* Hide time displays for cleaner UI */
          --time-display: none !important;
          --duration-display: none !important;
          /* Hide all control buttons */
          --bottom-play-button: none !important;
          --seek-backward-button: none !important;
          --seek-forward-button: none !important;
          --mute-button: none !important;
          --captions-button: none !important;
          --airplay-button: none !important;
          --pip-button: none !important;
          --fullscreen-button: none !important;
          --cast-button: none !important;
          --playback-rate-button: none !important;
          --volume-range: none !important;
          --time-range: none !important;
          --rendition-menu-button: none !important;
          --audio-track-menu-button: none !important;
          --loading-indicator: none !important;
          --dialog: none !important;
          --media-control-bar-display: none !important;
          /* Internal media-chrome display vars (base.mjs) */
          --media-control-display: none !important;
          --center-controls: none !important;
          --bottom-controls: none !important;
          --top-controls: none !important;
          /* Hide control bar completely */
          --controls: none !important;
          --controls-backdrop-color: transparent !important;
          /* Prevent media-gesture-receiver from receiving taps → pause */
          pointer-events: none !important;
          -webkit-user-select: none !important;
          user-select: none !important;
          -webkit-touch-callout: none !important;
        }

        /* Chrome-only. NEVER hide ::part(controller|layer|media-layer|video):
           media-controller owns the <video> surface. Hiding it left "Video is
           playing" logs with a black/poster-only UI (prod 2026-07-31). */
        .mux-video-player-cta-${safeCssId} mux-player::part(bottom),
        .mux-video-player-cta-${safeCssId} mux-player::part(top),
        .mux-video-player-cta-${safeCssId} mux-player::part(center),
        .mux-video-player-cta-${safeCssId} mux-player::part(gesture-layer),
        .mux-video-player-cta-${safeCssId} mux-player::part(control-bar),
        .mux-video-player-cta-${safeCssId} mux-player::part(play button),
        .mux-video-player-cta-${safeCssId} mux-player::part(center play button),
        .mux-video-player-cta-${safeCssId} mux-player::part(bottom play button),
        .mux-video-player-cta-${safeCssId} mux-player::part(live button),
        .mux-video-player-cta-${safeCssId} mux-player::part(seek-backward button),
        .mux-video-player-cta-${safeCssId} mux-player::part(seek-forward button),
        .mux-video-player-cta-${safeCssId} mux-player::part(mute button),
        .mux-video-player-cta-${safeCssId} mux-player::part(captions button),
        .mux-video-player-cta-${safeCssId} mux-player::part(airplay button),
        .mux-video-player-cta-${safeCssId} mux-player::part(pip button),
        .mux-video-player-cta-${safeCssId} mux-player::part(fullscreen button),
        .mux-video-player-cta-${safeCssId} mux-player::part(cast button),
        .mux-video-player-cta-${safeCssId} mux-player::part(playback-rate button),
        .mux-video-player-cta-${safeCssId} mux-player::part(volume range),
        .mux-video-player-cta-${safeCssId} mux-player::part(time range),
        .mux-video-player-cta-${safeCssId} mux-player::part(time display),
        .mux-video-player-cta-${safeCssId} mux-player::part(duration display) {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }

        /* Force the media surface visible even if a var leaks display:none. */
        .mux-video-player-cta-${safeCssId} mux-player::part(controller),
        .mux-video-player-cta-${safeCssId} mux-player::part(layer),
        .mux-video-player-cta-${safeCssId} mux-player::part(media-layer),
        .mux-video-player-cta-${safeCssId} mux-player::part(video) {
          display: block !important;
          opacity: 1 !important;
          visibility: visible !important;
          width: 100% !important;
          height: 100% !important;
          max-width: none !important;
          max-height: none !important;
          pointer-events: none !important;
        }

        .mux-video-player-cta-${safeCssId} mux-player video {
          pointer-events: none !important;
          -webkit-playsinline: true !important;
          playsinline: true !important;
          width: 100% !important;
          height: 100% !important;
          object-fit: ${reelObjectFit} !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        
        /* Floating CTA (buy) must sit above video controls */
        .mux-video-player-cta-${safeCssId} .action-button-container {
          z-index: 100 !important;
          pointer-events: auto !important;
        }

        /* Full-frame loading ambience (conversion: no “empty black” flashes) */
        @keyframes muxLoadShimmer-${safeCssId} {
          0% {
            transform: translateX(-55%);
            opacity: 0.2;
          }
          45% {
            opacity: 0.55;
          }
          100% {
            transform: translateX(55%);
            opacity: 0.2;
          }
        }
        .mux-video-player-cta-${safeCssId} .mux-load-shimmer-${safeCssId} {
          position: absolute;
          inset: 0;
          width: 220%;
          left: -60%;
          background: linear-gradient(
            104deg,
            transparent 42%,
            rgba(251, 191, 36, 0.14) 50%,
            rgba(245, 158, 11, 0.2) 52%,
            transparent 62%
          );
          animation: muxLoadShimmer-${safeCssId} 3s ease-in-out infinite;
          mix-blend-mode: screen;
          pointer-events: none;
        }
      `}</style>
      {isModal ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title || "Video"}
          className={`w-full h-full flex items-center justify-center p-4 bg-black/70 transition-opacity duration-200 ${
            modalMounted ? "opacity-100" : "opacity-0"
          } ${isInstagram ? "instagram-browser-modal" : ""}`}
          onClick={handleBackdropClick}
        >
          <div
            className={`w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl bg-black flex flex-col transition-all duration-200 ${
              modalMounted ? "opacity-100 scale-100" : "opacity-0 scale-95"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`${getContainerClasses()} mux-video-player-cta-${safeCssId} ${className}`}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onPointerDown={handlePointerDownUnlockSound}
              onClick={handleContainerClick}
              role="presentation"
              aria-busy={awaitingFirstPlayback}
              style={
                {
                  backgroundColor: "#000000",
                  position: "relative",
                  minHeight: 0,
                  flex: 1,
                } as React.CSSProperties
              }
            >
              {/* Video container with relative positioning for button overlay */}
              <div
                className={
                  isReelMode && !isMobile
                    ? "w-full h-full min-h-0 flex items-stretch justify-center relative"
                    : "w-full h-full flex items-center justify-center relative"
                }
                style={{ position: "relative", width: "100%", height: "100%" }}
              >
                {awaitingFirstPlayback && !externalPosterBackdrop && (
                  <div
                    className="absolute inset-0 z-[22] overflow-hidden pointer-events-none"
                    aria-hidden
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-black to-amber-950/40" />
                    <div className={`mux-load-shimmer-${safeCssId}`} />
                  </div>
                )}
                <MuxPlayer
                  key={`muxp-${safeCssId}-${effectivePlaybackId}-r${retryCount}`}
                  ref={playerRef}
                  streamType="on-demand"
                  playbackId={effectivePlaybackId}
                  accentColor="#f59e0b"
                  // TikTok Shop style: NO controls in any country - immersive experience (via CSS)
                  // Managed path owns startup. Only use native muted autoplay as backup
                  // in IG (muted-first). Elsewhere false so sound-first attempt isn't raced.
                  autoPlay={
                    managedAutoplay
                      ? preferMutedFirstAutoplay
                        ? "muted"
                        : false
                      : autoPlay
                  }
                  loop={loop}
                  muted={muxPlayerMuted}
                  // Conversion funnels must not inherit site-wide mute/volume prefs.
                  noMutedPref
                  noVolumePref
                  playsInline
                  // Optimize preload strategy for mobile networks:
                  // - "metadata": Fast initial load, user can start watching quickly
                  // - "auto": Desktop with good connection can buffer ahead
                  preload={effectivePreload}
                  // Disable keyboard shortcuts (not needed for mobile/touch)
                  nohotkeys
                  defaultHiddenCaptions
                  disablePictureInPicture
                  // When parent paints MuxPosterBackdrop, skip player poster downloads.
                  poster={externalPosterBackdrop ? "" : thumbnailUrl || undefined}
                  placeholder={
                    externalPosterBackdrop ? undefined : placeholderUrl || undefined
                  }
                  // Inline playback: Safari / iOS / Chrome iOS / IG / Android WebView
                  {...(isIOS || isInstagram || isMobile || isAndroid
                    ? { "webkit-playsinline": "true", "x5-playsinline": "true" }
                    : {})}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  onEnded={handleEnded}
                  onError={handleMuxPlayerError}
                  onLoadedMetadata={handleLoadedMetadata}
                  onLoadedData={handleMuxLoadedData}
                  onCanPlay={handleCanPlay}
                  onTimeUpdate={handleTimeUpdateForTelemetry}
                  onPlaying={() => {
                    hardenNativeVideoElement(getVideoElement(playerRef.current));
                    assertImmersiveChromeOnce({
                      playbackId: effectivePlaybackId,
                      country: effectiveCountry,
                    });
                    setIsPlaying(true);
                    setShowPlayButton(false);
                  }}
                  onStalled={() => handleStalledOrWaiting("stalled")}
                  onWaiting={() => handleStalledOrWaiting("buffering")}
                  style={
                    {
                      width: "100%",
                      height: "100%",
                      // Use official Mux CSS variables for video object fit
                      "--media-object-fit": reelObjectFit,
                      "--media-object-position": "center",
                      ...IMMERSIVE_MUX_CHROME_STYLE,
                      // Mobile GPU acceleration for smoother playback
                      ...(isMobile
                        ? {
                            transform: "translateZ(0)",
                            backfaceVisibility: "hidden",
                            WebkitBackfaceVisibility: "hidden",
                            WebkitFontSmoothing: "antialiased",
                          }
                        : {}),
                    } as React.CSSProperties & Record<`--${string}`, string>
                  }
                  metadata={muxDataMetadata}
                  // Performance optimizations for Mux
                  startTime={0}
                  primaryColor="#f59e0b"
                  secondaryColor="#b45309"
                  // Valid maxResolution values only (720p|1080p|1440p|2160p) — see Mux Player React API.
                  maxResolution={effectiveMaxResolution}
                  {...(effectiveMaxAutoResolution
                    ? { maxAutoResolution: effectiveMaxAutoResolution }
                    : {})}
                  // https://docs.mux.com/guides/player-advanced-usage#change-playback-engine
                  // Safari/iOS/IG: native HLS; Arc/Mac Chrome fragile MSE: force mse.
                  {...(preferNativePlayback || isInstagram
                    ? { preferPlayback: "native" as const }
                    : muxArcPlaybackTune
                      ? { preferPlayback: "mse" as const }
                      : {})}
                  // Thumbnail optimization: Show frame at 0s for instant preview
                  thumbnailTime={0}
                  // Note: minPreloadSegments / capRenditionToPlayerSize may be no-ops on 3.10.2
                  // until package supports them; safe to pass for forward-compat.
                  minPreloadSegments={effectiveMinPreloadSegments}
                  capRenditionToPlayerSize={effectiveCapRendition}
                  // Audio optimizations
                  defaultShowRemainingTime={false}
                  {...(fragileMseHlsConfig
                    ? { _hlsConfig: fragileMseHlsConfig }
                    : {})}
                />
              </div>

              {/* Descuento suerte: muestra después de tiempo de reproducción (no molesta el inicio) */}
              {showWatcherLuckBanner &&
                stripeWatcherLuckEligible &&
                stripeWatcherLuckPercent > 0 &&
                !showHeavyVideoLoader && (
                  <div
                    className="pointer-events-none absolute inset-x-0 z-[38] flex justify-center px-3 pb-1 motion-reduce:transition-none"
                    style={{
                      bottom:
                        "max(6.25rem, calc(env(safe-area-inset-bottom, 0px) + 5.85rem))",
                    }}
                  >
                    <div
                      className="pointer-events-auto motion-reduce:transition-opacity flex max-w-[min(100%,278px)] w-full items-start gap-1.5 rounded-lg border border-white/10 bg-black/38 px-2.5 py-1.5 shadow-md backdrop-blur-sm"
                      role="status"
                      aria-live="polite"
                    >
                      <p className="min-w-0 flex-1 text-[11px] font-normal leading-[1.4] tracking-tight text-white/[0.82] motion-reduce:opacity-100">
                        <span className="tabular-nums font-semibold text-white/[0.9]">{`+${stripeWatcherLuckPercent}%`}</span>
                        {" "}
                        {detectedLanguage === "es"
                          ? "extra en membresía (Stripe)."
                          : "extra on membership (Stripe)."}
                      </p>
                      <button
                        type="button"
                        aria-label={
                          detectedLanguage === "es" ? "Cerrar" : "Dismiss"
                        }
                        className="-m-0.5 shrink-0 rounded p-1 text-white/40 transition-colors hover:bg-white/[0.08] hover:text-white/65 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/55 motion-reduce:transition-none"
                        onClick={dismissWatcherLuckBanner}
                      >
                        <X className="size-[14px]" strokeWidth={2.25} aria-hidden />
                      </button>
                    </div>
                  </div>
                )}

              {/* Instagram browser hint — shown only when video fails to start; small and dismissible */}
              {showInstagramBanner && isInstagram && !showHeavyVideoLoader && (
                <div
                  className="absolute top-0 left-0 right-0 z-50 flex justify-center pointer-events-none"
                  style={{
                    paddingTop: "max(0.75rem, env(safe-area-inset-top, 10px))",
                    animation: "slideDown 0.35s ease-out",
                  }}
                >
                  <div className="pointer-events-auto flex items-center gap-2 bg-black/55 backdrop-blur-md rounded-full border border-white/10 shadow-lg px-3 py-1.5">
                    <button
                      type="button"
                      onClick={handleOpenInBrowser}
                      className="text-white/65 text-xs leading-none flex items-center gap-1.5 active:text-white/90 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3 shrink-0" strokeWidth={2} />
                      {linkCopiedForBrowser
                        ? t("linkCopied", detectedLanguage)
                        : t("openInBrowser", detectedLanguage)}
                    </button>
                    <button
                      type="button"
                      onClick={handleDismissInstagramBanner}
                      className="text-white/30 hover:text-white/55 transition-colors"
                      aria-label="Dismiss"
                    >
                      <X className="w-3 h-3" strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              )}

              {/* Legibility scrim under VIP CTA (mobile + IG WebView). */}
              {showCTAButton && isVipMembershipCta && (
                <div className="cta-vip-scrim" aria-hidden />
              )}

              {/* Floating buy CTA — visible desde el inicio, incluso durante loading */}
              {showCTAButton && (
                <div
                  className={`action-button-container ${
                    isInstagram ? "instagram-browser-buttons" : ""
                  }`}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  style={{
                    position: "absolute",
                    zIndex: 100,
                    // Garantiza visibilidad encima del loading overlay (z-40) y thumbnail (z-30)
                    isolation: "isolate",
                  }}
                >
                  {muxFriendlyBanner && (
                    <p
                      className="text-white/45 text-[10px] sm:text-[11px] text-center mb-1.5 px-4 max-w-[min(100%,20rem)] mx-auto leading-relaxed font-light tracking-wide"
                      role="note"
                    >
                      {muxFriendlyBanner}
                    </p>
                  )}
                  {/* Non-VIP only: subtitle can show pack/context. VIP hides price until press. */}
                  {floatingCtaSubtitle && (
                    <p className="cta-subtitle text-white/90 text-sm md:text-base text-center mb-2 drop-shadow-md">
                      {floatingCtaSubtitle}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={handleCTAClick}
                    onTouchStart={handleCTATouchStart}
                    onTouchEnd={handleCTATouchEnd}
                    disabled={!showCTAButton || ctaBusy}
                    className={`cta-action-button${
                      isVipMembershipCta ? " cta-vip-membership" : ""
                    }`}
                    data-attr={
                      isVipMembershipCta
                        ? "mux-vip-membership-cta"
                        : "mux-cta-button"
                    }
                    aria-label={ctaLabel}
                    aria-disabled={!showCTAButton || ctaBusy}
                    // touch-action: manipulation elimina el delay de 300ms en Android/iOS
                    // y desactiva gestos no deseados (zoom, scroll) en el botón
                    style={{ touchAction: "manipulation" }}
                  >
                    {ctaBusy ? (
                      <Loader2
                        className="w-5 h-5 shrink-0 animate-spin pointer-events-none"
                        aria-hidden
                      />
                    ) : (
                      !isVipMembershipCta &&
                      showInstagramIcon && (
                        <Instagram
                          className={`${
                            isMobile ? "w-4 h-4" : "w-5 h-5"
                          } shrink-0 pointer-events-none`}
                          strokeWidth={2.5}
                          style={{
                            filter: "drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1))",
                          }}
                        />
                      )
                    )}
                    <span className="cta-button-text">{ctaLabel}</span>
                    {!ctaBusy &&
                      (isVipMembershipCta ? (
                        <ChevronRight
                          className="cta-button-icon action-button-icon w-5 h-5 shrink-0"
                          strokeWidth={2.5}
                          aria-hidden
                        />
                      ) : (
                        <ExternalLink
                          className="cta-button-icon action-button-icon"
                          strokeWidth={2.5}
                        />
                      ))}
                  </button>
                  {/* Stripe logo OUTSIDE the button (subtle, not inside gold CTA) */}
                  {isVipMembershipCta && !ctaBusy && (
                    <div className="cta-stripe-trust" aria-hidden>
                      <PoweredByStripe variant="white" className="h-[14px]" />
                    </div>
                  )}
                  {/* CTA secundario (ej. "Acceso gratis" → registro + aprobación manual) */}
                  {secondaryCtaLabel && onSecondaryCtaClick && (
                    <button
                      type="button"
                      onClick={onSecondaryCtaClick}
                      className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-white/[0.09] px-5 py-3 text-[14px] font-semibold tracking-tight text-white ring-1 ring-white/20 backdrop-blur-md transition-all duration-150 hover:bg-white/[0.14] hover:ring-white/30 active:bg-white/[0.18]"
                      aria-label={secondaryCtaLabel}
                      style={{ touchAction: "manipulation" }}
                    >
                      <span>{secondaryCtaLabel}</span>
                      <span className="text-[11px] text-white/45" aria-hidden>
                        ✓
                      </span>
                    </button>
                  )}
                </div>
              )}

              {/* Progressive image loading: LQIP (Low Quality Image Placeholder) → Full thumbnail */}
              {/* Shows instantly while video buffers, crucial for mobile UX */}
              {awaitingFirstPlayback &&
                !externalPosterBackdrop &&
                (placeholderUrl || thumbnailUrl) &&
                !thumbnailError && (
                  <div className="absolute inset-0 z-30 overflow-hidden transition-opacity duration-300">
                    {/* LQIP - Blur-up effect for instant perceived load */}
                    {placeholderUrl && (
                      <div
                        className="absolute inset-0 transition-opacity duration-500"
                        style={{
                          backgroundImage: `url(${placeholderUrl})`,
                          backgroundSize: thumbnailObjectFit,
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          filter: "blur(20px)",
                          transform: "scale(1.1)", // Prevent blur edges
                          opacity: thumbnailUrl ? 0.5 : 1,
                        }}
                      />
                    )}
                    {/* Full quality thumbnail - fades in when loaded */}
                    {thumbnailUrl && (
                      <div
                        className="absolute inset-0 animate-fade-in"
                        style={{
                          backgroundImage: `url(${thumbnailUrl})`,
                          backgroundSize: thumbnailObjectFit,
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          animationDuration: "0.5s",
                          animationFillMode: "both",
                        }}
                      />
                    )}
                    {/* Dark overlay for better spinner visibility */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70" />
                  </div>
                )}

              {renderMuxLoadChrome()}
              {renderMuteIndicator()}

              {/* Play button overlay - shown if autoplay fails (sin texto de audio si hideUnmutePrompt) */}
              {!isPlaying && showPlayButton && (
                <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm gap-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleManualPlay();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleManualPlay();
                    }}
                    className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 hover:from-amber-500 hover:to-amber-700 active:from-amber-600 active:to-amber-800 flex items-center justify-center shadow-2xl transition-all duration-200 hover:scale-110 active:scale-95 touch-manipulation select-none border-4 border-white/30"
                    style={{
                      WebkitTapHighlightColor: "transparent",
                      WebkitTouchCallout: "none",
                      WebkitUserSelect: "none",
                      userSelect: "none",
                      cursor: "pointer",
                      minWidth: "96px",
                      minHeight: "96px",
                    }}
                    aria-label={t("tapToPlayWithSound", detectedLanguage)}
                  >
                    <Play
                      className="w-12 h-12 md:w-14 md:h-14 text-white ml-1.5"
                      fill="currentColor"
                      strokeWidth={2}
                    />
                  </button>
                  {!hideUnmutePrompt && (
                    <div className="bg-gradient-to-r from-amber-500 to-amber-700 text-white px-5 py-3 rounded-full text-base font-bold backdrop-blur-sm flex items-center gap-2.5 shadow-2xl border-2 border-white/30">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                        />
                      </svg>
                      <span className="tracking-tight">
                        {detectedLanguage === "en"
                          ? "🔊 Tap to play with sound"
                          : "🔊 Toca para reproducir con audio"}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {onClose && (
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  className={`mux-cta-close-btn fixed z-[200] min-w-[48px] min-h-[48px] w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 active:bg-black/90 text-white flex items-center justify-center transition-all duration-200 touch-manipulation outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-sm ${
                    isInstagram ? "instagram-browser-buttons" : ""
                  }`}
                  style={{
                    top: `max(${isMobile ? "0.75rem" : "1rem"}, env(safe-area-inset-top, 0.75rem))`,
                    right: `max(${isMobile ? "0.75rem" : "1rem"}, env(safe-area-inset-right, 0.75rem))`,
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                  }}
                  aria-label="Cerrar"
                >
                  <X className="w-6 h-6 shrink-0" strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`${getContainerClasses()} mux-video-player-cta-${safeCssId} ${className}`}
          data-mux-reel-viewport={isReelMode && !isMobile ? "page" : undefined}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onPointerDown={handlePointerDownUnlockSound}
          onClick={handleContainerClick}
          role="presentation"
          aria-busy={awaitingFirstPlayback}
          style={
            {
              backgroundColor: "#000000",
              position: isReelMode && isMobile ? "fixed" : "relative",
              ...(isMobile
                ? {
                    // TikTok style: full screen immersive
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "100dvh",
                    maxHeight: "100dvh",
                    // `100vh` acá era un bug: en móvil `vh` es el viewport LARGO
                    // (barra de URL retraída), así que cuando el WebView de IG
                    // despliega su chrome —viewport 733→423 en el replay 019f8489—
                    // el min-height forzaba 733px sobre una ventana de 423px y,
                    // al ser `position: fixed; top: 0`, empujaba los ~310px de
                    // abajo fuera de pantalla. Ahí vive el CTA.
                    // `100svh` = viewport CORTO: nunca supera lo visible.
                    minHeight: "100svh",
                    overflow: "hidden",
                    width: "100vw",
                  }
                : {
                    minHeight: "100dvh",
                    ...(isReelMode
                      ? {
                          height: "100dvh",
                          minHeight: "100dvh",
                        }
                      : {}),
                  }),
              ...(isInstagram
                ? {
                    touchAction: isConversionReel ? "none" : "pan-y pinch-zoom",
                    WebkitTransform: "translateZ(0)",
                    transform: "translateZ(0)",
                  }
                : isConversionReel
                  ? { touchAction: "none" as const }
                  : {}),
            } as React.CSSProperties
          }
        >
          {/* Video container: on mobile reel use absolute inset-0 so video fills entire screen */}
          <div
            className={
              isReelMode && !isMobile
                ? "w-full h-full min-h-[100dvh] flex items-stretch justify-center relative"
                : isReelMode && isMobile
                  ? "absolute inset-0"
                  : "w-full h-full flex items-center justify-center relative"
            }
            style={
              isReelMode && isMobile
                ? {
                    position: "absolute" as const,
                    inset: 0,
                    width: "100%",
                    height: "100%",
                  }
                : isReelMode && !isMobile
                  ? {
                      position: "relative" as const,
                      width: "100%",
                      height: "100%",
                      minHeight: "100dvh",
                    }
                  : {
                      position: "relative" as const,
                      width: "100%",
                      height: "100%",
                    }
            }
          >
            {awaitingFirstPlayback && !externalPosterBackdrop && (
              <div
                className="absolute inset-0 z-[22] overflow-hidden pointer-events-none"
                aria-hidden
              >
                <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-black to-amber-950/40" />
                <div className={`mux-load-shimmer-${safeCssId}`} />
              </div>
            )}
            <div
              className={
                externalPosterBackdrop
                  ? "absolute inset-0 h-full w-full"
                  : undefined
              }
              style={muxPlayerRevealWrapperStyle}
            >
            <MuxPlayer
              key={`muxp-${safeCssId}-${effectivePlaybackId}-r${retryCount}`}
              ref={playerRef}
              streamType="on-demand"
              playbackId={effectivePlaybackId}
              accentColor="#f59e0b"
              // TikTok Shop style: NO controls in any country - immersive experience (via CSS)
              // Intelligent autoplay: try with sound first, fallback to muted (TikTok/Reels style)
              // Instagram Explorer often blocks autoplay with sound, so use "any" for fallback
              // Managed path owns startup. Native muted only as IG backup (no sound-first race).
              autoPlay={
                managedAutoplay
                  ? preferMutedFirstAutoplay
                    ? "muted"
                    : false
                  : autoPlay
              }
              loop={loop}
              muted={muxPlayerMuted}
              noMutedPref
              noVolumePref
              playsInline
              // Optimize preload strategy for mobile networks
              preload={effectivePreload}
              nohotkeys
              defaultHiddenCaptions
              disablePictureInPicture
              // When parent paints MuxPosterBackdrop, skip player poster downloads.
              poster={externalPosterBackdrop ? "" : thumbnailUrl || undefined}
              placeholder={
                externalPosterBackdrop ? undefined : placeholderUrl || undefined
              }
              // Inline playback: Safari / iOS / Chrome iOS / IG / Android WebView
              {...(isIOS || isInstagram || isMobile || isAndroid
                ? { "webkit-playsinline": "true", "x5-playsinline": "true" }
                : {})}
              onPlay={handlePlay}
              onPause={handlePause}
              onEnded={handleEnded}
              onError={handleMuxPlayerError}
              onLoadedMetadata={handleLoadedMetadata}
              onLoadedData={handleMuxLoadedData}
              onCanPlay={handleCanPlay}
              onTimeUpdate={handleTimeUpdateForTelemetry}
              onPlaying={() => {
                hardenNativeVideoElement(getVideoElement(playerRef.current));
                assertImmersiveChromeOnce({
                  playbackId: effectivePlaybackId,
                  country: effectiveCountry,
                });
                setIsPlaying(true);
                setShowPlayButton(false);
              }}
              onStalled={() => handleStalledOrWaiting("stalled")}
              onWaiting={() => handleStalledOrWaiting("buffering")}
              style={
                {
                  width: "100%",
                  height: "100%",
                  // Use official Mux CSS variables for video object fit
                  "--media-object-fit": reelObjectFit,
                  "--media-object-position": "center",
                  ...IMMERSIVE_MUX_CHROME_STYLE,
                  // Mobile GPU acceleration
                  ...(isMobile
                    ? {
                        transform: "translateZ(0)",
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        WebkitFontSmoothing: "antialiased",
                      }
                    : {}),
                } as React.CSSProperties & Record<`--${string}`, string>
              }
              metadata={muxDataMetadata}
              // Performance optimizations for Mux
              startTime={0}
              primaryColor="#f59e0b"
              secondaryColor="#b45309"
              maxResolution={effectiveMaxResolution}
              {...(effectiveMaxAutoResolution
                ? { maxAutoResolution: effectiveMaxAutoResolution }
                : {})}
              // https://docs.mux.com/guides/player-advanced-usage#change-playback-engine
              // Safari/iOS/IG: native HLS; Arc/Mac Chrome fragile MSE: force mse.
              {...(preferNativePlayback || isInstagram
                ? { preferPlayback: "native" as const }
                : muxArcPlaybackTune
                  ? { preferPlayback: "mse" as const }
                  : {})}
              // Thumbnail optimization for faster perceived load
              thumbnailTime={0}
              minPreloadSegments={effectiveMinPreloadSegments}
              capRenditionToPlayerSize={effectiveCapRendition}
              // Audio optimizations
              defaultShowRemainingTime={false}
              {...(fragileMseHlsConfig
                ? { _hlsConfig: fragileMseHlsConfig }
                : {})}
            />
            </div>
          </div>

          {showWatcherLuckBanner &&
            stripeWatcherLuckEligible &&
            stripeWatcherLuckPercent > 0 &&
            !showHeavyVideoLoader && (
              <div
                className="pointer-events-none absolute inset-x-0 z-[38] flex justify-center px-3 pb-1 motion-reduce:transition-none"
                style={{
                  bottom:
                    "max(6.25rem, calc(env(safe-area-inset-bottom, 0px) + 5.85rem))",
                }}
              >
                <div
                  className="pointer-events-auto motion-reduce:transition-opacity flex max-w-[min(100%,278px)] w-full items-start gap-1.5 rounded-lg border border-white/10 bg-black/38 px-2.5 py-1.5 shadow-md backdrop-blur-sm"
                  role="status"
                  aria-live="polite"
                >
                  <p className="min-w-0 flex-1 text-[11px] font-normal leading-[1.4] tracking-tight text-white/[0.82] motion-reduce:opacity-100">
                    <span className="tabular-nums font-semibold text-white/[0.9]">{`+${stripeWatcherLuckPercent}%`}</span>
                    {" "}
                    {detectedLanguage === "es"
                      ? "extra en membresía (Stripe)."
                      : "extra on membership (Stripe)."}
                  </p>
                  <button
                    type="button"
                    aria-label={
                      detectedLanguage === "es" ? "Cerrar" : "Dismiss"
                    }
                    className="-m-0.5 shrink-0 rounded p-1 text-white/40 transition-colors hover:bg-white/[0.08] hover:text-white/65 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/55 motion-reduce:transition-none"
                    onClick={dismissWatcherLuckBanner}
                  >
                    <X className="size-[14px]" strokeWidth={2.25} aria-hidden />
                  </button>
                </div>
              </div>
            )}

          {/* Instagram browser suggestion banner (dismissible, non-intrusive) */}
          {showInstagramBanner && isInstagram && !showHeavyVideoLoader && (
            <div
              className="absolute top-0 left-0 right-0 z-50 flex justify-center pointer-events-none"
              style={{
                paddingTop: "max(0.75rem, env(safe-area-inset-top, 10px))",
                animation: "slideDown 0.35s ease-out",
              }}
            >
              <div className="pointer-events-auto flex items-center gap-2 bg-black/55 backdrop-blur-md rounded-full border border-white/10 shadow-lg px-3 py-1.5">
                <button
                  type="button"
                  onClick={handleOpenInBrowser}
                  className="text-white/65 text-xs leading-none flex items-center gap-1.5 active:text-white/90 transition-colors"
                >
                  <ExternalLink className="w-3 h-3 shrink-0" strokeWidth={2} />
                  {linkCopiedForBrowser
                    ? t("linkCopied", detectedLanguage)
                    : t("openInBrowser", detectedLanguage)}
                </button>
                <button
                  type="button"
                  onClick={handleDismissInstagramBanner}
                  className="text-white/30 hover:text-white/55 transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="w-3 h-3" strokeWidth={2.5} />
                </button>
              </div>
            </div>
          )}

          {showCTAButton && isVipMembershipCta && (
            <div className="cta-vip-scrim" aria-hidden />
          )}

          {/* Floating buy CTA — visible desde el inicio, incluso durante loading */}
          {showCTAButton && (
            <div
              className={`action-button-container ${
                isInstagram ? "instagram-browser-buttons" : ""
              }`}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                zIndex: 100,
                isolation: "isolate",
              }}
            >
              {muxFriendlyBanner && (
                <p
                  className="text-white/45 text-[10px] sm:text-[11px] text-center mb-1.5 px-4 max-w-[min(100%,20rem)] mx-auto leading-relaxed font-light tracking-wide"
                  role="note"
                >
                  {muxFriendlyBanner}
                </p>
              )}
              {floatingCtaSubtitle && (
                <p className="cta-subtitle text-white/90 text-sm md:text-base text-center mb-2 drop-shadow-md">
                  {floatingCtaSubtitle}
                </p>
              )}
              <button
                type="button"
                onClick={handleCTAClick}
                onTouchStart={handleCTATouchStart}
                onTouchEnd={handleCTATouchEnd}
                disabled={!showCTAButton || ctaBusy}
                className={`cta-action-button${
                  isVipMembershipCta ? " cta-vip-membership" : ""
                }`}
                data-attr={
                  isVipMembershipCta
                    ? "mux-vip-membership-cta"
                    : "mux-cta-button"
                }
                aria-label={ctaLabel}
                aria-disabled={!showCTAButton || ctaBusy}
                style={{ touchAction: "manipulation" }}
              >
                {ctaBusy ? (
                  <Loader2
                    className="w-5 h-5 shrink-0 animate-spin pointer-events-none"
                    aria-hidden
                  />
                ) : (
                  !isVipMembershipCta &&
                  showInstagramIcon && (
                    <Instagram
                      className={`${
                        isMobile ? "w-4 h-4" : "w-5 h-5"
                      } shrink-0 pointer-events-none`}
                      strokeWidth={2.5}
                      style={{
                        filter: "drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1))",
                      }}
                    />
                  )
                )}
                <span className="cta-button-text">{ctaLabel}</span>
                {!ctaBusy &&
                  (isVipMembershipCta ? (
                    <ChevronRight
                      className="cta-button-icon action-button-icon w-5 h-5 shrink-0"
                      strokeWidth={2.5}
                      aria-hidden
                    />
                  ) : (
                    <ExternalLink
                      className="cta-button-icon action-button-icon"
                      strokeWidth={2.5}
                    />
                  ))}
              </button>
              {isVipMembershipCta && !ctaBusy && (
                <div className="cta-stripe-trust" aria-hidden>
                  <PoweredByStripe variant="white" className="h-[14px]" />
                </div>
              )}
              {/* CTA secundario (ej. "Acceso gratis" → registro + aprobación manual) */}
              {secondaryCtaLabel && onSecondaryCtaClick && (
                <button
                  type="button"
                  onClick={onSecondaryCtaClick}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-white/[0.09] px-5 py-3 text-[14px] font-semibold tracking-tight text-white ring-1 ring-white/20 backdrop-blur-md transition-all duration-150 hover:bg-white/[0.14] hover:ring-white/30 active:bg-white/[0.18]"
                  aria-label={secondaryCtaLabel}
                  style={{ touchAction: "manipulation" }}
                >
                  <span>{secondaryCtaLabel}</span>
                  <span className="text-[11px] text-white/45" aria-hidden>
                    ✓
                  </span>
                </button>
              )}
            </div>
          )}

          {/* Progressive image loading: LQIP → Full thumbnail (mobile-optimized) */}
          {awaitingFirstPlayback &&
            !externalPosterBackdrop &&
            (placeholderUrl || thumbnailUrl) &&
            !thumbnailError && (
              <div className="absolute inset-0 z-30 overflow-hidden transition-opacity duration-300">
                {/* LQIP - Blur-up effect */}
                {placeholderUrl && (
                  <div
                    className="absolute inset-0 transition-opacity duration-500"
                    style={{
                      backgroundImage: `url(${placeholderUrl})`,
                      backgroundSize: thumbnailObjectFit,
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      filter: "blur(20px)",
                      transform: "scale(1.1)",
                      opacity: thumbnailUrl ? 0.5 : 1,
                    }}
                  />
                )}
                {/* Full quality thumbnail */}
                {thumbnailUrl && (
                  <div
                    className="absolute inset-0 animate-fade-in"
                    style={{
                      backgroundImage: `url(${thumbnailUrl})`,
                      backgroundSize: thumbnailObjectFit,
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      animationDuration: "0.5s",
                      animationFillMode: "both",
                    }}
                  />
                )}
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70" />
              </div>
            )}

          {renderMuxLoadChrome()}
          {renderMuteIndicator()}

          {!isPlaying && showPlayButton && (
            <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm gap-4">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleManualPlay();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleManualPlay();
                }}
                className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 hover:from-amber-500 hover:to-amber-700 active:from-amber-600 active:to-amber-800 flex items-center justify-center shadow-2xl transition-all duration-200 hover:scale-110 active:scale-95 touch-manipulation select-none border-4 border-white/30"
                style={{
                  WebkitTapHighlightColor: "transparent",
                  WebkitTouchCallout: "none",
                  WebkitUserSelect: "none",
                  userSelect: "none",
                  cursor: "pointer",
                  minWidth: "96px",
                  minHeight: "96px",
                }}
                aria-label={t("tapToPlayWithSound", detectedLanguage)}
              >
                <Play
                  className="w-12 h-12 md:w-14 md:h-14 text-white ml-1.5"
                  fill="currentColor"
                  strokeWidth={2}
                />
              </button>
              {!hideUnmutePrompt && (
                <div className="bg-gradient-to-r from-amber-500 to-amber-700 text-white px-5 py-3 rounded-full text-base font-bold backdrop-blur-sm flex items-center gap-2.5 shadow-2xl border-2 border-white/30">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                    />
                  </svg>
                  <span className="tracking-tight">
                    {detectedLanguage === "en"
                      ? "🔊 Tap to play with sound"
                      : "🔊 Toca para reproducir con audio"}
                  </span>
                </div>
              )}
            </div>
          )}

          {onClose && (
            <button
              ref={closeButtonRef}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className={`mux-cta-close-btn absolute z-50 min-w-[44px] min-h-[44px] w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors touch-manipulation outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent ${
                isInstagram ? "instagram-browser-buttons" : ""
              }`}
              style={{
                top: "max(1rem, env(safe-area-inset-top))",
                right: "max(1rem, env(safe-area-inset-right))",
              }}
              aria-label="Cerrar"
            >
              <X className="w-5 h-5 shrink-0" strokeWidth={2.5} />
            </button>
          )}
        </div>
      )}

      {freeJoinDestination && (
        <FreeJoinEntrySheet
          open={freeJoinSheetOpen}
          onOpenChange={setFreeJoinSheetOpen}
          destinationUrl={freeJoinDestination}
          country={effectiveCountry}
          language={detectedLanguage}
          personName={BRAND_PERSON_NAME}
          onStepChange={(step) => {
            mirrorFunnelEvent({
              event_name: "mux_video_free_join_step",
              template: "MuxVideoPlayerWithCTA",
              country: effectiveCountry || undefined,
              language: detectedLanguage || undefined,
              landing_path:
                typeof window !== "undefined" ? window.location.pathname : undefined,
              props: {
                step,
                destination: freeJoinDestination,
                entry_ux: "free_join_sheet",
                funnel: "clerk_then_profile_then_link",
                source: BRAND_SOURCE_KEY,
                plan_tier: "free",
              },
            });
          }}
        />
      )}
    </>
  );
};

export default MuxVideoPlayerWithCTA;
